import { bindRewardAppointmentAttemptV1, appointmentReviewRequiresRewardCompanionV1 } from "@/app/booking/rewardsIntegration/rewardAppointmentAttemptBinding";
import { isCustomerOwnedSessionStorageQuarantined, quarantineCustomerOwnedSessionStorage } from "@/app/booking/core/customerOwnedSessionStorageBarrier";
import { readPackageSessionCurrentEntryForSelectionV2 } from "@/app/booking/packages/packageSessionEntryReadV2";
import { APPOINTMENT_PACKAGE_SESSION_PREFERENCE_V2_STORAGE_KEY, PACKAGE_SESSION_BOOKING_INTENT_V2_STORAGE_KEY } from "@/app/booking/packages/packageSessionEntryStoreV2";
import {
  captureCustomerOwnedSessionStorageMutationProof,
  customerOwnedSessionStorageMutationProofIsCurrent,
  discardCustomerOwnedSessionStorageMutationExactStrict,
  readCustomerOwnedSessionStorageRawValueFromCapturedReader,
  readCustomerOwnedSessionStorageRawValueStable,
  type CustomerOwnedSessionStorageMutationProof,
} from "@/app/booking/core/customerOwnedSessionStorageMutationCoordinator";
import { createVersionedSessionDraftStore } from "@/app/booking/core/versionedSessionDraftStore";
import { FRONTEND_UI_ONLY_CUSTOMER_ACCOUNT_ID } from "@/app/auth/frontendUiAuthSession";
import {
  CUSTOMER_AUTH_SESSION_STORAGE_KEY,
  readStoredCustomerAuthSessionStrict,
} from "@/app/services/authApi";
import {
  createAppointmentStepOneDraftFingerprint,
  isAppointmentDraftFingerprint,
} from "@/app/booking/core/appointmentDraftFingerprint";
import {
  validateAppointmentStepOneBookingDraft,
  type AppointmentBookingDraftV1,
} from "@/app/booking/core/appointmentBookingDraft";
import {
  validateAppointmentBookingReceipt,
  type AppointmentBookingReceiptV2,
} from "@/app/booking/core/appointmentBookingReceipt";
import { isMembershipSessionAppointmentDraftId, isPackageSessionAppointmentDraftId, isResultsAppointmentDraftId } from "@/app/booking/core/draftId";
import {
  createMembershipSessionBookingTerminalProof,
  validateMembershipSessionBookingTerminalProof,
  getMembershipSessionBookingTerminalProofAuthority,
  type MembershipSessionBookingTerminalProof,
} from "@/app/booking/memberships/membershipSessionBookingTerminalProof";
import { createMembershipSessionBookingTerminalProofV2 } from "@/app/booking/memberships/membershipSessionBookingTerminalProofV2";
import { MEMBERSHIP_SESSION_REDEMPTION_SELECTION_STORAGE_KEY, readMembershipSessionRedemptionSelectionStrict } from "@/app/booking/memberships/membershipSessionRedemptionSelectionStore";
import { MEMBERSHIP_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY_V2,
  captureMembershipSessionBookingReservationRecordCurrentGuardV2,
  membershipSessionBookingReservationRecordRequiresProtectionV2,
  readMembershipSessionBookingReservationForAppointmentStrictV2,
  validateMembershipSessionBookingReservationOperationRecordV2,
  type MembershipSessionBookingReservationOperationRecordV2,
} from "@/app/booking/memberships/membershipSessionBookingReservationOperationStoreV2";
import { validateMembershipSessionSubmissionDefinitiveRejection,
  type MembershipSessionSubmissionDefinitiveRejection,
} from "@/app/booking/memberships/membershipSessionSubmissionDefinitiveRejection";
import { markAppointmentSubmissionAttemptTerminal } from "@/app/booking/checkout/appointmentSubmissionAttemptDraft";
import { validateMembershipSessionBookingReservationHold } from "@/app/booking/memberships/membershipSessionBookingReservationHold";
import { APPOINTMENT_MEMBERSHIP_SESSION_PREFERENCE_STORAGE_KEY, inspectAppointmentMembershipSessionPreferenceExactPersisted } from "@/app/booking/memberships/appointmentMembershipSessionPreferenceStore";
import { MEMBERSHIP_SESSION_BOOKING_STORAGE_KEY, readMembershipSessionBookingIntentStrict } from "@/app/booking/memberships/membershipSessionBookingStore";
import { captureMembershipSessionPlainData } from "@/app/booking/memberships/membershipSessionPlainData";
import {
  MEMBERSHIP_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY,
  captureMembershipSessionBookingReservationRecordCurrentGuard,
  membershipSessionBookingReservationRecordRequiresProtection,
  readMembershipSessionBookingReservationForAppointmentStrict,
  validateMembershipSessionBookingReservationOperationRecord,
  type MembershipSessionBookingReservationOperationRecordV1,
} from "@/app/booking/memberships/membershipSessionBookingReservationOperationStore";
import { APPOINTMENT_PACKAGE_SESSION_PREFERENCE_STORAGE_KEY, readAppointmentPackageSessionPreferenceStrict } from "@/app/booking/packages/appointmentPackageSessionPreferenceStore";
import { PACKAGE_SESSION_BOOKING_INTENT_STORAGE_KEY, inspectPackageSessionBookingIntentExactPersisted } from "@/app/booking/packages/packageSessionBookingStore";
import {
  createPackageSessionBookingTerminalProof,
  resolvePackageSessionBookingReviewAuthority,
  type PackageSessionBookingTerminalProofV1,
} from "@/app/booking/packages/packageSessionBookingTerminalProof";
import { createPackageSessionNoneTerminalProofV2, validatePackageSessionBookingTerminalProofVersioned,
  type PackageSessionBookingTerminalProof } from "@/app/booking/packages/packageSessionBookingTerminalProofV2";
import { PACKAGE_SESSION_REVIEW_SELECTION_STORAGE_KEY_V2, readPackageSessionReviewSelectionStrictV2,
  type PackageSessionReviewSelectionV2 } from "@/app/booking/packages/packageSessionReviewSelectionStoreV2";
import { samePackageSessionRedemptionDataV2 } from "@/app/booking/packages/packageSessionRedemptionV2";
import { createPackageSessionBookingReservedTerminalProofV3 } from "@/app/booking/packages/packageSessionBookingReservedTerminalProofV3";
import { validatePackageSessionAtomicTerminalCommitV3, validatePackageSessionAtomicSubmissionEvidenceV3, validatePackageSessionAtomicReleaseCommitV3,
  createPackageSessionAtomicReleaseCommitV3, type PackageSessionAtomicTerminalCommitV3, type PackageSessionAtomicReleaseCommitV3 } from "@/app/booking/packages/packageSessionAtomicTerminalV3";
import { readPackageSessionAtomicReservationStrictV2, capturePackageSessionAtomicReservationCurrentGuardV2,
  type PackageSessionAtomicReservationRecordV2 } from "@/app/booking/packages/packageSessionAtomicReservationStoreV2";
import { capturePackageSessionAtomicReservationAbsenceGuardV2, inspectPackageSessionAtomicReservationsForTeardownV2,
  PACKAGE_SESSION_ATOMIC_RESERVATION_STORAGE_KEY_V2 } from "@/app/booking/packages/packageSessionAtomicReservationStoreV2";
import {
  resolvePackageSessionBookingReservationHold,
  validatePackageSessionBookingReservationHold,
} from "@/app/booking/packages/packageSessionBookingReservationHold";
import {
  capturePackageSessionBookingReservationRecordCurrentGuard,
  PACKAGE_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY,
  packageSessionBookingReservationRecordRequiresProtection,
  readPackageSessionBookingReservationForAppointmentStrict,
  validatePackageSessionBookingReservationOperationRecord,
  type PackageSessionBookingReservationOperationRecordV1,
} from "@/app/booking/packages/packageSessionBookingReservationOperationStore";
import {
  resolveAppointmentResultsEntryPreference,
  validateAppointmentResultsEntryPreference,
  type AppointmentResultsEntryPreferenceV1,
} from "@/app/booking/results/appointmentResultsEntryPreference";
import { readAppointmentResultsEntryPreferenceStrict } from "@/app/booking/results/appointmentResultsEntryPreferenceStore";
import { isSupportedSpecialistAppointmentWorkingDraftTransition } from "@/app/booking/adapters/specialistAppointmentCompletionContext";
import {
  markAppointmentPaymentAttemptTerminal,
  validateAppointmentPaymentAttemptResumeDraft,
  type AppointmentPaymentAttemptResumeDraftV1,
} from "@/app/booking/checkout/appointmentPaymentAttemptResumeDraft";
import {
  validatePackageSessionSubmissionDefinitiveRejection,
  type PackageSessionSubmissionDefinitiveRejectionV1,
} from "@/app/booking/packages/packageSessionSubmissionDefinitiveRejection";
import { capturePackageSessionBookingProviderPlainData } from "@/app/booking/packages/packageSessionBookingReservationProvider";
import {
  createAppointmentPaymentAttemptResumeDraftStrictPersisted,
  inspectAppointmentPaymentAttemptResumeDraftPersisted,
  isLegalAppointmentPaymentAttemptStateTransition,
  replaceAppointmentPaymentAttemptResumeDraftStrictPersisted,
  removeAppointmentPaymentAttemptResumeDraftStrictPersisted,
} from "@/app/booking/checkout/appointmentPaymentAttemptResumeDraftStore";
import {
  validateAppointmentSubmissionAttemptDraft,
  type AppointmentSubmissionAttemptDraftV1,
} from "@/app/booking/checkout/appointmentSubmissionAttemptDraft";
import {
  createAppointmentSubmissionAttemptDraftStrictPersisted,
  inspectAppointmentSubmissionAttemptDraftPersisted,
  isLegalAppointmentSubmissionStateTransition,
  replaceAppointmentSubmissionAttemptDraftStrictPersisted,
  removeAppointmentSubmissionAttemptDraftStrictPersisted,
} from "@/app/booking/checkout/appointmentSubmissionAttemptDraftStore";

import { SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION } from "./activation";
import { createSpecialistLocationGatewayOwnerKey } from "./canonicalKey";
import {
  validateSpecialistLocationGatewayNavigationAuthority,
  type SpecialistLocationGatewayCommittedNavigationLineageV1,
} from "./contract";
import { createSpecialistLocationGatewayAppointmentDestinationFingerprintFromStepOneFingerprint } from "./appointmentDestinationFingerprint";
import {
  beginSpecialistLocationGatewayTerminalWrite,
  completeSpecialistLocationGatewayTerminalWrite,
  specialistLocationGatewayTerminalWriteIsCurrent,
  specialistLocationGatewayTerminalWriteLeaseIsCurrent,
  type SpecialistLocationGatewayTerminalWriteLease,
} from "./terminalWriteCoordinator";

import { appointmentPaymentUsesOwnedGiftCardTenderV1 } from "@/app/booking/checkout/appointmentGiftCardTenderEvidenceV1";
import { frontendGiftCardAppointmentSnapshotFingerprintV1, frontendGiftCardAppointmentHoldMatchesPaymentV1 } from "@/app/booking/checkout/frontendGiftCardAppointmentLedgerV1";
import { readFrontendGiftCardAppointmentHoldForTeardownV1 } from "@/app/commerce/packages/frontendPackagePurchaseLedgerV2";

export const SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_ATTEMPT_ORIGIN_SCHEMA_VERSION =
  1 as const;
export const SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_ATTEMPT_ORIGIN_STORAGE_KEY =
  "volera:specialist-location-gateway-terminal-attempt-origins:appointment:v1";
export const SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY =
  "volera:specialist-location-gateway-appointment-terminal-attempt-aggregates:v1";
export const SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_ATTEMPT_ORIGIN_MAX_AGE_MS =
  24 * 60 * 60 * 1_000 + 10 * 60 * 1_000;

const APPOINTMENT_ATTEMPT_FINGERPRINT_PREFIX =
  "specialist-location-gateway-appointment-attempt-v1-";

export type SpecialistLocationGatewayAppointmentAttemptKind =
  "payment" | "payment-free-submission";

export interface SpecialistLocationGatewayAppointmentAttemptOriginV1 {
  schemaVersion: typeof SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_ATTEMPT_ORIGIN_SCHEMA_VERSION;
  kind: "specialist-location-gateway-appointment-attempt-origin";
  draftId: string;
  ownerKey: string;
  attemptKind: SpecialistLocationGatewayAppointmentAttemptKind;
  activationRevision: typeof SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION;
  attemptFingerprint: string;
  customerAccountId: string;
  destinationKind: "individual";
  destinationDraftId: string;
  destinationDraftFingerprint: string;
  sourceAppointmentDraftFingerprint: string;
  idempotencyKey: string;
  attemptId: string | null;
  attemptActivatedAtEpochMs: number;
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
}

interface SpecialistLocationGatewayAppointmentTerminalAttemptReceiptCommitV1 {
  readonly receipt: AppointmentBookingReceiptV2;
  readonly terminalLineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
}

/**
 * Minimal durable evidence that the exact historical Membership hold was
 * provider-released after a definitive no-booking result. The mutable
 * reservation ledger may be reclaimed only after this commit is canonical.
 */
interface MembershipSessionReservationReleaseCommitV1 {
  readonly schemaVersion: 1;
  readonly kind: "membership-session-reservation-release-commit";
  readonly reservationSchemaVersion: 1 | 2;
  readonly reservationProviderId: string;
  readonly reservationProviderRevision: string;
  readonly reservationId: string;
  readonly holdId: string;
  readonly holdVersion: string;
  readonly rejectionId: string;
  readonly releaseOperationId: string;
  readonly releaseIdempotencyKey: string;
  readonly releaseOperationCreatedAtEpochMs: number;
  readonly releasedAtEpochMs: number;
}

export interface SpecialistLocationGatewayAppointmentResultsEntryProofV1 {
  readonly expectedLocale: string;
  readonly preference: AppointmentResultsEntryPreferenceV1;
  readonly sourceAppointmentDraft: AppointmentBookingDraftV1;
}

export interface SpecialistLocationGatewayAppointmentResultsEntryProofCaptureInput {
  readonly expectedHandoffId: string;
  readonly expectedLocale: string;
  readonly sourceAppointmentDraft: unknown;
}

export interface AppointmentPackageSessionTerminalProofV1 {
  readonly expectedLocale: string;
  readonly proof: PackageSessionBookingTerminalProof;
}

export interface AppointmentPackageSessionTerminalProofCaptureInput {
  readonly expectedHandoffId: string;
  readonly expectedLocale: string;
  readonly sourceAppointmentDraft: unknown;
  readonly reservationHold: unknown;
  /** Current V2 choice: null hold captures None; exact atomic hold captures V3. */
  readonly selection?: PackageSessionReviewSelectionV2;
}

export interface AppointmentMembershipSessionTerminalProofV1 {
  readonly expectedLocale: string;
  readonly proof: MembershipSessionBookingTerminalProof;
}

export interface AppointmentMembershipSessionTerminalProofCaptureInput {
  readonly expectedHandoffId: string;
  readonly expectedLocale: string;
  readonly sourceAppointmentDraft: unknown;
  readonly reservationHold: unknown;
  /** Explicit V2 None/aggregate authority. Omitted only for historical V1. */
  readonly reviewAuthorityV2?: unknown;
}

interface SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1 {
  readonly schemaVersion: 1;
  readonly kind: "specialist-location-gateway-appointment-terminal-attempt-aggregate";
  readonly draftId: string;
  readonly customerAccountId: string;
  readonly attemptKind: SpecialistLocationGatewayAppointmentAttemptKind;
  readonly originRequirement: OriginRequirement;
  readonly revision: number;
  readonly attempt:
    | AppointmentPaymentAttemptResumeDraftV1
    | AppointmentSubmissionAttemptDraftV1;
  readonly origin: SpecialistLocationGatewayAppointmentAttemptOriginV1 | null;
  readonly resultsEntryProof?: SpecialistLocationGatewayAppointmentResultsEntryProofV1 | null;
  readonly packageSessionProof?: AppointmentPackageSessionTerminalProofV1 | null;
  readonly membershipSessionProof?: AppointmentMembershipSessionTerminalProofV1 | null;
  readonly membershipSessionDefinitiveRejection?: MembershipSessionSubmissionDefinitiveRejection;
  readonly membershipSessionReservationReleaseCommit?: MembershipSessionReservationReleaseCommitV1;
  readonly packageSessionDefinitiveRejection?: PackageSessionSubmissionDefinitiveRejectionV1;
  readonly packageSessionAtomicTerminalCommit?: PackageSessionAtomicTerminalCommitV3;
  readonly packageSessionAtomicReleaseCommit?: PackageSessionAtomicReleaseCommitV3;
  readonly receiptCommit: SpecialistLocationGatewayAppointmentTerminalAttemptReceiptCommitV1 | null;
  readonly confirmationAcknowledgedAtEpochMs: number | null;
}

export type ReadSpecialistLocationGatewayAppointmentAttemptOriginResult =
  | {
      status: "ready";
      origin: SpecialistLocationGatewayAppointmentAttemptOriginV1;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    }
  | { status: "absent" | "owner-mismatch" | "unreadable" };

type OriginRequirement = "gateway" | "legacy-non-gateway";

interface SharedInvocationInput {
  originRequirement: OriginRequirement;
  allowOriginCreation: boolean;
  sourceAppointmentDraft: AppointmentBookingDraftV1 | null;
  freshLineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
  isCurrentOwner: () => boolean;
}

export type InvokeSpecialistLocationGatewayAppointmentAttemptResult<Result> =
  | {
      status: "invoked";
      authority: OriginRequirement;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
      providerInvocation:
        { status: "returned"; value: Result } | { status: "threw" };
    }
  | { status: "refused" };

export type TransitionSpecialistLocationGatewayAppointmentAttemptResult =
  | {
      status: "transitioned" | "absorbed-terminal";
      attempt:
        | AppointmentPaymentAttemptResumeDraftV1
        | AppointmentSubmissionAttemptDraftV1;
    }
  | { status: "refused" };

type CreateSpecialistLocationGatewayAppointmentAttemptResult<Attempt> =
  | { status: "created" | "absorbed-terminal"; attempt: Attempt }
  | { status: "refused" };

type SupportedAttempt =
  | {
      attemptKind: "payment";
      attempt: AppointmentPaymentAttemptResumeDraftV1;
    }
  | {
      attemptKind: "payment-free-submission";
      attempt: AppointmentSubmissionAttemptDraftV1;
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
) {
  const actual = Object.keys(value).sort();
  const canonical = [...expected].sort();
  return (
    actual.length === canonical.length &&
    actual.every((key, index) => key === canonical[index])
  );
}

function isIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 512 &&
    !/[\u0000-\u001f\u007f]/u.test(value)
  );
}

function isOwnerKey(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.startsWith("specialist-location-gateway-owner-v1:") &&
    value.length <= 20_000
  );
}

function isEpoch(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= 8_640_000_000_000_000
  );
}

function hashSerializableSnapshot(value: string, seed: number) {
  let hash = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16_777_619);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x7feb352d);
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x846ca68b);
  hash ^= hash >>> 16;
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function createAttemptFingerprint(value: SupportedAttempt) {
  try {
    const serialized =
      value.attemptKind === "payment"
        ? JSON.stringify([
            value.attemptKind,
            value.attempt.draftId,
            value.attempt.customerAccountId,
            value.attempt.reviewDraft,
            Object.prototype.hasOwnProperty.call(
              value.attempt,
              "presentationSnapshot",
            ),
            value.attempt.presentationSnapshot ?? null,
            Object.prototype.hasOwnProperty.call(
              value.attempt,
              "staffChoiceEvidence",
            ),
            value.attempt.staffChoiceEvidence ?? null,
            value.attempt.checkoutContext,
            value.attempt.plan,
            value.attempt.attemptId,
            value.attempt.idempotencyKey,
          ])
        : JSON.stringify([
            value.attemptKind,
            value.attempt.draftId,
            value.attempt.customerAccountId,
            value.attempt.createdAtEpochMs,
            value.attempt.sourceAppointmentDraft,
            value.attempt.reviewDraft,
            value.attempt.staffChoiceEvidence,
            value.attempt.presentationSnapshot,
            value.attempt.idempotencyKey,
          ]);
    const digest = [
      0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35, 0x27d4eb2f, 0x165667b1,
      0xd3a2646c, 0xfd7046c5,
    ]
      .map((seed) => hashSerializableSnapshot(serialized, seed))
      .join("");
    return `${APPOINTMENT_ATTEMPT_FINGERPRINT_PREFIX}${digest}`;
  } catch {
    return null;
  }
}

function isAttemptFingerprint(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^specialist-location-gateway-appointment-attempt-v1-[a-f0-9]{64}$/u.test(
      value,
    )
  );
}

function cloneCommittedLineage(value: unknown) {
  try {
    const validation =
      validateSpecialistLocationGatewayNavigationAuthority(value);
    return validation.valid &&
      validation.draft.kind === "specialist-location-gateway-navigation-lineage"
      ? validation.draft
      : null;
  } catch {
    return null;
  }
}

function validateSupportedAttempt(
  attemptKind: SpecialistLocationGatewayAppointmentAttemptKind,
  value: unknown,
): SupportedAttempt | null {
  try {
    if (attemptKind === "payment") {
      const validation = validateAppointmentPaymentAttemptResumeDraft(value);
      return validation.valid
        ? { attemptKind, attempt: validation.draft }
        : null;
    }
    const validation = validateAppointmentSubmissionAttemptDraft(value);
    return validation.valid &&
      Object.prototype.hasOwnProperty.call(
        validation.draft,
        "createdAtEpochMs",
      ) &&
      isEpoch(validation.draft.createdAtEpochMs)
      ? { attemptKind, attempt: validation.draft }
      : null;
  } catch {
    return null;
  }
}

function getAttemptActivatedAtEpochMs(value: SupportedAttempt) {
  return value.attemptKind === "payment"
    ? value.attempt.checkoutContext.quote.issuedAtEpochMs
    : value.attempt.createdAtEpochMs!;
}

function getAttemptId(value: SupportedAttempt) {
  return value.attemptKind === "payment" ? value.attempt.attemptId : null;
}

function getWorkingAppointmentDraft(value: SupportedAttempt) {
  return value.attempt.reviewDraft.intakeDraft.schedulingDraft.appointmentDraft;
}

function getPaymentAuthorizedSourceDraft(
  attempt: AppointmentPaymentAttemptResumeDraftV1,
) {
  const state =
    attempt.state.kind === "terminal"
      ? attempt.state.priorState
      : attempt.state;
  return state.kind === "authorized-pending-booking"
    ? state.sourceAppointmentDraft
    : null;
}

function validateAppointmentResultsEntryProofForAttempt(input: {
  attempt: SupportedAttempt;
  value: unknown;
}):
  | {
      valid: true;
      proof: SpecialistLocationGatewayAppointmentResultsEntryProofV1 | null;
    }
  | { valid: false } {
  try {
    const requiresResultsEntryProof = isResultsAppointmentDraftId(
      input.attempt.attempt.draftId,
    );
    if (input.value === null) {
      return requiresResultsEntryProof
        ? { valid: false }
        : { valid: true, proof: null };
    }
    if (
      !requiresResultsEntryProof ||
      !isRecord(input.value) ||
      !hasExactKeys(input.value, [
        "expectedLocale",
        "preference",
        "sourceAppointmentDraft",
      ]) ||
      typeof input.value.expectedLocale !== "string"
    ) {
      return { valid: false };
    }

    const preferenceValidation = validateAppointmentResultsEntryPreference(
      input.value.preference,
    );
    const sourceValidation = validateAppointmentStepOneBookingDraft(
      input.value.sourceAppointmentDraft,
    );
    if (!preferenceValidation.valid || !sourceValidation.valid) {
      return { valid: false };
    }
    const preference = preferenceValidation.draft;
    const sourceAppointmentDraft = sourceValidation.draft;
    if (
      input.value.expectedLocale !== preference.locale ||
      sourceAppointmentDraft.draftId !== input.attempt.attempt.draftId
    ) {
      return { valid: false };
    }

    // Terminal aggregates outlive the mutable Results preference. Re-run the
    // complete source/customer/locale/handoff/destination binding at the
    // preference's own creation frontier, deliberately omitting only current
    // wall-clock freshness.
    const historical = resolveAppointmentResultsEntryPreference({
      preference,
      sourceAppointmentDraft,
      expectedCustomerAccountId: input.attempt.attempt.customerAccountId,
      expectedLocale: input.value.expectedLocale,
      expectedHandoffId: preference.handoffId,
      nowEpochMs: preference.createdAtEpochMs,
    });
    if (historical.status !== "ready") return { valid: false };

    if (
      input.attempt.attemptKind === "payment" &&
      !isSupportedSpecialistAppointmentWorkingDraftTransition({
        sourceDraft: sourceAppointmentDraft,
        workingDraft: getWorkingAppointmentDraft(input.attempt),
        staffChoiceEvidence:
          input.attempt.attempt.staffChoiceEvidence ?? null,
        timeProfessionalMutationEvidence:
          input.attempt.attempt.reviewDraft.intakeDraft.schedulingDraft
            .timeProfessionalMutationEvidence,
        evaluatedAtEpochMs: getAttemptActivatedAtEpochMs(input.attempt),
      })
    ) {
      return { valid: false };
    }

    const attemptSourceAppointmentDraft =
      input.attempt.attemptKind === "payment-free-submission"
        ? input.attempt.attempt.sourceAppointmentDraft
        : getPaymentAuthorizedSourceDraft(input.attempt.attempt);
    if (
      attemptSourceAppointmentDraft !== null &&
      !sameSnapshot(attemptSourceAppointmentDraft, sourceAppointmentDraft)
    ) {
      return { valid: false };
    }

    return {
      valid: true,
      proof: {
        expectedLocale: preference.locale,
        preference,
        sourceAppointmentDraft,
      },
    };
  } catch {
    return { valid: false };
  }
}

function captureCurrentAppointmentResultsEntryProofForAttempt(input: {
  attempt: SupportedAttempt;
  captureInput: unknown;
}):
  | {
      status: "ready";
      proof: SpecialistLocationGatewayAppointmentResultsEntryProofV1 | null;
    }
  | { status: "refused" } {
  try {
    const requiresResultsEntryProof = isResultsAppointmentDraftId(
      input.attempt.attempt.draftId,
    );
    if (!requiresResultsEntryProof) {
      return input.captureInput === null
        ? { status: "ready", proof: null }
        : { status: "refused" };
    }
    if (
      !isRecord(input.captureInput) ||
      !hasExactKeys(input.captureInput, [
        "expectedHandoffId",
        "expectedLocale",
        "sourceAppointmentDraft",
      ]) ||
      typeof input.captureInput.expectedHandoffId !== "string" ||
      typeof input.captureInput.expectedLocale !== "string"
    ) {
      return { status: "refused" };
    }
    const sourceValidation = validateAppointmentStepOneBookingDraft(
      input.captureInput.sourceAppointmentDraft,
    );
    if (
      !sourceValidation.valid ||
      sourceValidation.draft.draftId !== input.attempt.attempt.draftId
    ) {
      return { status: "refused" };
    }
    const persisted = readAppointmentResultsEntryPreferenceStrict({
      sourceAppointmentDraft: sourceValidation.draft,
      expectedCustomerAccountId: input.attempt.attempt.customerAccountId,
      expectedLocale: input.captureInput.expectedLocale,
      expectedHandoffId: input.captureInput.expectedHandoffId,
    });
    if (persisted.status !== "ready") return { status: "refused" };
    const validation = validateAppointmentResultsEntryProofForAttempt({
      attempt: input.attempt,
      value: {
        expectedLocale: input.captureInput.expectedLocale,
        preference: persisted.preference,
        sourceAppointmentDraft: sourceValidation.draft,
      },
    });
    return validation.valid
      ? { status: "ready", proof: validation.proof }
      : { status: "refused" };
  } catch {
    return { status: "refused" };
  }
}

function validatePackageSessionProofForAttempt(input: {
  attempt: SupportedAttempt;
  value: unknown;
}):
  | { valid: true; proof: AppointmentPackageSessionTerminalProofV1 | null }
  | { valid: false } {
  try {
    const required = isPackageSessionAppointmentDraftId(
      input.attempt.attempt.draftId,
    );
    if (input.value === null) {
      return required ? { valid: false } : { valid: true, proof: null };
    }
    if (
      !required ||
      !isRecord(input.value) ||
      !hasExactKeys(input.value, ["expectedLocale", "proof"]) ||
      input.value.expectedLocale !==
        input.attempt.attempt.reviewDraft.intakeDraft.locale
    ) {
      return { valid: false };
    }
    const validated = validatePackageSessionBookingTerminalProofVersioned(
      input.value.proof,
    );
    if (!validated.valid) return { valid: false };
    const proof = validated.proof;
    const source =
      input.attempt.attemptKind === "payment-free-submission"
        ? input.attempt.attempt.sourceAppointmentDraft
        : getPaymentAuthorizedSourceDraft(input.attempt.attempt);
    if (proof.schemaVersion !== 1) {
      const context = proof.selection.allocation.request.context;
      return proof.customerAccountId === input.attempt.attempt.customerAccountId &&
        proof.draftId === input.attempt.attempt.draftId && proof.expectedLocale === input.value.expectedLocale &&
        proof.capturedAtEpochMs >= getAttemptActivatedAtEpochMs(input.attempt) &&
        (source === null || sameSnapshot(source, context.sourceAppointmentDraft)) &&
        sameSnapshot(proof.reviewDraft, input.attempt.attempt.reviewDraft) &&
        sameSnapshot(context.staffChoiceEvidence, input.attempt.attempt.staffChoiceEvidence ?? null)
        ? { valid: true, proof: Object.freeze({ expectedLocale: input.value.expectedLocale as string, proof }) }
        : { valid: false };
    }
    if (
      proof.customerAccountId !== input.attempt.attempt.customerAccountId ||
      proof.sourceAppointmentDraft.draftId !== input.attempt.attempt.draftId ||
      proof.capturedAtEpochMs < getAttemptActivatedAtEpochMs(input.attempt) ||
      (source !== null && !sameSnapshot(source, proof.sourceAppointmentDraft))
    ) {
      return { valid: false };
    }
    const authority = resolvePackageSessionBookingReviewAuthority({
      preference: proof.preference,
      sourceAppointmentDraft: proof.sourceAppointmentDraft,
      reviewDraft: input.attempt.attempt.reviewDraft,
      staffChoiceEvidence: input.attempt.attempt.staffChoiceEvidence ?? null,
      evaluatedAtEpochMs: proof.capturedAtEpochMs,
    });
    if (
      !authority ||
      resolvePackageSessionBookingReservationHold({
        hold: proof.reservationHold,
        preference: proof.preference,
        sourceAppointmentDraft: proof.sourceAppointmentDraft,
        expectedStaffAuthorityFingerprint: authority.staffAuthorityFingerprint,
        expectedSelectedSlotId: authority.selectedSlotId,
        expectedSelectedSlotVersion: authority.selectedSlotVersion,
        expectedSelectedStartAtEpochMs: authority.selectedStartAtEpochMs,
        expectedTimeZone: authority.timeZone,
        nowEpochMs: proof.capturedAtEpochMs,
      }).status !== "ready"
    ) {
      return { valid: false };
    }
    return {
      valid: true,
      proof: Object.freeze({
        expectedLocale: input.value.expectedLocale as string,
        proof,
      }),
    };
  } catch {
    return { valid: false };
  }
}

/** Frozen Membership authority is validated historically, independently of mutable stores. */
function validateMembershipSessionProofForAttempt(input: {
  attempt: SupportedAttempt;
  value: unknown;
}):
  | { valid: true; proof: AppointmentMembershipSessionTerminalProofV1 | null }
  | { valid: false } {
  try {
    const required = isMembershipSessionAppointmentDraftId(input.attempt.attempt.draftId);
    if (input.value === null) return required ? { valid: false } : { valid: true, proof: null };
    if (!required || !isRecord(input.value) || !hasExactKeys(input.value, ["expectedLocale", "proof"]) ||
      input.value.expectedLocale !== input.attempt.attempt.reviewDraft.intakeDraft.locale) return { valid: false };
    const validated = validateMembershipSessionBookingTerminalProof(input.value.proof);
    if (!validated.valid) return { valid: false };
    const proof = validated.proof;
    const heldAuthority = getMembershipSessionBookingTerminalProofAuthority(proof);
    const source = input.attempt.attemptKind === "payment-free-submission"
      ? input.attempt.attempt.sourceAppointmentDraft : getPaymentAuthorizedSourceDraft(input.attempt.attempt);
    if (proof.customerAccountId !== input.attempt.attempt.customerAccountId ||
      proof.draftId !== input.attempt.attempt.draftId || proof.expectedLocale !== input.value.expectedLocale ||
      proof.capturedAtEpochMs < getAttemptActivatedAtEpochMs(input.attempt) ||
      (source !== null && !sameSnapshot(source, heldAuthority.sourceAppointmentDraft)) ||
      !sameSnapshot(heldAuthority.reviewDraft, input.attempt.attempt.reviewDraft) ||
      !sameSnapshot(heldAuthority.staffChoiceEvidence, input.attempt.attempt.staffChoiceEvidence ?? null)) return { valid: false };
    // The terminal-proof validator already resolves the full held Review at
    // capturedAtEpochMs. The exact joins above bind that proven Review to this
    // attempt; re-resolving the identical immutable input adds no authority.
    return { valid: true, proof: Object.freeze({ expectedLocale: input.value.expectedLocale as string, proof }) };
  } catch { return { valid: false }; }
}

/**
 * Package and Membership provider authority requires real persisted authentication, never the
 * frontend preview owner or a stale route callback. After validation, close on
 * the exact captured raw identity without invoking a replaceable public reader.
 * This guard intentionally does not govern historical, read-only recovery.
 */
function capturePackageSessionAuthenticatedOwnerGuard(
  customerAccountId: string,
): (() => boolean) | null {
  try {
    if (
      customerAccountId === FRONTEND_UI_ONLY_CUSTOMER_ACCOUNT_ID ||
      typeof window === "undefined"
    ) return null;
    const storage = window.localStorage;
    const owner = readStoredCustomerAuthSessionStrict();
    if (
      owner.status !== "ready" ||
      owner.session === null ||
      owner.session.account.uuid !== customerAccountId ||
      owner.session.token.trim().length === 0 ||
      owner.session.tokenType.trim().length === 0 ||
      window.localStorage !== storage
    ) return null;
    const captured = readCustomerOwnedSessionStorageRawValueStable({
      storage,
      storageKey: CUSTOMER_AUTH_SESSION_STORAGE_KEY,
    });
    if (
      captured.status !== "ready" ||
      captured.rawValue === null ||
      !sameSnapshot(JSON.parse(captured.rawValue), owner.session)
    ) return null;
    const rawValue = captured.rawValue;
    return () => {
      try {
        if (window.localStorage !== storage) return false;
        const current = readCustomerOwnedSessionStorageRawValueFromCapturedReader({
          storage,
          storageKey: CUSTOMER_AUTH_SESSION_STORAGE_KEY,
        });
        return current.status === "ready" && current.rawValue === rawValue;
      } catch { return false; }
    };
  } catch { return null; }
}

function captureCurrentPackageSessionProofForAttempt(input: {
  attempt: SupportedAttempt;
  captureInput: unknown;
}):
  | {
      status: "ready";
      proof: AppointmentPackageSessionTerminalProofV1 | null;
      authenticatedOwnerIsCurrent: () => boolean;
    }
  | { status: "refused" } {
  try {
    if (!isPackageSessionAppointmentDraftId(input.attempt.attempt.draftId)) {
      return input.captureInput === null
        ? { status: "ready", proof: null, authenticatedOwnerIsCurrent: () => true }
        : { status: "refused" };
    }
    const authenticatedOwnerIsCurrent =
      capturePackageSessionAuthenticatedOwnerGuard(input.attempt.attempt.customerAccountId);
    if (!authenticatedOwnerIsCurrent) return { status: "refused" };
    if (isRecord(input.captureInput) && Object.hasOwn(input.captureInput, "selection")) {
      return captureCurrentPackageSessionNoneProofForAttempt({ attempt: input.attempt, capture: input.captureInput, authenticated: authenticatedOwnerIsCurrent });
    }
    if (
      !isRecord(input.captureInput) ||
      !hasExactKeys(input.captureInput, [
        "expectedHandoffId",
        "expectedLocale",
        "sourceAppointmentDraft",
        "reservationHold",
      ])
    ) {
      return { status: "refused" };
    }
    const {
      expectedHandoffId,
      expectedLocale,
      sourceAppointmentDraft,
      reservationHold,
    } = input.captureInput;
    if (
      typeof expectedHandoffId !== "string" ||
      expectedLocale !== input.attempt.attempt.reviewDraft.intakeDraft.locale
    ) return { status: "refused" };
    const source = validateAppointmentStepOneBookingDraft(
      sourceAppointmentDraft,
    );
    const hold = validatePackageSessionBookingReservationHold(reservationHold);
    const nowEpochMs = Date.now();
    if (
      !source.valid ||
      !hold.valid ||
      !isEpoch(nowEpochMs) ||
      source.draft.draftId !== input.attempt.attempt.draftId
    ) {
      return { status: "refused" };
    }
    const atomicReservationIsAbsent = capturePackageSessionAtomicReservationAbsenceGuardV2(input.attempt.attempt.draftId, nowEpochMs);
    if (!atomicReservationIsAbsent) return { status: "refused" };
    const persisted = readAppointmentPackageSessionPreferenceStrict({
      sourceAppointmentDraft: source.draft,
      expectedCustomerAccountId: input.attempt.attempt.customerAccountId,
      expectedHandoffId,
      nowEpochMs,
    });
    if (
      persisted.status !== "ready" ||
      inspectPackageSessionBookingIntentExactPersisted(
        persisted.preference.packageIntent,
        nowEpochMs,
      ).status !== "present-exact"
    ) {
      return { status: "refused" };
    }
    // A structurally valid caller-supplied hold is insufficient. Fresh
    // terminal creation must consume the exact durable provider frontier;
    // historical aggregate readers deliberately do not depend on this row.
    const reservation = readPackageSessionBookingReservationForAppointmentStrict({
      appointmentDraftId: input.attempt.attempt.draftId,
      customerAccountId: input.attempt.attempt.customerAccountId,
      evaluatedAtEpochMs: nowEpochMs,
    });
    if (
      reservation.status !== "ready" ||
      reservation.record.status !== "held" ||
      reservation.record.outcome?.status !== "held" ||
      reservation.record.updatedAtEpochMs > nowEpochMs ||
      !sameSnapshot(reservation.record.heldSnapshot, hold.hold) ||
      !sameSnapshot(reservation.record.operation.sourceAppointmentDraft, source.draft) ||
      !sameSnapshot(reservation.record.operation.preference, persisted.preference) ||
      !sameSnapshot(reservation.record.operation.reviewDraft, input.attempt.attempt.reviewDraft) ||
      !sameSnapshot(
        reservation.record.operation.staffChoiceEvidence,
        input.attempt.attempt.staffChoiceEvidence ?? null,
      )
    ) {
      return { status: "refused" };
    }
    const reservationIsCurrent =
      capturePackageSessionBookingReservationRecordCurrentGuard(
        reservation.record,
        nowEpochMs,
      );
    if (!reservationIsCurrent) return { status: "refused" };
    const proof = createPackageSessionBookingTerminalProof({
      terminalBoundary: "appointment-submission",
      sourceAppointmentDraft: source.draft,
      preference: persisted.preference,
      reservationHold: hold.hold,
      expectedCustomerAccountId: input.attempt.attempt.customerAccountId,
      capturedAtEpochMs: nowEpochMs,
    });
    const validated = proof
      ? validatePackageSessionProofForAttempt({
          attempt: input.attempt,
          value: { expectedLocale, proof },
        })
      : null;
    const exactOwnerIsCurrent = () => authenticatedOwnerIsCurrent() && reservationIsCurrent() && atomicReservationIsAbsent();
    return validated?.valid && exactOwnerIsCurrent()
      ? { status: "ready", proof: validated.proof, authenticatedOwnerIsCurrent: exactOwnerIsCurrent }
      : { status: "refused" };
  } catch {
    return { status: "refused" };
  }
}

/** Explicit None is persisted choice, never the absence of a hold. All mutable
 * publications are rejoined inside the canonical CAS, including an absent
 * reservation ledger. Unknown/held/resolved foreign rows cannot become None. */
function captureCurrentPackageSessionNoneProofForAttempt(input: {
  attempt: SupportedAttempt; capture: Record<string, unknown>; authenticated: () => boolean;
}): { status: "ready"; proof: AppointmentPackageSessionTerminalProofV1; authenticatedOwnerIsCurrent: () => boolean } | { status: "refused" } {
  try {
    const { authenticated } = input;
    const capture = capturePackageSessionBookingProviderPlainData(input.capture);
    if (!isRecord(capture)) return { status: "refused" };
    if (!hasExactKeys(capture, ["expectedHandoffId", "expectedLocale", "sourceAppointmentDraft", "reservationHold", "selection"]) ||
      typeof capture.expectedHandoffId !== "string" ||
      (capture.expectedLocale !== "en" && capture.expectedLocale !== "ro")) return { status: "refused" };
    const now = Date.now(); const source = validateAppointmentStepOneBookingDraft(capture.sourceAppointmentDraft);
    if (!source.valid || !isEpoch(now) || source.draft.draftId !== input.attempt.attempt.draftId) return { status: "refused" };
    const storage = window.sessionStorage;
    const authStorage = window.localStorage;
    const authPublication = readCustomerOwnedSessionStorageRawValueStable({ storage: authStorage, storageKey: CUSTOMER_AUTH_SESSION_STORAGE_KEY });
    if (authPublication.status !== "ready" || authPublication.rawValue === null) return { status: "refused" };
    const storageKeys = [PACKAGE_SESSION_REVIEW_SELECTION_STORAGE_KEY_V2, APPOINTMENT_PACKAGE_SESSION_PREFERENCE_STORAGE_KEY,
      PACKAGE_SESSION_BOOKING_INTENT_STORAGE_KEY, PACKAGE_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY, PACKAGE_SESSION_ATOMIC_RESERVATION_STORAGE_KEY_V2,
      APPOINTMENT_PACKAGE_SESSION_PREFERENCE_V2_STORAGE_KEY, PACKAGE_SESSION_BOOKING_INTENT_V2_STORAGE_KEY];
    const publications = storageKeys.map(storageKey => ({ storageKey, value: readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey }) }));
    if (publications.some(row => row.value.status !== "ready")) return { status: "refused" };
    const selection = readPackageSessionReviewSelectionStrictV2({ draftId: source.draft.draftId,
      customerAccountId: input.attempt.attempt.customerAccountId, evaluatedAtEpochMs: now });
    const entryPreference = readPackageSessionCurrentEntryForSelectionV2({ sourceAppointmentDraft: source.draft,
      expectedCustomerAccountId: input.attempt.attempt.customerAccountId, expectedHandoffId: capture.expectedHandoffId, nowEpochMs: now });
    const reservation = readPackageSessionBookingReservationForAppointmentStrict({ appointmentDraftId: source.draft.draftId,
      customerAccountId: input.attempt.attempt.customerAccountId, evaluatedAtEpochMs: now });
    const atomic = readPackageSessionAtomicReservationStrictV2({ draftId: source.draft.draftId,
      customerAccountId: input.attempt.attempt.customerAccountId, evaluatedAtEpochMs: now });
    const wantsNone = capture.reservationHold === null;
    if (selection.status !== "ready" || !samePackageSessionRedemptionDataV2(selection.selection, capture.selection) ||
      reservation.status !== "absent" || !entryPreference ||
      (wantsNone ? atomic.status !== "absent" : atomic.status !== "ready" || atomic.record.status !== "held" ||
        !sameSnapshot(atomic.record.heldSnapshot, capture.reservationHold) || !sameSnapshot(atomic.record.operation.selection, selection.selection) ||
        !sameSnapshot(atomic.record.operation.reviewDraft, input.attempt.attempt.reviewDraft))) return { status: "refused" };
    const context = selection.selection.allocation.request.context; const lineage = entryPreference.optionLineage;
    if (!samePackageSessionRedemptionDataV2(context.sourceAppointmentDraft, source.draft) ||
      !samePackageSessionRedemptionDataV2(context.entry, { kind: "activity-package", handoffId: entryPreference.handoffId,
        activityPointer: lineage.sourceActivityPointer, activityId: lineage.sourceActivityId, businessId: lineage.sourceBusinessId,
        snapshotId: lineage.sourceSnapshotId, dataRevision: lineage.sourceDataRevision, entityRevision: lineage.sourceEntityRevision,
        providerId: lineage.providerId, providerActionId: lineage.providerActionId, providerActionRevision: lineage.providerActionRevision,
        entitlementId: lineage.entitlementId, soldPackageVersion: lineage.soldPackageVersion, benefitId: lineage.benefitId })) return { status: "refused" };
    const proof = !wantsNone && atomic.status === "ready" ? createPackageSessionBookingReservedTerminalProofV3({ record: atomic.record,
      expectedCustomerAccountId: input.attempt.attempt.customerAccountId, expectedDraftId: input.attempt.attempt.draftId,
      expectedLocale: capture.expectedLocale, capturedAtEpochMs: now }) : createPackageSessionNoneTerminalProofV2({ selection: selection.selection, reviewDraft: input.attempt.attempt.reviewDraft,
      expectedCustomerAccountId: input.attempt.attempt.customerAccountId, expectedDraftId: input.attempt.attempt.draftId,
      expectedLocale: capture.expectedLocale, capturedAtEpochMs: now });
    const checked = proof ? validatePackageSessionProofForAttempt({ attempt: input.attempt, value: { expectedLocale: capture.expectedLocale, proof } }) : null;
    if (!checked?.valid || !checked.proof) return { status: "refused" };
    const isCurrent = () => {
      try {
        if (!authenticated() || isCustomerOwnedSessionStorageQuarantined() || window.sessionStorage !== storage) return false;
        for (const row of publications) {
          const current = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey: row.storageKey });
          if (row.value.status !== "ready" || current.status !== "ready" || current.rawValue !== row.value.rawValue) return false;
        }
        const ownerStillCurrent = authenticated() && !isCustomerOwnedSessionStorageQuarantined();
        const currentEpoch = Date.now();
        if (!(ownerStillCurrent && isEpoch(currentEpoch) && currentEpoch >= now &&
          currentEpoch < selection.selection.allocation.expiresAtEpochMs &&
          (wantsNone || (atomic.status === "ready" && atomic.record.heldSnapshot !== null && currentEpoch < atomic.record.heldSnapshot.expiresAtEpochMs)) &&
          (input.attempt.attemptKind !== "payment" || currentEpoch < input.attempt.attempt.checkoutContext.quote.expiresAtEpochMs) &&
          currentEpoch < entryPreference.expiresAtEpochMs && currentEpoch < entryPreference.packageIntent.expiresAtEpochMs)) return false;
        // Date.now is injectable too. Close on the already captured readers
        // after the final clock callback so it cannot replace this authority.
        if (window.localStorage !== authStorage || window.sessionStorage !== storage) return false;
        const finalAuth = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage: authStorage, storageKey: CUSTOMER_AUTH_SESSION_STORAGE_KEY });
        if (finalAuth.status !== "ready" || finalAuth.rawValue !== authPublication.rawValue) return false;
        for (const row of publications) {
          const final = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey: row.storageKey });
          if (row.value.status !== "ready" || final.status !== "ready" || final.rawValue !== row.value.rawValue) return false;
        }
        return !isCustomerOwnedSessionStorageQuarantined();
      } catch { return false; }
    };
    return isCurrent() ? { status: "ready", proof: checked.proof, authenticatedOwnerIsCurrent: isCurrent } : { status: "refused" };
  } catch { return { status: "refused" }; }
}

/** V2 current capture: reversible selection is frozen exactly once at activation. */
function captureCurrentMembershipSessionProofV2ForAttempt(input: { attempt: SupportedAttempt; capture: Record<string, unknown>; authenticated: () => boolean }):
  | { status: "ready"; proof: AppointmentMembershipSessionTerminalProofV1; authenticatedOwnerIsCurrent: () => boolean }
  | { status: "refused" } {
  try {
    const { capture, authenticated } = input;
    if (!hasExactKeys(capture, ["expectedHandoffId", "expectedLocale", "sourceAppointmentDraft", "reservationHold", "reviewAuthorityV2"]) ||
      typeof capture.expectedHandoffId !== "string" || typeof capture.expectedLocale !== "string") return { status: "refused" };
    const now = Date.now();
    if (!isEpoch(now)) return { status: "refused" };
    const proof = createMembershipSessionBookingTerminalProofV2({ authority: capture.reviewAuthorityV2, reservationHold: capture.reservationHold,
      expectedHandoffId: capture.expectedHandoffId, expectedCustomerAccountId: input.attempt.attempt.customerAccountId,
      expectedLocale: capture.expectedLocale, capturedAtEpochMs: now });
    const source = validateAppointmentStepOneBookingDraft(capture.sourceAppointmentDraft);
    if (!proof || !source.valid || !sameSnapshot(source.draft, proof.authority.context.preference.sourceAppointmentDraft)) return { status: "refused" };
    const storage = window.sessionStorage;
    const keys = [MEMBERSHIP_SESSION_BOOKING_STORAGE_KEY, APPOINTMENT_MEMBERSHIP_SESSION_PREFERENCE_STORAGE_KEY,
      MEMBERSHIP_SESSION_REDEMPTION_SELECTION_STORAGE_KEY, MEMBERSHIP_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY,
      MEMBERSHIP_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY_V2];
    const publications = keys.map((storageKey) => ({ storageKey, value: readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey }) }));
    if (publications.some(({ value }, index) => value.status !== "ready" || (index < 3 && value.rawValue === null))) return { status: "refused" };
    const preference = proof.authority.context.preference;
    const persisted = inspectAppointmentMembershipSessionPreferenceExactPersisted(preference, now);
    const intent = readMembershipSessionBookingIntentStrict(capture.expectedHandoffId, preference.customerAccountId, now);
    const selection = readMembershipSessionRedemptionSelectionStrict({ appointmentDraftId: preference.draftId, customerAccountId: preference.customerAccountId, nowEpochMs: now });
    const legacy = readMembershipSessionBookingReservationForAppointmentStrict({ appointmentDraftId: preference.draftId, customerAccountId: preference.customerAccountId, evaluatedAtEpochMs: now });
    const reservation = readMembershipSessionBookingReservationForAppointmentStrictV2({ appointmentDraftId: preference.draftId, customerAccountId: preference.customerAccountId, evaluatedAtEpochMs: now });
    if (persisted.status !== "present-exact" || !intent || !sameSnapshot(intent, preference.intent) ||
      selection.status !== "ready" || !sameSnapshot(selection.selection.allocation, proof.authority.allocation) || legacy.status !== "absent") return { status: "refused" };
    let reservationCurrent: (() => boolean) | null = null;
    if (proof.redemption === "none") {
      if (reservation.status !== "absent") return { status: "refused" };
    } else {
      if (reservation.status !== "ready" || reservation.record.status !== "held" || reservation.record.outcome?.status !== "held" ||
        reservation.record.updatedAtEpochMs > now || !sameSnapshot(reservation.record.heldSnapshot, proof.reservationHold) ||
        !sameSnapshot(reservation.record.operation.authority, proof.authority)) return { status: "refused" };
      reservationCurrent = captureMembershipSessionBookingReservationRecordCurrentGuardV2(reservation.record, now);
      if (!reservationCurrent) return { status: "refused" };
    }
    const sourceCurrent = () => {
      try {
        return !isCustomerOwnedSessionStorageQuarantined() && window.sessionStorage === storage && authenticated() &&
          (reservationCurrent === null || reservationCurrent()) && publications.every(({ storageKey, value }) => {
            const latest = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
            return value.status === "ready" && latest.status === "ready" && latest.rawValue === value.rawValue;
          }) && authenticated();
      } catch { return false; }
    };
    const validated = validateMembershipSessionProofForAttempt({ attempt: input.attempt, value: { expectedLocale: capture.expectedLocale, proof } });
    return validated.valid && validated.proof && sourceCurrent()
      ? { status: "ready", proof: validated.proof, authenticatedOwnerIsCurrent: sourceCurrent } : { status: "refused" };
  } catch { return { status: "refused" }; }
}

function captureCurrentMembershipSessionProofForAttempt(input: {
  attempt: SupportedAttempt;
  captureInput: unknown;
}):
  | { status: "ready"; proof: AppointmentMembershipSessionTerminalProofV1 | null; authenticatedOwnerIsCurrent: () => boolean }
  | { status: "refused" } {
  try {
    if (!isMembershipSessionAppointmentDraftId(input.attempt.attempt.draftId)) return input.captureInput === null
      ? { status: "ready", proof: null, authenticatedOwnerIsCurrent: () => true } : { status: "refused" };
    const authenticated = capturePackageSessionAuthenticatedOwnerGuard(input.attempt.attempt.customerAccountId);
    const capture = captureMembershipSessionPlainData(input.captureInput);
    if (authenticated && isRecord(capture) && Object.prototype.hasOwnProperty.call(capture, "reviewAuthorityV2")) {
      return captureCurrentMembershipSessionProofV2ForAttempt({ attempt: input.attempt, capture, authenticated });
    }
    if (!authenticated || !isRecord(capture) || !hasExactKeys(capture, ["expectedHandoffId", "expectedLocale", "sourceAppointmentDraft", "reservationHold"]) ||
      typeof capture.expectedHandoffId !== "string" || capture.expectedLocale !== input.attempt.attempt.reviewDraft.intakeDraft.locale) return { status: "refused" };
    const now = Date.now();
    const source = validateAppointmentStepOneBookingDraft(capture.sourceAppointmentDraft);
    const hold = validateMembershipSessionBookingReservationHold(capture.reservationHold);
    if (!isEpoch(now) || !source.valid || !hold.valid || source.draft.draftId !== input.attempt.attempt.draftId ||
      !sameSnapshot(hold.hold.authority.sourceAppointmentDraft, source.draft)) return { status: "refused" };
    const storage = window.sessionStorage;
    const keys = [MEMBERSHIP_SESSION_BOOKING_STORAGE_KEY, APPOINTMENT_MEMBERSHIP_SESSION_PREFERENCE_STORAGE_KEY, MEMBERSHIP_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY,
      MEMBERSHIP_SESSION_REDEMPTION_SELECTION_STORAGE_KEY, MEMBERSHIP_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY_V2];
    const publications = keys.map((storageKey) => ({ storageKey, value: readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey }) }));
    if (publications.some(({ value }, index) => value.status !== "ready" || (index < 3 && value.rawValue === null))) return { status: "refused" };
    if (readMembershipSessionRedemptionSelectionStrict({ appointmentDraftId: input.attempt.attempt.draftId, customerAccountId: input.attempt.attempt.customerAccountId, nowEpochMs: now }).status !== "absent" ||
      readMembershipSessionBookingReservationForAppointmentStrictV2({ appointmentDraftId: input.attempt.attempt.draftId, customerAccountId: input.attempt.attempt.customerAccountId, evaluatedAtEpochMs: now }).status !== "absent") return { status: "refused" };
    const preference = hold.hold.authority.preference;
    const persisted = inspectAppointmentMembershipSessionPreferenceExactPersisted(preference, now);
    const intent = readMembershipSessionBookingIntentStrict(capture.expectedHandoffId, input.attempt.attempt.customerAccountId, now);
    if (persisted.status !== "present-exact" || !intent || !sameSnapshot(intent, preference.intent) ||
      preference.handoffId !== capture.expectedHandoffId) return { status: "refused" };
    const reservation = readMembershipSessionBookingReservationForAppointmentStrict({ appointmentDraftId: input.attempt.attempt.draftId,
      customerAccountId: input.attempt.attempt.customerAccountId, evaluatedAtEpochMs: now });
    if (reservation.status !== "ready" || reservation.record.status !== "held" || reservation.record.outcome?.status !== "held" ||
      reservation.record.updatedAtEpochMs > now || !sameSnapshot(reservation.record.heldSnapshot, hold.hold) ||
      !sameSnapshot(reservation.record.operation.sourceAppointmentDraft, source.draft) ||
      !sameSnapshot(reservation.record.operation.preference, preference) ||
      !sameSnapshot(reservation.record.operation.reviewDraft, input.attempt.attempt.reviewDraft) ||
      !sameSnapshot(reservation.record.operation.staffChoiceEvidence, input.attempt.attempt.staffChoiceEvidence ?? null) ||
      !sameSnapshot(reservation.record.operation.allocation, hold.hold.authority.allocation)) return { status: "refused" };
    const reservationCurrent = captureMembershipSessionBookingReservationRecordCurrentGuard(reservation.record, now);
    const sourceCurrent = () => {
      try {
        return !isCustomerOwnedSessionStorageQuarantined() && window.sessionStorage === storage && authenticated() && Boolean(reservationCurrent?.()) &&
          publications.every(({ storageKey, value }) => {
            const latest = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
            return value.status === "ready" && latest.status === "ready" && latest.rawValue === value.rawValue;
          });
      } catch { return false; }
    };
    const proof = createMembershipSessionBookingTerminalProof({ reservationHold: hold.hold,
      expectedHandoffId: capture.expectedHandoffId, expectedLocale: capture.expectedLocale as string,
      expectedCustomerAccountId: input.attempt.attempt.customerAccountId, capturedAtEpochMs: now });
    const validated = proof && validateMembershipSessionProofForAttempt({ attempt: input.attempt, value: { expectedLocale: capture.expectedLocale, proof } });
    return validated && validated.valid && sourceCurrent()
      ? { status: "ready", proof: validated.proof, authenticatedOwnerIsCurrent: sourceCurrent } : { status: "refused" };
  } catch { return { status: "refused" }; }
}

function membershipReservationMatchesTerminalAggregate(
  reservation: MembershipSessionBookingReservationOperationRecordV1 | MembershipSessionBookingReservationOperationRecordV2,
  aggregate: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1,
) {
  const proof = aggregate.membershipSessionProof?.proof;
  const hold = reservation.heldSnapshot;
  const historical = proof?.reservationHold;
  return Boolean(historical && hold && reservation.schemaVersion === historical.schemaVersion && reservation.draftId === aggregate.draftId && reservation.customerAccountId === aggregate.customerAccountId &&
    reservation.operation.providerId === historical.providerId && reservation.operation.providerRevision === historical.providerRevision &&
    reservation.operation.reservationId === historical.reservationId &&
    sameSnapshot(hold.authority, historical.authority) &&
    hold.holdId === historical.holdId && hold.createdAtEpochMs >= historical.createdAtEpochMs &&
    (hold.holdVersion !== historical.holdVersion || sameSnapshot(hold, historical)));
}

function validateMembershipSessionReservationReleaseCommit(
  value: unknown,
  proof: AppointmentMembershipSessionTerminalProofV1 | null,
  rejection: MembershipSessionSubmissionDefinitiveRejection | null,
): MembershipSessionReservationReleaseCommitV1 | null {
  try {
    const historical = proof?.proof.reservationHold ?? null;
    if (
      !historical ||
      !rejection ||
      !isRecord(value) ||
      !hasExactKeys(value, [
        "schemaVersion",
        "kind",
        "reservationSchemaVersion",
        "reservationProviderId",
        "reservationProviderRevision",
        "reservationId",
        "holdId",
        "holdVersion",
        "rejectionId",
        "releaseOperationId",
        "releaseIdempotencyKey",
        "releaseOperationCreatedAtEpochMs",
        "releasedAtEpochMs",
      ]) ||
      value.schemaVersion !== 1 ||
      value.kind !== "membership-session-reservation-release-commit" ||
      value.reservationSchemaVersion !== historical.schemaVersion ||
      value.reservationProviderId !== historical.providerId ||
      value.reservationProviderRevision !== historical.providerRevision ||
      value.reservationId !== historical.reservationId ||
      value.holdId !== historical.holdId ||
      value.rejectionId !== rejection.rejectionId ||
      !isIdentifier(value.holdVersion) ||
      !isIdentifier(value.releaseOperationId) ||
      !isIdentifier(value.releaseIdempotencyKey) ||
      !isEpoch(value.releaseOperationCreatedAtEpochMs) ||
      !isEpoch(value.releasedAtEpochMs) ||
      value.releaseOperationCreatedAtEpochMs < rejection.rejectedAtEpochMs ||
      value.releasedAtEpochMs < value.releaseOperationCreatedAtEpochMs
    ) return null;
    return Object.freeze({
      schemaVersion: 1,
      kind: "membership-session-reservation-release-commit",
      reservationSchemaVersion: historical.schemaVersion,
      reservationProviderId: historical.providerId,
      reservationProviderRevision: historical.providerRevision,
      reservationId: historical.reservationId,
      holdId: historical.holdId,
      holdVersion: value.holdVersion,
      rejectionId: rejection.rejectionId,
      releaseOperationId: value.releaseOperationId,
      releaseIdempotencyKey: value.releaseIdempotencyKey,
      releaseOperationCreatedAtEpochMs: value.releaseOperationCreatedAtEpochMs,
      releasedAtEpochMs: value.releasedAtEpochMs,
    });
  } catch {
    return null;
  }
}

function createMembershipSessionReservationReleaseCommit(
  record: MembershipSessionBookingReservationOperationRecordV1 | MembershipSessionBookingReservationOperationRecordV2,
  aggregate: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1,
): MembershipSessionReservationReleaseCommitV1 | null {
  try {
    if (
      !aggregate.membershipSessionProof ||
      !aggregate.membershipSessionDefinitiveRejection ||
      record.status !== "released" ||
      record.outcome?.status !== "released" ||
      record.operation.operation !== "release" ||
      !record.operation.expectedHold ||
      !record.heldSnapshot ||
      !sameSnapshot(record.operation.expectedHold, record.heldSnapshot) ||
      !membershipReservationMatchesTerminalAggregate(record, aggregate)
    ) return null;
    return validateMembershipSessionReservationReleaseCommit({
      schemaVersion: 1,
      kind: "membership-session-reservation-release-commit",
      reservationSchemaVersion: record.schemaVersion,
      reservationProviderId: record.operation.providerId,
      reservationProviderRevision: record.operation.providerRevision,
      reservationId: record.operation.reservationId,
      holdId: record.heldSnapshot.holdId,
      holdVersion: record.heldSnapshot.holdVersion,
      rejectionId: aggregate.membershipSessionDefinitiveRejection.rejectionId,
      releaseOperationId: record.operation.operationId,
      releaseIdempotencyKey: record.operation.idempotencyKey,
      releaseOperationCreatedAtEpochMs: record.operation.createdAtEpochMs,
      releasedAtEpochMs: record.updatedAtEpochMs,
    }, aggregate.membershipSessionProof, aggregate.membershipSessionDefinitiveRejection);
  } catch {
    return null;
  }
}

function packageReservationMatchesTerminalAggregate(
  reservation: PackageSessionBookingReservationOperationRecordV1,
  aggregate: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1,
) {
  const proof = aggregate.packageSessionProof?.proof;
  const hold = reservation.heldSnapshot;
  return Boolean(proof?.schemaVersion === 1 && hold &&
    reservation.draftId === aggregate.draftId &&
    reservation.customerAccountId === aggregate.customerAccountId &&
    reservation.operation.providerId === proof.reservationHold.providerId &&
    reservation.operation.providerRevision === proof.reservationHold.providerRevision &&
    sameSnapshot(reservation.operation.sourceAppointmentDraft, proof.sourceAppointmentDraft) &&
    sameSnapshot(reservation.operation.preference, proof.preference) &&
    sameSnapshot(reservation.operation.reviewDraft, aggregate.attempt.reviewDraft) &&
    sameSnapshot(reservation.operation.staffChoiceEvidence, aggregate.attempt.staffChoiceEvidence ?? null) &&
    hold.holdId === proof.reservationHold.holdId &&
    hold.createdAtEpochMs >= proof.reservationHold.createdAtEpochMs &&
    (hold.holdVersion !== proof.reservationHold.holdVersion ||
      sameSnapshot(hold, proof.reservationHold)));
}

function createSourceFingerprint(value: unknown) {
  try {
    const validation = validateAppointmentStepOneBookingDraft(value);
    if (
      !validation.valid ||
      validation.draft.entryPoint.kind !== "specialist-services"
    ) {
      return null;
    }
    const fingerprint = createAppointmentStepOneDraftFingerprint(
      validation.draft,
    );
    return fingerprint ? { draft: validation.draft, fingerprint } : null;
  } catch {
    return null;
  }
}

function createOriginFromBinding(input: {
  attempt: SupportedAttempt;
  lineage: unknown;
  sourceAppointmentDraftFingerprint: unknown;
}): SpecialistLocationGatewayAppointmentAttemptOriginV1 | null {
  try {
    const lineage = cloneCommittedLineage(input.lineage);
    const sourceAppointmentDraftFingerprint =
      input.sourceAppointmentDraftFingerprint;
    const attempt = input.attempt.attempt;
    const workingDraft = getWorkingAppointmentDraft(input.attempt);
    const destinationDraftFingerprint =
      createSpecialistLocationGatewayAppointmentDestinationFingerprintFromStepOneFingerprint(
        sourceAppointmentDraftFingerprint,
      );
    const attemptFingerprint = createAttemptFingerprint(input.attempt);
    if (
      !lineage ||
      !isAppointmentDraftFingerprint(sourceAppointmentDraftFingerprint) ||
      !destinationDraftFingerprint ||
      !attemptFingerprint ||
      workingDraft.entryPoint.kind !== "specialist-services" ||
      lineage.customerAccountId !== attempt.customerAccountId ||
      lineage.destinationKind !== "individual" ||
      lineage.destinationDraftId !== attempt.draftId ||
      lineage.destinationDraftFingerprint !== destinationDraftFingerprint ||
      lineage.selection.selectedLocationId !== workingDraft.locationId ||
      lineage.selection.selectedUserLocationId !==
        workingDraft.entryPoint.userLocationId
    ) {
      return null;
    }
    const exactSourceDraft =
      input.attempt.attemptKind === "payment"
        ? getPaymentAuthorizedSourceDraft(input.attempt.attempt)
        : input.attempt.attempt.sourceAppointmentDraft;
    if (exactSourceDraft) {
      const exactSource = createSourceFingerprint(exactSourceDraft);
      if (
        !exactSource ||
        exactSource.fingerprint !== sourceAppointmentDraftFingerprint
      ) {
        return null;
      }
    }
    const attemptId = getAttemptId(input.attempt);
    const attemptActivatedAtEpochMs = getAttemptActivatedAtEpochMs(
      input.attempt,
    );
    const ownerKey = createSpecialistLocationGatewayOwnerKey({
      stage: "appointment-attempt-origin-v1",
      fields: [
        input.attempt.attemptKind,
        attempt.draftId,
        attemptFingerprint,
        attempt.customerAccountId,
        destinationDraftFingerprint,
        sourceAppointmentDraftFingerprint,
        attempt.idempotencyKey,
        attemptId,
        attemptActivatedAtEpochMs,
        lineage.ownerKey,
        SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION,
      ],
    });
    const candidate: SpecialistLocationGatewayAppointmentAttemptOriginV1 = {
      schemaVersion:
        SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_ATTEMPT_ORIGIN_SCHEMA_VERSION,
      kind: "specialist-location-gateway-appointment-attempt-origin",
      draftId: attempt.draftId,
      ownerKey,
      attemptKind: input.attempt.attemptKind,
      activationRevision: SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION,
      attemptFingerprint,
      customerAccountId: attempt.customerAccountId,
      destinationKind: "individual",
      destinationDraftId: attempt.draftId,
      destinationDraftFingerprint,
      sourceAppointmentDraftFingerprint,
      idempotencyKey: attempt.idempotencyKey,
      attemptId,
      attemptActivatedAtEpochMs,
      lineage,
    };
    const validation = validateOrigin(candidate);
    return validation.valid ? validation.draft : null;
  } catch {
    return null;
  }
}

function createOrigin(input: {
  attempt: SupportedAttempt;
  lineage: unknown;
  sourceAppointmentDraft: unknown;
}) {
  const source = createSourceFingerprint(input.sourceAppointmentDraft);
  const paymentAttemptHasFrozenAuthority =
    input.attempt.attemptKind !== "payment" ||
    (Object.prototype.hasOwnProperty.call(
      input.attempt.attempt,
      "presentationSnapshot",
    ) &&
      input.attempt.attempt.presentationSnapshot !== undefined &&
      Object.prototype.hasOwnProperty.call(
        input.attempt.attempt,
        "staffChoiceEvidence",
      ));
  const sourceTransitionIsExact =
    source &&
    (input.attempt.attemptKind !== "payment" ||
      isSupportedSpecialistAppointmentWorkingDraftTransition({
        sourceDraft: source.draft,
        workingDraft: getWorkingAppointmentDraft(input.attempt),
        staffChoiceEvidence: input.attempt.attempt.staffChoiceEvidence ?? null,
        timeProfessionalMutationEvidence:
          input.attempt.attempt.reviewDraft.intakeDraft.schedulingDraft
            .timeProfessionalMutationEvidence,
        evaluatedAtEpochMs:
          input.attempt.attempt.checkoutContext.quote.issuedAtEpochMs,
      }));
  if (
    !source ||
    !paymentAttemptHasFrozenAuthority ||
    !sourceTransitionIsExact ||
    source.draft.draftId !== input.attempt.attempt.draftId ||
    source.draft.locationId !==
      getWorkingAppointmentDraft(input.attempt).locationId ||
    (input.attempt.attemptKind === "payment-free-submission" &&
      JSON.stringify(source.draft) !==
        JSON.stringify(input.attempt.attempt.sourceAppointmentDraft))
  ) {
    return null;
  }
  return createOriginFromBinding({
    attempt: input.attempt,
    lineage: input.lineage,
    sourceAppointmentDraftFingerprint: source.fingerprint,
  });
}

function validateOrigin(value: unknown):
  | {
      valid: true;
      draft: SpecialistLocationGatewayAppointmentAttemptOriginV1;
    }
  | {
      valid: false;
      reason: "invalid-specialist-location-gateway-appointment-attempt-origin";
    } {
  const invalid = () => ({
    valid: false as const,
    reason:
      "invalid-specialist-location-gateway-appointment-attempt-origin" as const,
  });
  try {
    if (
      !isRecord(value) ||
      !hasExactKeys(value, [
        "schemaVersion",
        "kind",
        "draftId",
        "ownerKey",
        "attemptKind",
        "activationRevision",
        "attemptFingerprint",
        "customerAccountId",
        "destinationKind",
        "destinationDraftId",
        "destinationDraftFingerprint",
        "sourceAppointmentDraftFingerprint",
        "idempotencyKey",
        "attemptId",
        "attemptActivatedAtEpochMs",
        "lineage",
      ]) ||
      value.schemaVersion !==
        SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_ATTEMPT_ORIGIN_SCHEMA_VERSION ||
      value.kind !== "specialist-location-gateway-appointment-attempt-origin" ||
      !isIdentifier(value.draftId) ||
      !isOwnerKey(value.ownerKey) ||
      (value.attemptKind !== "payment" &&
        value.attemptKind !== "payment-free-submission") ||
      value.activationRevision !==
        SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION ||
      !isAttemptFingerprint(value.attemptFingerprint) ||
      !isIdentifier(value.customerAccountId) ||
      value.destinationKind !== "individual" ||
      value.destinationDraftId !== value.draftId ||
      !isAppointmentDraftFingerprint(value.sourceAppointmentDraftFingerprint) ||
      !isIdentifier(value.idempotencyKey) ||
      (value.attemptId !== null && !isIdentifier(value.attemptId)) ||
      (value.attemptKind === "payment" && value.attemptId === null) ||
      (value.attemptKind === "payment-free-submission" &&
        value.attemptId !== null) ||
      !isEpoch(value.attemptActivatedAtEpochMs)
    ) {
      return invalid();
    }
    const destinationDraftFingerprint =
      createSpecialistLocationGatewayAppointmentDestinationFingerprintFromStepOneFingerprint(
        value.sourceAppointmentDraftFingerprint,
      );
    const lineage = cloneCommittedLineage(value.lineage);
    if (
      !destinationDraftFingerprint ||
      !lineage ||
      destinationDraftFingerprint !== value.destinationDraftFingerprint ||
      lineage.customerAccountId !== value.customerAccountId ||
      lineage.destinationKind !== "individual" ||
      lineage.destinationDraftId !== value.destinationDraftId ||
      lineage.destinationDraftFingerprint !== destinationDraftFingerprint
    ) {
      return invalid();
    }
    const expectedOwnerKey = createSpecialistLocationGatewayOwnerKey({
      stage: "appointment-attempt-origin-v1",
      fields: [
        value.attemptKind,
        value.draftId,
        value.attemptFingerprint,
        value.customerAccountId,
        destinationDraftFingerprint,
        value.sourceAppointmentDraftFingerprint,
        value.idempotencyKey,
        value.attemptId,
        value.attemptActivatedAtEpochMs,
        lineage.ownerKey,
        value.activationRevision,
      ],
    });
    if (expectedOwnerKey !== value.ownerKey) return invalid();
    return {
      valid: true,
      draft: {
        schemaVersion:
          SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_ATTEMPT_ORIGIN_SCHEMA_VERSION,
        kind: "specialist-location-gateway-appointment-attempt-origin",
        draftId: value.draftId,
        ownerKey: value.ownerKey,
        attemptKind: value.attemptKind,
        activationRevision: value.activationRevision,
        attemptFingerprint: value.attemptFingerprint,
        customerAccountId: value.customerAccountId,
        destinationKind: "individual",
        destinationDraftId: value.destinationDraftId,
        destinationDraftFingerprint,
        sourceAppointmentDraftFingerprint:
          value.sourceAppointmentDraftFingerprint,
        idempotencyKey: value.idempotencyKey,
        attemptId: value.attemptId,
        attemptActivatedAtEpochMs: value.attemptActivatedAtEpochMs,
        lineage,
      },
    };
  } catch {
    return invalid();
  }
}

const appointmentAttemptOriginStore =
  createVersionedSessionDraftStore<SpecialistLocationGatewayAppointmentAttemptOriginV1>(
    {
      storageKey:
        SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_ATTEMPT_ORIGIN_STORAGE_KEY,
      storeKind: "specialist-location-gateway-appointment-attempt-origins",
      maxEntries: 8,
      maxAgeMs:
        SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_ATTEMPT_ORIGIN_MAX_AGE_MS,
      maxSerializedCharacters: 1_500_000,
      validate: validateOrigin,
    },
  );

function sameSnapshot(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function ambiguousStatus(status: string) {
  return (
    status === "unreadable" ||
    status === "persistence-failed" ||
    status === "readback-mismatch"
  );
}

function inspectStableCustomerOwnedSnapshot<T>(
  inspect: () => T,
): { status: "stable"; value: T } | { status: "changed" } {
  const proof = captureCustomerOwnedSessionStorageMutationProof();
  let value: T;
  try {
    value = inspect();
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "changed" };
  }
  return customerOwnedSessionStorageMutationProofIsCurrent(proof)
    ? { status: "stable", value }
    : { status: "changed" };
}

function inspectStableTerminalAttemptAggregate(draftId: string) {
  return inspectStableCustomerOwnedSnapshot(() =>
    inspectTerminalAttemptAggregate(draftId),
  );
}

interface TerminalAttemptAggregateStrictMutationAuthority {
  readonly mutationProof: CustomerOwnedSessionStorageMutationProof;
  readonly mutationIsCurrent: () => boolean;
}

function captureTerminalAttemptAggregateStrictMutationAuthority(
  lease: SpecialistLocationGatewayTerminalWriteLease,
): TerminalAttemptAggregateStrictMutationAuthority {
  const mutationProof = captureCustomerOwnedSessionStorageMutationProof();
  return {
    mutationProof,
    mutationIsCurrent: () =>
      specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease) &&
      customerOwnedSessionStorageMutationProofIsCurrent(mutationProof),
  };
}

function strictMutationAuthorityIsCurrent(
  authority: TerminalAttemptAggregateStrictMutationAuthority,
) {
  try {
    return authority.mutationIsCurrent() === true;
  } catch {
    return false;
  }
}

function inspectPersistedAttempt(
  value: SupportedAttempt,
):
  | { status: "absent" }
  | { status: "unreadable" }
  | { status: "present"; attempt: SupportedAttempt; exact: boolean } {
  try {
    const inspected =
      value.attemptKind === "payment"
        ? inspectAppointmentPaymentAttemptResumeDraftPersisted(
            value.attempt.draftId,
          )
        : inspectAppointmentSubmissionAttemptDraftPersisted(
            value.attempt.draftId,
          );
    if (inspected.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "unreadable" };
    }
    if (inspected.status === "absent") return { status: "absent" };
    const persistedAttempt = validateSupportedAttempt(
      value.attemptKind,
      inspected.draft,
    );
    if (!persistedAttempt) {
      quarantineCustomerOwnedSessionStorage();
      return { status: "unreadable" };
    }
    return {
      status: "present",
      attempt: persistedAttempt,
      exact: sameSnapshot(persistedAttempt.attempt, value.attempt),
    };
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" };
  }
}

function inspectOrigin(draftId: string) {
  try {
    const inspected =
      appointmentAttemptOriginStore.inspectPersistedDraft(draftId);
    if (inspected.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
    }
    return inspected;
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" as const };
  }
}

function createOriginStrictPersisted(
  origin: SpecialistLocationGatewayAppointmentAttemptOriginV1,
) {
  let created: ReturnType<
    typeof appointmentAttemptOriginStore.createDraftStrictPersisted
  >;
  try {
    created = appointmentAttemptOriginStore.createDraftStrictPersisted(origin);
  } catch {
    return { status: "unreadable" as const };
  }
  if (created.status !== "readback-mismatch") return created;
  const current = inspectOrigin(origin.draftId);
  return current.status === "present" || current.status === "absent"
    ? ({ status: "superseded" } as const)
    : created;
}

function removeOriginStrictPersisted(
  origin: SpecialistLocationGatewayAppointmentAttemptOriginV1,
) {
  let removed: ReturnType<
    typeof appointmentAttemptOriginStore.removeDraftStrictPersisted
  >;
  try {
    removed = appointmentAttemptOriginStore.removeDraftStrictPersisted(origin);
  } catch {
    return { status: "unreadable" as const };
  }
  if (removed.status !== "readback-mismatch") return removed;
  const current = inspectOrigin(origin.draftId);
  return current.status === "present" || current.status === "absent"
    ? ({ status: "superseded" } as const)
    : removed;
}

function exactOriginForAttempt(
  attempt: SupportedAttempt,
  candidate: SpecialistLocationGatewayAppointmentAttemptOriginV1,
) {
  if (candidate.attemptKind !== attempt.attemptKind) return null;
  const expected = createOriginFromBinding({
    attempt,
    lineage: candidate.lineage,
    sourceAppointmentDraftFingerprint:
      candidate.sourceAppointmentDraftFingerprint,
  });
  return expected && sameSnapshot(expected, candidate) ? expected : null;
}

function validateTerminalAttemptAggregate(value: unknown):
  | {
      valid: true;
      draft: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1;
    }
  | { valid: false; reason: string } {
  try {
    const aggregateKeys = [
      "schemaVersion",
      "kind",
      "draftId",
      "customerAccountId",
      "attemptKind",
      "originRequirement",
      "revision",
      "attempt",
      "origin",
      "resultsEntryProof",
      "packageSessionProof",
      "membershipSessionProof",
      "membershipSessionDefinitiveRejection",
      "membershipSessionReservationReleaseCommit",
      "packageSessionDefinitiveRejection",
      "packageSessionAtomicTerminalCommit",
      "packageSessionAtomicReleaseCommit",
      "receiptCommit",
      "confirmationAcknowledgedAtEpochMs",
    ] as const;
    // Optional extensions preserve legacy ordinary/Results bytes. Each
    // entitlement draft discriminator independently requires its own proof;
    // stripping the field cannot downgrade it into ordinary booking.
    const requiredAggregateKeys = aggregateKeys.filter(
      (key) => key !== "resultsEntryProof" && key !== "packageSessionProof" && key !== "membershipSessionProof" && key !== "membershipSessionDefinitiveRejection" && key !== "membershipSessionReservationReleaseCommit" && key !== "packageSessionDefinitiveRejection" && key !== "packageSessionAtomicTerminalCommit" && key !== "packageSessionAtomicReleaseCommit",
    );
    if (
      !isRecord(value) ||
      !hasExactKeys(value, [
        ...requiredAggregateKeys,
        ...(Object.prototype.hasOwnProperty.call(value, "resultsEntryProof")
          ? ["resultsEntryProof"]
          : []),
        ...(Object.prototype.hasOwnProperty.call(value, "packageSessionProof")
          ? ["packageSessionProof"]
          : []),
        ...(Object.prototype.hasOwnProperty.call(value, "membershipSessionProof")
          ? ["membershipSessionProof"]
          : []),
        ...(Object.prototype.hasOwnProperty.call(value, "packageSessionDefinitiveRejection")
          ? ["packageSessionDefinitiveRejection"] : []),
        ...(Object.prototype.hasOwnProperty.call(value, "packageSessionAtomicTerminalCommit") ? ["packageSessionAtomicTerminalCommit"] : []),
        ...(Object.prototype.hasOwnProperty.call(value, "packageSessionAtomicReleaseCommit") ? ["packageSessionAtomicReleaseCommit"] : []),
        ...(Object.prototype.hasOwnProperty.call(value, "membershipSessionDefinitiveRejection")
          ? ["membershipSessionDefinitiveRejection"] : []),
        ...(Object.prototype.hasOwnProperty.call(value, "membershipSessionReservationReleaseCommit")
          ? ["membershipSessionReservationReleaseCommit"] : []),
      ]) ||
      value.schemaVersion !== 1 ||
      value.kind !==
        "specialist-location-gateway-appointment-terminal-attempt-aggregate" ||
      !isIdentifier(value.draftId) ||
      !isIdentifier(value.customerAccountId) ||
      (value.attemptKind !== "payment" &&
        value.attemptKind !== "payment-free-submission") ||
      (value.originRequirement !== "gateway" &&
        value.originRequirement !== "legacy-non-gateway") ||
      !Number.isSafeInteger(value.revision) ||
      (value.revision as number) < 1 ||
      (value.confirmationAcknowledgedAtEpochMs !== null &&
        !isEpoch(value.confirmationAcknowledgedAtEpochMs))
    ) {
      return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }
    const attempt = validateSupportedAttempt(value.attemptKind, value.attempt);
    if (
      !attempt ||
      attempt.attempt.draftId !== value.draftId ||
      attempt.attempt.customerAccountId !== value.customerAccountId
    ) {
      return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }
    const attemptIsSpecialistOrigin =
      getWorkingAppointmentDraft(attempt).entryPoint.kind ===
      "specialist-services";
    if (
      (value.originRequirement === "gateway" && !attemptIsSpecialistOrigin) ||
      (value.originRequirement === "legacy-non-gateway" &&
        attemptIsSpecialistOrigin)
    ) {
      return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }
    let origin: SpecialistLocationGatewayAppointmentAttemptOriginV1 | null =
      null;
    if (value.originRequirement === "gateway") {
      if (value.origin !== null) {
        const validation = validateOrigin(value.origin);
        if (!validation.valid) {
          return {
            valid: false,
            reason: "invalid-terminal-attempt-aggregate",
          };
        }
        origin = exactOriginForAttempt(attempt, validation.draft);
        if (!origin) {
          return {
            valid: false,
            reason: "invalid-terminal-attempt-aggregate",
          };
        }
      }
    } else if (value.origin !== null) {
      return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }

    const hasResultsEntryProofField = Object.prototype.hasOwnProperty.call(
      value,
      "resultsEntryProof",
    );
    const resultsEntryProofValidation =
      validateAppointmentResultsEntryProofForAttempt({
        attempt,
        value: hasResultsEntryProofField ? value.resultsEntryProof : null,
      });
    if (
      !resultsEntryProofValidation.valid ||
      (origin !== null &&
        resultsEntryProofValidation.proof !== null &&
        origin.sourceAppointmentDraftFingerprint !==
          resultsEntryProofValidation.proof.preference
            .appointmentDraftFingerprint)
    ) {
      return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }
    const resultsEntryProof = resultsEntryProofValidation.proof;
    const hasPackageSessionProofField = Object.prototype.hasOwnProperty.call(
      value,
      "packageSessionProof",
    );
    const packageValidation = validatePackageSessionProofForAttempt({
      attempt,
      value: hasPackageSessionProofField ? value.packageSessionProof : null,
    });
    if (
      !packageValidation.valid ||
      (packageValidation.proof !== null &&
        (origin !== null ||
          value.originRequirement !== "legacy-non-gateway" ||
          resultsEntryProof !== null))
    ) {
      return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }
    const packageSessionProof = packageValidation.proof;
    const hasMembershipSessionProofField = Object.prototype.hasOwnProperty.call(value, "membershipSessionProof");
    const membershipValidation = validateMembershipSessionProofForAttempt({ attempt,
      value: hasMembershipSessionProofField ? value.membershipSessionProof : null });
    if (!membershipValidation.valid || (membershipValidation.proof !== null &&
      (origin !== null || value.originRequirement !== "legacy-non-gateway" || resultsEntryProof !== null || packageSessionProof !== null))) {
      return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }
    const membershipSessionProof = membershipValidation.proof;
    let packageSessionDefinitiveRejection: PackageSessionSubmissionDefinitiveRejectionV1 | null = null;
    if (Object.prototype.hasOwnProperty.call(value, "packageSessionDefinitiveRejection")) {
      if (!packageSessionProof || packageSessionProof.proof.schemaVersion !== 1 || attempt.attemptKind !== "payment" ||
        attempt.attempt.state.kind !== "terminal" ||
        attempt.attempt.state.failure.source !== "submission" ||
        attempt.attempt.state.priorState.kind !== "authorized-pending-booking" || value.receiptCommit !== null) {
        return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
      }
      packageSessionDefinitiveRejection = validatePackageSessionSubmissionDefinitiveRejection({
        value: value.packageSessionDefinitiveRejection, attempt: attempt.attempt,
        proof: packageSessionProof.proof, expectedReason: attempt.attempt.state.failure.reason,
      });
      if (!packageSessionDefinitiveRejection) return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }

    let membershipSessionDefinitiveRejection: MembershipSessionSubmissionDefinitiveRejection | null = null;
    if (Object.prototype.hasOwnProperty.call(value, "membershipSessionDefinitiveRejection")) {
      const failureReason = attempt.attemptKind === "payment"
        ? (attempt.attempt.state.kind === "terminal" && attempt.attempt.state.failure.source === "submission" &&
          attempt.attempt.state.priorState.kind === "authorized-pending-booking" ? attempt.attempt.state.failure.reason : null)
        : (attempt.attempt.state.kind === "terminal" ? attempt.attempt.state.reason : null);
      if (!membershipSessionProof || packageSessionDefinitiveRejection || failureReason === null || value.receiptCommit !== null) {
        return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
      }
      membershipSessionDefinitiveRejection = validateMembershipSessionSubmissionDefinitiveRejection({
        value: value.membershipSessionDefinitiveRejection, attempt: attempt.attempt,
        proof: membershipSessionProof.proof, expectedReason: failureReason,
      });
      if (!membershipSessionDefinitiveRejection) return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }
    const hasMembershipSessionReservationReleaseCommitField = Object.prototype.hasOwnProperty.call(
      value,
      "membershipSessionReservationReleaseCommit",
    );
    const membershipSessionReservationReleaseCommit =
      hasMembershipSessionReservationReleaseCommitField
        ? validateMembershipSessionReservationReleaseCommit(
            value.membershipSessionReservationReleaseCommit,
            membershipSessionProof,
            membershipSessionDefinitiveRejection,
          )
        : null;
    if (
      hasMembershipSessionReservationReleaseCommitField &&
      !membershipSessionReservationReleaseCommit
    ) {
      return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }
    let receiptCommit: SpecialistLocationGatewayAppointmentTerminalAttemptReceiptCommitV1 | null =
      null;
    if (value.receiptCommit !== null) {
      if (
        !isRecord(value.receiptCommit) ||
        !hasExactKeys(value.receiptCommit, ["receipt", "terminalLineage"]) ||
        attemptStateIsTerminal(attempt)
      ) {
        return {
          valid: false,
          reason: "invalid-terminal-attempt-aggregate",
        };
      }
      const receipt = cloneAppointmentBookingReceipt(
        value.receiptCommit.receipt,
      );
      const expectedLineage = origin?.lineage ?? null;
      if (
        !receipt ||
        (value.originRequirement === "gateway" && !origin) ||
        !sameSnapshot(value.receiptCommit.terminalLineage, expectedLineage) ||
        (resultsEntryProof !== null &&
          receipt.sourceAppointmentDraftFingerprint !==
            resultsEntryProof.preference.appointmentDraftFingerprint) ||
        (packageSessionProof !== null &&
          receipt.sourceAppointmentDraftFingerprint !==
            packageSessionProof.proof.sourceAppointmentDraftFingerprint) ||
        (membershipSessionProof !== null &&
          receipt.sourceAppointmentDraftFingerprint !==
            createAppointmentStepOneDraftFingerprint(getMembershipSessionBookingTerminalProofAuthority(membershipSessionProof.proof).sourceAppointmentDraft)) ||
        !canonicalReceiptMatchesAttempt({
          attempt,
          origin,
          originRequirement: value.originRequirement,
          receipt,
        })
      ) {
        return {
          valid: false,
          reason: "invalid-terminal-attempt-aggregate",
        };
      }
      receiptCommit = {
        receipt,
        terminalLineage: expectedLineage,
      };
    } else if (value.confirmationAcknowledgedAtEpochMs !== null) {
      return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }
    const atomicFailureReason = attempt.attemptKind === "payment"
      ? attempt.attempt.state.kind === "terminal" && attempt.attempt.state.failure.source === "submission" && attempt.attempt.state.priorState.kind === "authorized-pending-booking"
        ? attempt.attempt.state.failure.reason : null
      : attempt.attempt.state.kind === "terminal" ? attempt.attempt.state.reason : null;
    const atomicCommit = Object.hasOwn(value, "packageSessionAtomicTerminalCommit") && packageSessionProof?.proof.schemaVersion === 3
      ? validatePackageSessionAtomicTerminalCommitV3({ value: value.packageSessionAtomicTerminalCommit, attempt: attempt.attempt,
          proof: packageSessionProof.proof, receipt: receiptCommit?.receipt ?? null, rejectionReason: atomicFailureReason }) : null;
    if ((Object.hasOwn(value, "packageSessionAtomicTerminalCommit") && !atomicCommit) ||
      (packageSessionProof?.proof.schemaVersion === 3 && receiptCommit !== null && atomicCommit?.disposition !== "consumed")) {
      return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    }
    const atomicRelease = Object.hasOwn(value, "packageSessionAtomicReleaseCommit") && atomicCommit && packageSessionProof?.proof.schemaVersion === 3
      ? validatePackageSessionAtomicReleaseCommitV3({ value: value.packageSessionAtomicReleaseCommit, proof: packageSessionProof.proof, rejection: atomicCommit }) : null;
    if (Object.hasOwn(value, "packageSessionAtomicReleaseCommit") && !atomicRelease) return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
    return {
      valid: true,
      draft: {
        schemaVersion: 1,
        kind: "specialist-location-gateway-appointment-terminal-attempt-aggregate",
        draftId: attempt.attempt.draftId,
        customerAccountId: attempt.attempt.customerAccountId,
        attemptKind: attempt.attemptKind,
        originRequirement: value.originRequirement,
        revision: value.revision as number,
        attempt: attempt.attempt,
        origin,
        ...(hasResultsEntryProofField ? { resultsEntryProof } : {}),
        ...(hasPackageSessionProofField ? { packageSessionProof } : {}),
        ...(hasMembershipSessionProofField ? { membershipSessionProof } : {}),
        ...(packageSessionDefinitiveRejection ? { packageSessionDefinitiveRejection } : {}),
        ...(atomicCommit ? { packageSessionAtomicTerminalCommit: atomicCommit } : {}),
        ...(atomicRelease ? { packageSessionAtomicReleaseCommit: atomicRelease } : {}),
        ...(membershipSessionDefinitiveRejection ? { membershipSessionDefinitiveRejection } : {}),
        ...(membershipSessionReservationReleaseCommit
          ? { membershipSessionReservationReleaseCommit }
          : {}),
        receiptCommit,
        confirmationAcknowledgedAtEpochMs:
          value.confirmationAcknowledgedAtEpochMs as number | null,
      },
    };
  } catch {
    return { valid: false, reason: "invalid-terminal-attempt-aggregate" };
  }
}

const terminalAttemptAggregateStore =
  createVersionedSessionDraftStore<SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1>(
    {
      storageKey:
        SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY,
      storeKind:
        "specialist-location-gateway-appointment-terminal-attempt-aggregates",
      maxEntries: 8,
      maxAgeMs:
        SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_ATTEMPT_ORIGIN_MAX_AGE_MS,
      maxSerializedCharacters: 3_500_000,
      validate: validateTerminalAttemptAggregate,
      // PSBC provider obligations end through explicit retirement, accepted
      // receipt consumption or acknowledged-capacity/auth cleanup, not TTL.
      // The exact allocated owned Gift tender has a durable provider hold too.
      // Other Ordinary/Results consumers keep their existing TTL behavior.
      retainExpiredDraft: (draft) => draft.packageSessionProof != null || draft.membershipSessionProof != null ||
        draft.attemptKind === "payment" && appointmentPaymentUsesOwnedGiftCardTenderV1(draft.attempt as AppointmentPaymentAttemptResumeDraftV1),
    },
  );

function inspectTerminalAttemptAggregate(draftId: string) {
  const mutationProof = captureCustomerOwnedSessionStorageMutationProof();
  try {
    const inspected =
      terminalAttemptAggregateStore.inspectPersistedDraft(draftId);
    if (!customerOwnedSessionStorageMutationProofIsCurrent(mutationProof)) {
      return { status: "unreadable" as const };
    }
    if (inspected.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
    }
    return inspected;
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" as const };
  }
}

function createTerminalAttemptAggregateStrict(
  aggregate: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1,
  authority: TerminalAttemptAggregateStrictMutationAuthority,
) {
  let created: ReturnType<
    typeof terminalAttemptAggregateStore.createDraftStrictPersisted
  >;
  try {
    if (!strictMutationAuthorityIsCurrent(authority)) {
      return { status: "superseded" as const };
    }
    created = terminalAttemptAggregateStore.createDraftStrictPersisted(
      aggregate,
      authority,
    );
  } catch {
    return { status: "unreadable" as const };
  }
  if (!strictMutationAuthorityIsCurrent(authority)) {
    return { status: "superseded" as const };
  }
  if (created.status !== "readback-mismatch") return created;
  const current = inspectTerminalAttemptAggregate(aggregate.draftId);
  return current.status === "present" || current.status === "absent"
    ? ({ status: "superseded" } as const)
    : created;
}

function replaceTerminalAttemptAggregateStrict(
  expected: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1,
  next: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1,
  authority: TerminalAttemptAggregateStrictMutationAuthority,
) {
  let replaced: ReturnType<
    typeof terminalAttemptAggregateStore.replaceDraftStrictPersisted
  >;
  try {
    if (!strictMutationAuthorityIsCurrent(authority)) {
      return { status: "superseded" as const };
    }
    replaced = terminalAttemptAggregateStore.replaceDraftStrictPersisted(
      expected,
      next,
      authority,
    );
  } catch {
    return { status: "unreadable" as const };
  }
  if (!strictMutationAuthorityIsCurrent(authority)) {
    return { status: "superseded" as const };
  }
  if (replaced.status !== "readback-mismatch") return replaced;
  const current = inspectTerminalAttemptAggregate(expected.draftId);
  return current.status === "present" || current.status === "absent"
    ? ({ status: "superseded" } as const)
    : replaced;
}

function removeTerminalAttemptAggregateStrict(
  aggregate: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1,
  authority: TerminalAttemptAggregateStrictMutationAuthority,
) {
  let removed: ReturnType<
    typeof terminalAttemptAggregateStore.removeDraftStrictPersisted
  >;
  try {
    if (!strictMutationAuthorityIsCurrent(authority)) {
      return { status: "superseded" as const };
    }
    removed = terminalAttemptAggregateStore.removeDraftStrictPersisted(
      aggregate,
      authority,
    );
  } catch {
    return { status: "unreadable" as const };
  }
  if (!strictMutationAuthorityIsCurrent(authority)) {
    return { status: "superseded" as const };
  }
  if (removed.status !== "readback-mismatch") return removed;
  const current = inspectTerminalAttemptAggregate(aggregate.draftId);
  return current.status === "present" || current.status === "absent"
    ? ({ status: "superseded" } as const)
    : removed;
}

/**
 * Reclaims only a Receipt aggregate that Confirmation has durably
 * acknowledged. Unacknowledged accepted outcomes remain recovery authority
 * and are never displaced merely because the bounded envelope is full.
 */
function reclaimAcknowledgedTerminalAttemptAggregateCapacity(
  expectedCustomerAccountId: string,
  lease: SpecialistLocationGatewayTerminalWriteLease,
) {
  try {
    const authority =
      captureTerminalAttemptAggregateStrictMutationAuthority(lease);
    const inspected = terminalAttemptAggregateStore.inspectPersistedDrafts();
    if (inspected.status !== "present") return false;
    const candidates = inspected.drafts
      .filter(
        (draft) =>
          draft.customerAccountId === expectedCustomerAccountId &&
          draft.receiptCommit !== null &&
          draft.confirmationAcknowledgedAtEpochMs !== null,
      )
      .sort((left, right) => {
        const epochDelta =
          left.confirmationAcknowledgedAtEpochMs! -
          right.confirmationAcknowledgedAtEpochMs!;
        return epochDelta !== 0
          ? epochDelta
          : left.draftId.localeCompare(right.draftId);
      });
    let membershipReservationsAreCurrent = () => true;
    const candidate = candidates.find((draft) => {
      if (draft.membershipSessionProof) {
        const evaluatedAtEpochMs = Date.now();
        if (!isEpoch(evaluatedAtEpochMs)) throw new Error("Invalid capacity clock.");
        const storage = window.sessionStorage;
        const publications = [
          MEMBERSHIP_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY,
          MEMBERSHIP_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY_V2,
        ].map((storageKey) => ({
          storageKey,
          value: readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey }),
        }));
        if (publications.some(({ value }) => value.status !== "ready")) {
          throw new Error("Unreadable Membership reservation during capacity recovery.");
        }
        const reservation = readMembershipSessionBookingReservationForAppointmentStrict({
          appointmentDraftId: draft.draftId,
          customerAccountId: draft.customerAccountId,
          evaluatedAtEpochMs,
        });
        const reservationV2 = readMembershipSessionBookingReservationForAppointmentStrictV2({
          appointmentDraftId: draft.draftId,
          customerAccountId: draft.customerAccountId,
          evaluatedAtEpochMs,
        });
        if ((reservation.status !== "ready" && reservation.status !== "absent") ||
          (reservationV2.status !== "ready" && reservationV2.status !== "absent") ||
          (reservation.status === "ready" && reservationV2.status === "ready")) {
          throw new Error("Unverifiable Membership reservation during capacity recovery.");
        }
        const record = reservation.status === "ready" ? reservation.record
          : reservationV2.status === "ready" ? reservationV2.record : null;
        if (record && !membershipReservationMatchesTerminalAggregate(record, draft)) {
          throw new Error("Mismatched Membership reservation during capacity recovery.");
        }
        if (record && (record.schemaVersion === 2
          ? membershipSessionBookingReservationRecordRequiresProtectionV2(record)
          : membershipSessionBookingReservationRecordRequiresProtection(record))) return false;
        // Receipt acknowledgement does not settle a provider hold. Preserve
        // both versioned frontiers through the final aggregate CAS, including
        // absence: a newly published hold must withdraw this candidate.
        membershipReservationsAreCurrent = () => {
          try {
            return !isCustomerOwnedSessionStorageQuarantined() && window.sessionStorage === storage &&
              publications.every(({ storageKey, value }) => {
                const current = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
                return value.status === "ready" && current.status === "ready" && current.rawValue === value.rawValue;
              }) && !isCustomerOwnedSessionStorageQuarantined();
          } catch { return false; }
        };
        return membershipReservationsAreCurrent();
      }
      if (!draft.packageSessionProof) return true;
      const evaluatedAtEpochMs = Date.now();
      if (!isEpoch(evaluatedAtEpochMs)) throw new Error("Invalid capacity clock.");
      const atomicAbsent = capturePackageSessionAtomicReservationAbsenceGuardV2(draft.draftId, evaluatedAtEpochMs);
      if (!atomicAbsent) return false;
      membershipReservationsAreCurrent = atomicAbsent;
      const reservation = readPackageSessionBookingReservationForAppointmentStrict({
        appointmentDraftId: draft.draftId,
        customerAccountId: draft.customerAccountId,
        evaluatedAtEpochMs,
      });
      if (reservation.status === "absent") return true;
      if (reservation.status !== "ready" ||
        !packageReservationMatchesTerminalAggregate(reservation.record, draft)) {
        throw new Error("Unverifiable Package reservation during capacity recovery.");
      }
      return !packageSessionBookingReservationRecordRequiresProtection(reservation.record);
    });
    if (!candidate) return false;
    const reclaimAuthority = {
      ...authority,
      mutationIsCurrent: () => membershipReservationsAreCurrent() &&
        authority.mutationIsCurrent() && membershipReservationsAreCurrent(),
    };
    if (!strictMutationAuthorityIsCurrent(reclaimAuthority)) return false;
    const removed = removeTerminalAttemptAggregateStrict(
      candidate,
      reclaimAuthority,
    );
    if (removed.status === "removed" || removed.status === "absent") {
      return true;
    }
    if (removed.status === "superseded") {
      return false;
    }
    if (ambiguousStatus(removed.status)) {
      quarantineCustomerOwnedSessionStorage();
    }
    return false;
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return false;
  }
}

function createInitialTerminalAttemptAggregate(input: {
  attempt: SupportedAttempt;
  originRequirement: OriginRequirement;
  resultsEntryProof: SpecialistLocationGatewayAppointmentResultsEntryProofV1 | null;
  packageSessionProof: AppointmentPackageSessionTerminalProofV1 | null;
  membershipSessionProof: AppointmentMembershipSessionTerminalProofV1 | null;
}): SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1 {
  return {
    schemaVersion: 1,
    kind: "specialist-location-gateway-appointment-terminal-attempt-aggregate",
    draftId: input.attempt.attempt.draftId,
    customerAccountId: input.attempt.attempt.customerAccountId,
    attemptKind: input.attempt.attemptKind,
    originRequirement: input.originRequirement,
    revision: 1,
    attempt: input.attempt.attempt,
    origin: null,
    resultsEntryProof: input.resultsEntryProof,
    ...(input.packageSessionProof !== null
      ? { packageSessionProof: input.packageSessionProof }
      : {}),
    ...(input.membershipSessionProof !== null
      ? { membershipSessionProof: input.membershipSessionProof }
      : {}),
    receiptCommit: null,
    confirmationAcknowledgedAtEpochMs: null,
  };
}

function terminalAttemptAggregateMatchesAttempt(
  aggregate: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1,
  attempt: SupportedAttempt,
  originRequirement?: OriginRequirement,
) {
  return (
    aggregate.attemptKind === attempt.attemptKind &&
    aggregate.customerAccountId === attempt.attempt.customerAccountId &&
    (!originRequirement || aggregate.originRequirement === originRequirement) &&
    sameSnapshot(aggregate.attempt, attempt.attempt)
  );
}

function readOriginStrict(input: {
  attemptKind: SpecialistLocationGatewayAppointmentAttemptKind;
  attempt: unknown;
  expectedCustomerAccountId: string;
}): ReadSpecialistLocationGatewayAppointmentAttemptOriginResult {
  const attempt = validateSupportedAttempt(input.attemptKind, input.attempt);
  if (
    !attempt ||
    attempt.attempt.customerAccountId !== input.expectedCustomerAccountId ||
    getWorkingAppointmentDraft(attempt).entryPoint.kind !==
      "specialist-services"
  ) {
    return { status: "owner-mismatch" };
  }
  const stableAggregate = inspectStableTerminalAttemptAggregate(
    attempt.attempt.draftId,
  );
  if (stableAggregate.status !== "stable") {
    return { status: "owner-mismatch" };
  }
  const aggregate = stableAggregate.value;
  if (aggregate.status !== "present") return { status: aggregate.status };
  if (!terminalAttemptAggregateMatchesAttempt(aggregate.draft, attempt)) {
    return { status: "owner-mismatch" };
  }
  if (
    aggregate.draft.originRequirement !== "gateway" ||
    !aggregate.draft.origin
  ) {
    return { status: "absent" };
  }
  const exact = exactOriginForAttempt(attempt, aggregate.draft.origin);
  if (!exact) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "owner-mismatch" };
  }
  return { status: "ready", origin: exact, lineage: exact.lineage };
}

export function readSpecialistLocationGatewayAppointmentPaymentAttemptOriginStrict(input: {
  attempt: unknown;
  expectedCustomerAccountId: string;
}): ReadSpecialistLocationGatewayAppointmentAttemptOriginResult {
  let attempt: unknown;
  let expectedCustomerAccountId: string;
  try {
    attempt = input.attempt;
    expectedCustomerAccountId = input.expectedCustomerAccountId;
  } catch {
    return { status: "owner-mismatch" };
  }
  return readOriginStrict({
    attemptKind: "payment",
    attempt,
    expectedCustomerAccountId,
  });
}

export function readSpecialistLocationGatewayAppointmentSubmissionAttemptOriginStrict(input: {
  attempt: unknown;
  expectedCustomerAccountId: string;
}): ReadSpecialistLocationGatewayAppointmentAttemptOriginResult {
  let attempt: unknown;
  let expectedCustomerAccountId: string;
  try {
    attempt = input.attempt;
    expectedCustomerAccountId = input.expectedCustomerAccountId;
  } catch {
    return { status: "owner-mismatch" };
  }
  return readOriginStrict({
    attemptKind: "payment-free-submission",
    attempt,
    expectedCustomerAccountId,
  });
}

function appointmentAttemptReceiptCommitOwnerIsCurrentStrict(input: {
  attemptKind: SpecialistLocationGatewayAppointmentAttemptKind;
  attempt: unknown;
  receipt: unknown;
  expectedCustomerAccountId: string;
  originRequirement: "gateway" | "legacy-non-gateway";
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}) {
  let attemptValue: unknown;
  let expectedCustomerAccountId: string;
  let receiptValue: unknown;
  let originRequirement: "gateway" | "legacy-non-gateway";
  let isCurrentOwner: () => boolean;
  let isPureCurrentOwner: () => boolean;
  try {
    attemptValue = input.attempt;
    receiptValue = input.receipt;
    expectedCustomerAccountId = input.expectedCustomerAccountId;
    originRequirement = input.originRequirement;
    isCurrentOwner = input.isCurrentOwner;
    isPureCurrentOwner = input.isPureCurrentOwner;
  } catch {
    return false;
  }
  if (
    (originRequirement !== "gateway" &&
      originRequirement !== "legacy-non-gateway") ||
    typeof isCurrentOwner !== "function" ||
    typeof isPureCurrentOwner !== "function"
  ) {
    return false;
  }
  const attempt = validateSupportedAttempt(input.attemptKind, attemptValue);
  const receipt = cloneAppointmentBookingReceipt(receiptValue);
  if (
    !attempt ||
    !receipt ||
    attempt.attempt.customerAccountId !== expectedCustomerAccountId
  ) {
    return false;
  }
  const ownsCurrent = (callback: () => boolean) => {
    try {
      return callback() === true;
    } catch {
      return false;
    }
  };
  const exactCanonicalReceiptIsPersisted = () => {
    const stableAggregate = inspectStableTerminalAttemptAggregate(
      attempt.attempt.draftId,
    );
    if (stableAggregate.status !== "stable") return false;
    const aggregate = stableAggregate.value;
    return (
      aggregate.status === "present" &&
      terminalAttemptAggregateMatchesAttempt(
        aggregate.draft,
        attempt,
        originRequirement,
      ) &&
      aggregate.draft.receiptCommit !== null &&
      sameSnapshot(aggregate.draft.receiptCommit.receipt, receipt)
    );
  };

  // Receipt persistence may call this owner repeatedly between its own
  // projection writes. Every invocation closes auth/current -> exact
  // canonical receipt -> auth/current -> exact canonical receipt -> pure
  // generation, so compatibility bytes never become authority themselves.
  if (!ownsCurrent(isCurrentOwner)) return false;
  if (!exactCanonicalReceiptIsPersisted()) return false;
  if (!ownsCurrent(isCurrentOwner)) return false;
  if (!exactCanonicalReceiptIsPersisted()) return false;
  const finalMutationProof = captureCustomerOwnedSessionStorageMutationProof();
  return (
    ownsCurrent(isPureCurrentOwner) &&
    customerOwnedSessionStorageMutationProofIsCurrent(finalMutationProof)
  );
}

export function specialistLocationGatewayAppointmentPaymentReceiptCommitOwnerIsCurrentStrict(input: {
  attempt: unknown;
  receipt: unknown;
  expectedCustomerAccountId: string;
  originRequirement: "gateway" | "legacy-non-gateway";
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}) {
  return appointmentAttemptReceiptCommitOwnerIsCurrentStrict({
    ...input,
    attemptKind: "payment",
  });
}

export function specialistLocationGatewayAppointmentSubmissionReceiptCommitOwnerIsCurrentStrict(input: {
  attempt: unknown;
  receipt: unknown;
  expectedCustomerAccountId: string;
  originRequirement: "gateway" | "legacy-non-gateway";
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}) {
  return appointmentAttemptReceiptCommitOwnerIsCurrentStrict({
    ...input,
    attemptKind: "payment-free-submission",
  });
}

export function inspectSpecialistLocationGatewayAppointmentAttemptOriginPersistedStrict(
  draftId: string,
):
  | { status: "absent" | "unreadable" }
  | {
      status: "present";
      origin: SpecialistLocationGatewayAppointmentAttemptOriginV1;
    } {
  const inspected = inspectOrigin(draftId);
  return inspected.status === "present"
    ? { status: "present", origin: inspected.draft }
    : { status: inspected.status };
}

function createPersistedAttemptStrict(attempt: SupportedAttempt) {
  try {
    return attempt.attemptKind === "payment"
      ? createAppointmentPaymentAttemptResumeDraftStrictPersisted(
          attempt.attempt,
        )
      : createAppointmentSubmissionAttemptDraftStrictPersisted(attempt.attempt);
  } catch {
    return { status: "unreadable" as const };
  }
}

function removePersistedAttemptStrict(attempt: SupportedAttempt) {
  try {
    return attempt.attemptKind === "payment"
      ? removeAppointmentPaymentAttemptResumeDraftStrictPersisted(
          attempt.attempt,
        )
      : removeAppointmentSubmissionAttemptDraftStrictPersisted(attempt.attempt);
  } catch {
    return { status: "unreadable" as const };
  }
}

function createAttemptUnderOwnerLease(input: {
  attempt: SupportedAttempt;
  originRequirement: OriginRequirement;
  resultsEntryProofInput: unknown;
  packageSessionProofInput: unknown;
  membershipSessionProofInput: unknown;
  isCurrentOwner: () => boolean;
}):
  | { status: "created" | "absorbed-terminal"; attempt: SupportedAttempt }
  | { status: "refused" } {
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify([
      "appointment-attempt-create",
      input.attempt.attemptKind,
      input.attempt.attempt.draftId,
    ]),
    input.isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    const initialStateIsValid =
      input.attempt.attemptKind === "payment"
        ? input.attempt.attempt.state.kind === "starting"
        : input.attempt.attempt.state.kind === "pending-reconciliation";
    const attemptIsSpecialistOrigin =
      getWorkingAppointmentDraft(input.attempt).entryPoint.kind ===
      "specialist-services";
    const originRequirementMatchesImmutableOrigin =
      input.originRequirement === "gateway"
        ? attemptIsSpecialistOrigin
        : !attemptIsSpecialistOrigin;
    const initialAggregate = inspectTerminalAttemptAggregate(
      input.attempt.attempt.draftId,
    );
    const initiallyAbsent = initialAggregate.status === "absent";
    const initialOwnerIsCurrent =
      initiallyAbsent &&
      specialistLocationGatewayTerminalWriteIsCurrent(
        lease,
        input.isCurrentOwner,
      );
    const postOwnerAggregate = initialOwnerIsCurrent
      ? inspectStableTerminalAttemptAggregate(input.attempt.attempt.draftId)
      : ({ status: "changed" } as const);
    if (
      !initialStateIsValid ||
      !originRequirementMatchesImmutableOrigin ||
      !initiallyAbsent ||
      !initialOwnerIsCurrent ||
      postOwnerAggregate.status !== "stable" ||
      postOwnerAggregate.value.status !== "absent" ||
      !specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
    ) {
      return { status: "refused" };
    }
    const captureCreationCandidate = () => {
      const creationAuthority =
        captureTerminalAttemptAggregateStrictMutationAuthority(lease);
      const capturedResultsEntryProof =
        captureCurrentAppointmentResultsEntryProofForAttempt({
          attempt: input.attempt,
          captureInput: input.resultsEntryProofInput,
        });
      const capturedPackageSessionProof =
        captureCurrentPackageSessionProofForAttempt({
          attempt: input.attempt,
          captureInput: input.packageSessionProofInput,
        });
      const capturedMembershipSessionProof = captureCurrentMembershipSessionProofForAttempt({
        attempt: input.attempt, captureInput: input.membershipSessionProofInput,
      });
      if (
        capturedResultsEntryProof.status !== "ready" ||
        capturedPackageSessionProof.status !== "ready" ||
        capturedMembershipSessionProof.status !== "ready" ||
        !strictMutationAuthorityIsCurrent(creationAuthority)
      ) {
        return null;
      }
      return {
        aggregate: createInitialTerminalAttemptAggregate({
          attempt: input.attempt,
          originRequirement: input.originRequirement,
          resultsEntryProof: capturedResultsEntryProof.proof,
          packageSessionProof: capturedPackageSessionProof.proof,
          membershipSessionProof: capturedMembershipSessionProof.proof,
        }),
        authority: {
          ...creationAuthority,
          mutationIsCurrent: () =>
            capturedPackageSessionProof.authenticatedOwnerIsCurrent() &&
            capturedMembershipSessionProof.authenticatedOwnerIsCurrent() &&
            strictMutationAuthorityIsCurrent(creationAuthority),
        },
      };
    };
    let creationCandidate = captureCreationCandidate();
    if (!creationCandidate) return { status: "refused" };
    let aggregateCreation = createTerminalAttemptAggregateStrict(
      creationCandidate.aggregate,
      creationCandidate.authority,
    );
    if (
      aggregateCreation.status === "capacity" &&
      specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
    ) {
      const reclaimed = reclaimAcknowledgedTerminalAttemptAggregateCapacity(
        input.attempt.attempt.customerAccountId,
        lease,
      );
      if (reclaimed) {
        // Reclaim performs external storage work and may synchronously start
        // auth teardown. Re-open the exact-absence frontier, then close it
        // with only pure lease and mutation-generation proofs before the
        // second create. An advanced teardown epoch can never resurrect the
        // stale owner after freeing capacity.
        const postReclaimAggregate = inspectStableTerminalAttemptAggregate(
          input.attempt.attempt.draftId,
        );
        if (
          postReclaimAggregate.status === "stable" &&
          postReclaimAggregate.value.status === "absent" &&
          specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
        ) {
          creationCandidate = captureCreationCandidate();
          if (creationCandidate) {
            aggregateCreation = createTerminalAttemptAggregateStrict(
              creationCandidate.aggregate,
              creationCandidate.authority,
            );
          }
        }
      }
    }
    if (aggregateCreation.status !== "created") {
      if (ambiguousStatus(aggregateCreation.status)) {
        quarantineCustomerOwnedSessionStorage();
      }
      return { status: "refused" };
    }
    const compensateCreatedAggregate = () => {
      if (!specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)) {
        return;
      }
      const current = inspectTerminalAttemptAggregate(
        input.attempt.attempt.draftId,
      );
      if (
        current.status === "present" &&
        sameSnapshot(current.draft, aggregateCreation.draft)
      ) {
        const compensationAuthority =
          captureTerminalAttemptAggregateStrictMutationAuthority(lease);
        const removed = removeTerminalAttemptAggregateStrict(
          current.draft,
          compensationAuthority,
        );
        if (
          removed.status !== "removed" &&
          removed.status !== "absent" &&
          removed.status !== "superseded"
        ) {
          quarantineCustomerOwnedSessionStorage();
        }
      }
    };
    // The versioned attempt store is a compatibility projection. A failed or
    // superseded projection cannot roll back the canonical attempt once its
    // same-key CAS has committed; current callers can continue from the
    // returned canonical value and reload recovery reads this aggregate.
    try {
      createPersistedAttemptStrict(input.attempt);
    } catch {
      // Canonical authority has already committed. A throwing compatibility
      // store cannot strand or roll back the fresh aggregate.
    }
    const exactReadback = inspectTerminalAttemptAggregate(
      input.attempt.attempt.draftId,
    );
    const finalOwnerIsCurrent =
      exactReadback.status === "present" &&
      sameSnapshot(exactReadback.draft, aggregateCreation.draft) &&
      specialistLocationGatewayTerminalWriteIsCurrent(
        lease,
        input.isCurrentOwner,
      );
    const currentPackageProof = finalOwnerIsCurrent
      ? captureCurrentPackageSessionProofForAttempt({
          attempt: input.attempt,
          captureInput: input.packageSessionProofInput,
        })
      : null;
    const capturedPackageProof =
      aggregateCreation.draft.packageSessionProof ?? null;
    const currentMembershipProof = finalOwnerIsCurrent ? captureCurrentMembershipSessionProofForAttempt({
      attempt: input.attempt, captureInput: input.membershipSessionProofInput,
    }) : null;
    const capturedMembershipProof = aggregateCreation.draft.membershipSessionProof ?? null;
    const membershipCaptureStillCurrent = currentMembershipProof?.status === "ready" &&
      (capturedMembershipProof === null ? currentMembershipProof.proof === null : currentMembershipProof.proof !== null &&
        capturedMembershipProof.expectedLocale === currentMembershipProof.proof.expectedLocale &&
        sameSnapshot(capturedMembershipProof.proof.reservationHold, currentMembershipProof.proof.proof.reservationHold));
    const packageCaptureStillCurrent =
      currentPackageProof?.status === "ready" &&
      (capturedPackageProof === null
        ? currentPackageProof.proof === null
        : currentPackageProof.proof !== null &&
          capturedPackageProof.expectedLocale ===
            currentPackageProof.proof.expectedLocale &&
          capturedPackageProof.proof.schemaVersion === currentPackageProof.proof.proof.schemaVersion &&
          (capturedPackageProof.proof.schemaVersion !== 1
            ? currentPackageProof.proof.proof.schemaVersion !== 1 &&
              sameSnapshot(capturedPackageProof.proof.selection, currentPackageProof.proof.proof.selection) &&
              sameSnapshot(capturedPackageProof.proof.reviewDraft, currentPackageProof.proof.proof.reviewDraft)
            : currentPackageProof.proof.proof.schemaVersion === 1 &&
              sameSnapshot(capturedPackageProof.proof.preference, currentPackageProof.proof.proof.preference) &&
              sameSnapshot(capturedPackageProof.proof.sourceAppointmentDraft, currentPackageProof.proof.proof.sourceAppointmentDraft)) &&
          sameSnapshot(
            capturedPackageProof.proof.reservationHold,
            currentPackageProof.proof.proof.reservationHold,
          ));
    const finalAggregate =
      finalOwnerIsCurrent && packageCaptureStillCurrent && membershipCaptureStillCurrent
        ? inspectStableTerminalAttemptAggregate(input.attempt.attempt.draftId)
        : ({ status: "changed" } as const);
    if (
      finalOwnerIsCurrent &&
      packageCaptureStillCurrent &&
      membershipCaptureStillCurrent &&
      finalAggregate.status === "stable" &&
      finalAggregate.value.status === "present" &&
      sameSnapshot(finalAggregate.value.draft, aggregateCreation.draft) &&
      currentPackageProof?.status === "ready" &&
      currentPackageProof.authenticatedOwnerIsCurrent() &&
      currentMembershipProof?.status === "ready" &&
      currentMembershipProof.authenticatedOwnerIsCurrent() &&
      specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
    ) {
      const persisted = validateSupportedAttempt(
        input.attempt.attemptKind,
        finalAggregate.value.draft.attempt,
      );
      if (!persisted) return { status: "refused" };
      return {
        status: "created",
        attempt: persisted,
      };
    }
    if (specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)) {
      const projection = inspectPersistedAttempt(input.attempt);
      if (projection.status === "present" && projection.exact) {
        const removed = removePersistedAttemptStrict(projection.attempt);
        if (
          removed.status !== "removed" &&
          removed.status !== "absent" &&
          removed.status !== "superseded"
        ) {
          quarantineCustomerOwnedSessionStorage();
        }
      }
      compensateCreatedAggregate();
    }
    return { status: "refused" };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export function createSpecialistLocationGatewayAppointmentPaymentAttemptStrict(input: {
  attempt: unknown;
  originRequirement: OriginRequirement;
  resultsEntryProof?: SpecialistLocationGatewayAppointmentResultsEntryProofCaptureInput | null;
  packageSessionProof?: AppointmentPackageSessionTerminalProofCaptureInput | null;
  membershipSessionProof?: AppointmentMembershipSessionTerminalProofCaptureInput | null;
  isCurrentOwner: () => boolean;
}): CreateSpecialistLocationGatewayAppointmentAttemptResult<AppointmentPaymentAttemptResumeDraftV1> {
  let attemptValue: unknown;
  let originRequirement: OriginRequirement;
  let resultsEntryProofInput: unknown;
  let packageSessionProofInput: unknown;
  let membershipSessionProofInput: unknown;
  let isCurrentOwner: () => boolean;
  try {
    attemptValue = input.attempt;
    originRequirement = input.originRequirement;
    resultsEntryProofInput = input.resultsEntryProof ?? null;
    packageSessionProofInput = input.packageSessionProof ?? null;
    membershipSessionProofInput = input.membershipSessionProof ?? null;
    isCurrentOwner = input.isCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (
    (originRequirement !== "gateway" &&
      originRequirement !== "legacy-non-gateway") ||
    typeof isCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const attempt = validateSupportedAttempt("payment", attemptValue);
  if (!attempt || attempt.attemptKind !== "payment" ||
    !bindRewardAppointmentAttemptV1({ attemptKind: "payment", attempt: attempt.attempt, isCurrentOwner, serviceBenefitsCreationEvidenceV1: {
      schemaVersion: 1, kind: "service-benefits-attempt-creation", originRequirement, resultsEntryProof: resultsEntryProofInput,
      packageSessionProof: packageSessionProofInput, membershipSessionProof: membershipSessionProofInput,
    } })) {
    return { status: "refused" };
  }
  const result = createAttemptUnderOwnerLease({
    attempt,
    originRequirement,
    resultsEntryProofInput,
    packageSessionProofInput,
    membershipSessionProofInput,
    isCurrentOwner,
  });
  if (result.status === "refused") return result;
  const persisted = validateAppointmentPaymentAttemptResumeDraft(
    result.attempt.attempt,
  );
  return persisted.valid
    ? { status: result.status, attempt: persisted.draft }
    : { status: "refused" };
}

export function createSpecialistLocationGatewayAppointmentSubmissionAttemptStrict(input: {
  attempt: unknown;
  originRequirement: OriginRequirement;
  resultsEntryProof?: SpecialistLocationGatewayAppointmentResultsEntryProofCaptureInput | null;
  packageSessionProof?: AppointmentPackageSessionTerminalProofCaptureInput | null;
  membershipSessionProof?: AppointmentMembershipSessionTerminalProofCaptureInput | null;
  isCurrentOwner: () => boolean;
}): CreateSpecialistLocationGatewayAppointmentAttemptResult<AppointmentSubmissionAttemptDraftV1> {
  let attemptValue: unknown;
  let originRequirement: OriginRequirement;
  let resultsEntryProofInput: unknown;
  let packageSessionProofInput: unknown;
  let membershipSessionProofInput: unknown;
  let isCurrentOwner: () => boolean;
  try {
    attemptValue = input.attempt;
    originRequirement = input.originRequirement;
    resultsEntryProofInput = input.resultsEntryProof ?? null;
    packageSessionProofInput = input.packageSessionProof ?? null;
    membershipSessionProofInput = input.membershipSessionProof ?? null;
    isCurrentOwner = input.isCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (
    (originRequirement !== "gateway" &&
      originRequirement !== "legacy-non-gateway") ||
    typeof isCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const attempt = validateSupportedAttempt(
    "payment-free-submission",
    attemptValue,
  );
  if (!attempt || attempt.attemptKind !== "payment-free-submission" ||
    !bindRewardAppointmentAttemptV1({ attemptKind: "payment-free-submission", attempt: attempt.attempt, isCurrentOwner, serviceBenefitsCreationEvidenceV1: {
      schemaVersion: 1, kind: "service-benefits-attempt-creation", originRequirement, resultsEntryProof: resultsEntryProofInput,
      packageSessionProof: packageSessionProofInput, membershipSessionProof: membershipSessionProofInput,
    } })) {
    return { status: "refused" };
  }
  const result = createAttemptUnderOwnerLease({
    attempt,
    originRequirement,
    resultsEntryProofInput,
    packageSessionProofInput,
    membershipSessionProofInput,
    isCurrentOwner,
  });
  if (result.status === "refused") return result;
  const persisted = validateAppointmentSubmissionAttemptDraft(
    result.attempt.attempt,
  );
  return persisted.valid
    ? { status: result.status, attempt: persisted.draft }
    : { status: "refused" };
}

function transitionPersistedAttemptStrict(input: {
  expected: SupportedAttempt;
  next: SupportedAttempt;
}) {
  try {
    return input.expected.attemptKind === "payment" &&
      input.next.attemptKind === "payment"
      ? replaceAppointmentPaymentAttemptResumeDraftStrictPersisted(
          input.expected.attempt,
          input.next.attempt,
        )
      : input.expected.attemptKind === "payment-free-submission" &&
          input.next.attemptKind === "payment-free-submission"
        ? replaceAppointmentSubmissionAttemptDraftStrictPersisted(
            input.expected.attempt,
            input.next.attempt,
          )
        : ({ status: "invalid" } as const);
  } catch {
    return { status: "unreadable" as const };
  }
}

function attemptStateIsTerminal(attempt: SupportedAttempt) {
  return attempt.attempt.state.kind === "terminal";
}

/**
 * Owner-leased expected-old -> next transition for every Appointment attempt
 * that can lead to a provider call or publish a provider result. Gateway
 * recovery requires its exact marker; only the first fresh transition may
 * prove strict marker absence before the invocation transaction creates it.
 */
function transitionAppointmentAttemptWithCapturedPackageRejectionStrict(input: {
  attemptKind: SpecialistLocationGatewayAppointmentAttemptKind;
  expectedAttempt: unknown;
  nextAttempt: unknown;
  originRequirement: OriginRequirement;
  allowGatewayOriginAbsence: boolean;
  isCurrentOwner: () => boolean;
}, definitivePackageRejection?: PackageSessionSubmissionDefinitiveRejectionV1,
  definitiveMembershipRejection?: MembershipSessionSubmissionDefinitiveRejection,
  atomicTerminalCommit?: PackageSessionAtomicTerminalCommitV3): TransitionSpecialistLocationGatewayAppointmentAttemptResult {
  let attemptKind: SpecialistLocationGatewayAppointmentAttemptKind;
  let expectedAttemptValue: unknown;
  let nextAttemptValue: unknown;
  let originRequirement: OriginRequirement;
  let allowGatewayOriginAbsence: boolean;
  let isCurrentOwner: () => boolean;
  try {
    attemptKind = input.attemptKind;
    expectedAttemptValue = input.expectedAttempt;
    nextAttemptValue = input.nextAttempt;
    originRequirement = input.originRequirement;
    allowGatewayOriginAbsence = input.allowGatewayOriginAbsence;
    isCurrentOwner = input.isCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (
    (attemptKind !== "payment" && attemptKind !== "payment-free-submission") ||
    (originRequirement !== "gateway" &&
      originRequirement !== "legacy-non-gateway") ||
    typeof allowGatewayOriginAbsence !== "boolean" ||
    typeof isCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const expected = validateSupportedAttempt(attemptKind, expectedAttemptValue);
  const next = validateSupportedAttempt(attemptKind, nextAttemptValue);
  if (
    !expected ||
    !next ||
    expected.attemptKind !== next.attemptKind ||
    expected.attempt.draftId !== next.attempt.draftId ||
    createAttemptFingerprint(expected) !== createAttemptFingerprint(next)
  ) {
    return { status: "refused" };
  }

  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify([
      "appointment-attempt-transition",
      attemptKind,
      expected.attempt.draftId,
    ]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    const initialAggregate = inspectTerminalAttemptAggregate(
      expected.attempt.draftId,
    );
    const initialSnapshotsAreExact =
      initialAggregate.status === "present" &&
      terminalAttemptAggregateMatchesAttempt(
        initialAggregate.draft,
        expected,
        originRequirement,
      ) &&
      initialAggregate.draft.receiptCommit === null &&
      (originRequirement === "legacy-non-gateway"
        ? initialAggregate.draft.origin === null
        : allowGatewayOriginAbsence || initialAggregate.draft.origin !== null);
    const initialOwnerIsCurrent =
      initialSnapshotsAreExact &&
      specialistLocationGatewayTerminalWriteIsCurrent(lease, isCurrentOwner);
    const postOwnerSnapshot = initialOwnerIsCurrent
      ? inspectStableTerminalAttemptAggregate(expected.attempt.draftId)
      : ({ status: "changed" } as const);
    const postOwnerSnapshotsAreExact =
      postOwnerSnapshot.status === "stable" &&
      postOwnerSnapshot.value.status === "present" &&
      initialAggregate.status === "present" &&
      sameSnapshot(postOwnerSnapshot.value.draft, initialAggregate.draft);
    if (
      !initialSnapshotsAreExact ||
      !initialOwnerIsCurrent ||
      !postOwnerSnapshotsAreExact ||
      !specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
    ) {
      return { status: "refused" };
    }

    if (initialAggregate.status !== "present") {
      return { status: "refused" };
    }
    const legalTransition =
      expected.attemptKind === "payment" && next.attemptKind === "payment"
        ? isLegalAppointmentPaymentAttemptStateTransition(
            expected.attempt,
            next.attempt,
          )
        : expected.attemptKind === "payment-free-submission" &&
            next.attemptKind === "payment-free-submission"
          ? isLegalAppointmentSubmissionStateTransition(
              expected.attempt,
              next.attempt,
            )
          : false;
    if (!legalTransition) return { status: "refused" };
    const nextAggregate: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1 =
      {
        ...initialAggregate.draft,
        revision: initialAggregate.draft.revision + 1,
        attempt: next.attempt,
        ...(definitivePackageRejection ? { packageSessionDefinitiveRejection: definitivePackageRejection } : {}),
        ...(definitiveMembershipRejection ? { membershipSessionDefinitiveRejection: definitiveMembershipRejection } : {}),
        ...(atomicTerminalCommit ? { packageSessionAtomicTerminalCommit: atomicTerminalCommit } : {}),
      };
    const canonicalTransition = replaceTerminalAttemptAggregateStrict(
      initialAggregate.draft,
      nextAggregate,
      captureTerminalAttemptAggregateStrictMutationAuthority(lease),
    );
    if (canonicalTransition.status !== "replaced") {
      const concurrent = inspectTerminalAttemptAggregate(
        expected.attempt.draftId,
      );
      if (
        concurrent.status === "present" &&
        concurrent.draft.receiptCommit === null &&
        concurrent.draft.attemptKind === expected.attemptKind
      ) {
        const concurrentAttempt = validateSupportedAttempt(
          expected.attemptKind,
          concurrent.draft.attempt,
        );
        if (
          concurrentAttempt &&
          createAttemptFingerprint(concurrentAttempt) ===
            createAttemptFingerprint(expected) &&
          attemptStateIsTerminal(concurrentAttempt)
        ) {
          return {
            status: "absorbed-terminal",
            attempt: concurrentAttempt.attempt,
          };
        }
      }
      if (ambiguousStatus(canonicalTransition.status)) {
        quarantineCustomerOwnedSessionStorage();
      }
      return { status: "refused" };
    }

    // Compatibility projection only. It can make old UI conservative, but it
    // never grants authority and can never override the canonical aggregate.
    try {
      transitionPersistedAttemptStrict({ expected, next });
    } catch {
      // Canonical transition remains the only recovery/provider authority.
    }

    const persistedSnapshot = inspectTerminalAttemptAggregate(
      expected.attempt.draftId,
    );
    const persistedIsExact =
      persistedSnapshot.status === "present" &&
      sameSnapshot(persistedSnapshot.draft, canonicalTransition.draft);
    const ownerIsStillCurrent =
      persistedIsExact &&
      specialistLocationGatewayTerminalWriteIsCurrent(lease, isCurrentOwner);
    const finalSnapshot = ownerIsStillCurrent
      ? inspectStableTerminalAttemptAggregate(expected.attempt.draftId)
      : ({ status: "changed" } as const);
    const postOwnerLeaseIsCurrent =
      specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease);
    if (
      ownerIsStillCurrent &&
      finalSnapshot.status === "stable" &&
      finalSnapshot.value.status === "present" &&
      sameSnapshot(finalSnapshot.value.draft, canonicalTransition.draft) &&
      postOwnerLeaseIsCurrent
    ) {
      const persisted = validateSupportedAttempt(
        attemptKind,
        finalSnapshot.value.draft.attempt,
      );
      if (!persisted) return { status: "refused" };
      if (attemptStateIsTerminal(persisted)) {
        return {
          status: "absorbed-terminal",
          attempt: persisted.attempt,
        };
      }
      return { status: "transitioned", attempt: persisted.attempt };
    }

    // A completed canonical CAS is durable provider/reconciliation evidence.
    // Owner drift withdraws only this caller's publication; rolling the
    // aggregate back could turn an unknown or terminal outcome into a state
    // that permits a duplicate provider call. Recovery adopts the committed
    // next state under the current owner.
    return { status: "refused" };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export function transitionSpecialistLocationGatewayAppointmentAttemptStrict(
  input: Parameters<typeof transitionAppointmentAttemptWithCapturedPackageRejectionStrict>[0],
) {
  return transitionAppointmentAttemptWithCapturedPackageRejectionStrict(input);
}

export function transitionSpecialistLocationGatewayAppointmentPaymentAttemptStrict(input: {
  expectedAttempt: unknown;
  nextAttempt: unknown;
  originRequirement: OriginRequirement;
  allowGatewayOriginAbsence: boolean;
  isCurrentOwner: () => boolean;
}):
  | {
      status: "transitioned" | "absorbed-terminal";
      attempt: AppointmentPaymentAttemptResumeDraftV1;
    }
  | { status: "refused" } {
  const result = transitionSpecialistLocationGatewayAppointmentAttemptStrict({
    ...input,
    attemptKind: "payment",
  });
  if (result.status === "refused") return result;
  const validation = validateAppointmentPaymentAttemptResumeDraft(
    result.attempt,
  );
  return validation.valid
    ? { status: result.status, attempt: validation.draft }
    : { status: "refused" };
}

export function transitionSpecialistLocationGatewayAppointmentSubmissionAttemptStrict(input: {
  expectedAttempt: unknown;
  nextAttempt: unknown;
  originRequirement: OriginRequirement;
  allowGatewayOriginAbsence: boolean;
  isCurrentOwner: () => boolean;
}):
  | {
      status: "transitioned" | "absorbed-terminal";
      attempt: AppointmentSubmissionAttemptDraftV1;
    }
  | { status: "refused" } {
  const result = transitionSpecialistLocationGatewayAppointmentAttemptStrict({
    ...input,
    attemptKind: "payment-free-submission",
  });
  if (result.status === "refused") return result;
  const validation = validateAppointmentSubmissionAttemptDraft(result.attempt);
  return validation.valid
    ? { status: result.status, attempt: validation.draft }
    : { status: "refused" };
}

const packageAuthorizedSubmissionInvocations = new WeakMap<object, {
  attempt: AppointmentPaymentAttemptResumeDraftV1;
  proof: { readonly expectedLocale: string; readonly proof: PackageSessionBookingTerminalProofV1 };
  response: Promise<unknown>;
}>();
const membershipSubmissionInvocations = new WeakMap<object, {
  attempt: SupportedAttempt;
  proof: AppointmentMembershipSessionTerminalProofV1;
  response: Promise<unknown>;
}>();
const packageAtomicSubmissionInvocations = new WeakMap<object, {
  attempt: SupportedAttempt; proof: AppointmentPackageSessionTerminalProofV1; response: Promise<unknown>;
}>();

/** One canonical transaction installs receipt + consume evidence, or the
 * absorbing rejection + no-booking evidence. Only its actual invocation may
 * add that provider authority; interrupted persistence reuses the same key. */
export async function commitPackageSessionAtomicSubmissionOutcomeFromInvocationStrictV3(input: {
  invocation: object; expectedAttempt: AppointmentPaymentAttemptResumeDraftV1 | AppointmentSubmissionAttemptDraftV1;
  isCurrentOwner: () => boolean;
}) {
  const captured = packageAtomicSubmissionInvocations.get(input.invocation);
  if (!captured || captured.proof.proof.schemaVersion !== 3 || !sameSnapshot(captured.attempt.attempt, input.expectedAttempt)) return { status: "refused" } as const;
  packageAtomicSubmissionInvocations.delete(input.invocation);
  const response = await captured.response;
  if (!isRecord(response)) return { status: "refused" } as const;
  const receipt = response.status === "accepted" ? cloneAppointmentBookingReceipt(response.receipt) : null;
  const reason = response.status === "error" && response.retryable === false && typeof response.reason === "string" ? response.reason : null;
  if ((!receipt && reason === null) || (response.status === "accepted" && !receipt)) return { status: "refused" } as const;
  const commit = validatePackageSessionAtomicSubmissionEvidenceV3({ value: response.packageSessionAtomicEvidence,
    attempt: captured.attempt.attempt, proof: captured.proof.proof, receipt, rejectionReason: reason });
  if (!commit || commit.resolvedAtEpochMs > Date.now()) return { status: "refused" } as const;
  const authenticated = capturePackageSessionAuthenticatedOwnerGuard(captured.attempt.attempt.customerAccountId);
  const terminal = inspectStableTerminalAttemptAggregate(captured.attempt.attempt.draftId);
  if (terminal.status !== "stable" || terminal.value.status !== "present" ||
    !sameSnapshot(terminal.value.draft.attempt, captured.attempt.attempt) || !sameSnapshot(terminal.value.draft.packageSessionProof, captured.proof)) return { status: "refused" } as const;
  const reservationIsCurrent = capturePackageAtomicTerminalReservationGuard(terminal.value.draft);
  const isCurrentOwner = () => { try { return input.isCurrentOwner() === true && authenticated?.() === true && reservationIsCurrent?.() === true; } catch { return false; } };
  if (!isCurrentOwner()) return { status: "refused" } as const;
  if (receipt) {
    const result = commitAppointmentTerminalReceiptStrict({ attempt: captured.attempt, receipt, originRequirement: "legacy-non-gateway",
      isCurrentOwner, isPureCurrentOwner: () => !isCustomerOwnedSessionStorageQuarantined(), atomicTerminalCommit: commit });
    return result.status === "committed" || result.status === "already-committed"
      ? { status: "accepted", receipt: result.receipt, attempt: result.attempt, ownerCurrent: result.ownerCurrent } as const : { status: "refused" } as const;
  }
  if (commit.reason === null) return { status: "refused" } as const;
  const next = captured.attempt.attemptKind === "payment"
    ? markAppointmentPaymentAttemptTerminal({ currentDraft: captured.attempt.attempt, failure: { source: "submission", reason: commit.reason } })
    : markAppointmentSubmissionAttemptTerminal({ attempt: captured.attempt.attempt, reason: commit.reason });
  if (!next) return { status: "refused" } as const;
  const result = transitionAppointmentAttemptWithCapturedPackageRejectionStrict({ attemptKind: captured.attempt.attemptKind, expectedAttempt: captured.attempt.attempt, nextAttempt: next,
    originRequirement: "legacy-non-gateway", allowGatewayOriginAbsence: true, isCurrentOwner }, undefined, undefined, commit);
  return result.status === "transitioned" || result.status === "absorbed-terminal" ? { status: "rejected", attempt: result.attempt } as const : { status: "refused" } as const;
}

function capturePackageAtomicTerminalReservationGuard(aggregate: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1) {
  const proof = aggregate.packageSessionProof?.proof;
  if (proof?.schemaVersion !== 3) return null;
  const now = Date.now();
  const record = readPackageSessionAtomicReservationStrictV2({ draftId: aggregate.draftId, customerAccountId: aggregate.customerAccountId, evaluatedAtEpochMs: now });
  if (record.status !== "ready" || !["held", "held-expired"].includes(record.record.status) ||
    record.record.revision !== proof.reservationRecordRevision || record.record.operation.operationId !== proof.reservationOperationId ||
    !sameSnapshot(record.record.heldSnapshot, proof.reservationHold)) return null;
  return capturePackageSessionAtomicReservationCurrentGuardV2(record.record, now);
}

/** Canonical V3 close authority: immutable accepted consumption or exact
 * definitive rejection. Release is a separately durable provider commit. */
export function capturePackageSessionAtomicTerminalCleanupAuthorityV3(input: { expectedAttempt: unknown; isCurrentOwner: () => boolean }) {
  try {
    const expected = validateSupportedAttempt("payment", input.expectedAttempt) ?? validateSupportedAttempt("payment-free-submission", input.expectedAttempt);
    if (!expected) return null;
    const authenticated = capturePackageSessionAuthenticatedOwnerGuard(expected.attempt.customerAccountId);
    if (!authenticated || !input.isCurrentOwner()) return null;
    const storage = window.sessionStorage;
    const storageKey = SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY;
    const initialRaw = readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey });
    const initial = inspectStableTerminalAttemptAggregate(expected.attempt.draftId);
    if (initialRaw.status !== "ready" || initialRaw.rawValue === null || initial.status !== "stable" || initial.value.status !== "present") return null;
    const aggregate = initial.value.draft;
    if (aggregate.packageSessionProof?.proof.schemaVersion !== 3 || !aggregate.packageSessionAtomicTerminalCommit || !sameSnapshot(aggregate.attempt, expected.attempt)) return null;
    const proof = aggregate.packageSessionProof.proof;
    const terminalCommit = aggregate.packageSessionAtomicTerminalCommit;
    const isCurrent = () => {
      try {
        if (!input.isCurrentOwner() || !authenticated() || isCustomerOwnedSessionStorageQuarantined() || window.sessionStorage !== storage) return false;
        const current = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
        return current.status === "ready" && current.rawValue === initialRaw.rawValue && !isCustomerOwnedSessionStorageQuarantined();
      } catch { return false; }
    };
    const reservationMatches = (record: PackageSessionAtomicReservationRecordV2) => record.draftId === aggregate.draftId && record.customerAccountId === aggregate.customerAccountId &&
      sameSnapshot(record.heldSnapshot, proof.reservationHold) && sameSnapshot(record.operation.selection, proof.selection) && sameSnapshot(record.operation.reviewDraft, proof.reviewDraft);
    const commitRelease = (record: PackageSessionAtomicReservationRecordV2) => {
      if (!isCurrent() || terminalCommit.disposition !== "definitive-not-accepted" || !reservationMatches(record) || record.status !== "released") return false;
      const releaseCommit = createPackageSessionAtomicReleaseCommitV3({ outcome: record.outcome, proof, rejection: terminalCommit });
      if (!releaseCommit || releaseCommit.releasedAtEpochMs > Date.now()) return false;
      if (aggregate.packageSessionAtomicReleaseCommit) return sameSnapshot(aggregate.packageSessionAtomicReleaseCommit, releaseCommit) && isCurrent();
      const recordIsCurrent = capturePackageSessionAtomicReservationCurrentGuardV2(record, Date.now());
      if (!recordIsCurrent || !recordIsCurrent() || !isCurrent()) return false;
      const ownsWrite = () => { try { return input.isCurrentOwner() && authenticated() && recordIsCurrent(); } catch { return false; } };
      const lease = beginSpecialistLocationGatewayTerminalWrite(`package-atomic-release-commit:${aggregate.draftId}`, ownsWrite);
      if (!lease) return false;
      try {
        const validatedNext = validateTerminalAttemptAggregate({ ...aggregate, revision: aggregate.revision + 1, packageSessionAtomicReleaseCommit: releaseCommit });
        if (!validatedNext.valid) return false;
        const next = validatedNext.draft;
        if (!isCurrent() || !recordIsCurrent()) return false;
        const committed = replaceTerminalAttemptAggregateStrict(aggregate, next, captureTerminalAttemptAggregateStrictMutationAuthority(lease));
        const final = inspectStableTerminalAttemptAggregate(aggregate.draftId);
        const finalRaw = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
        const currentOwner = ownsWrite() && specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease);
        const closedRaw = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
        const ready = committed.status === "replaced" && currentOwner && recordIsCurrent() &&
          final.status === "stable" && final.value.status === "present" && sameSnapshot(final.value.draft, next) &&
          finalRaw.status === "ready" && closedRaw.status === "ready" && closedRaw.rawValue === finalRaw.rawValue;
        if (!ready) quarantineCustomerOwnedSessionStorage();
        return ready;
      } finally { completeSpecialistLocationGatewayTerminalWrite(lease); }
    };
    return isCurrent() ? { proof, terminalCommit, releaseCommit: aggregate.packageSessionAtomicReleaseCommit ?? null,
      receipt: aggregate.receiptCommit?.receipt ?? null, isCurrent, reservationMatches, commitRelease } : null;
  } catch { return null; }
}

/** A generic non-retryable error cannot release an entitlement or settle money. */
export async function commitMembershipSessionDefinitiveSubmissionRejectionFromInvocationStrict(input: {
  invocation: object;
  expectedAttempt: AppointmentPaymentAttemptResumeDraftV1 | AppointmentSubmissionAttemptDraftV1;
  isCurrentOwner: () => boolean;
}): Promise<TransitionSpecialistLocationGatewayAppointmentAttemptResult> {
  const captured = membershipSubmissionInvocations.get(input.invocation);
  if (!captured || !sameSnapshot(captured.attempt.attempt, input.expectedAttempt)) return { status: "refused" };
  membershipSubmissionInvocations.delete(input.invocation);
  const response = await captured.response;
  if (!isRecord(response) || response.status !== "error" || response.retryable !== false || typeof response.reason !== "string") {
    return { status: "refused" };
  }
  const evidence = validateMembershipSessionSubmissionDefinitiveRejection({ value: response.membershipSessionDefinitiveRejection,
    attempt: captured.attempt.attempt, proof: captured.proof.proof, expectedReason: response.reason });
  if (!evidence || evidence.rejectedAtEpochMs > Date.now()) return { status: "refused" };
  const authenticated = capturePackageSessionAuthenticatedOwnerGuard(captured.attempt.attempt.customerAccountId);
  const isCurrentOwner = () => {
    try { return input.isCurrentOwner() === true && authenticated?.() === true; } catch { return false; }
  };
  if (!isCurrentOwner()) return { status: "refused" };
  const next = captured.attempt.attemptKind === "payment"
    ? markAppointmentPaymentAttemptTerminal({ currentDraft: captured.attempt.attempt, failure: { source: "submission", reason: evidence.reason } })
    : markAppointmentSubmissionAttemptTerminal({ attempt: captured.attempt.attempt, reason: evidence.reason });
  if (!next) return { status: "refused" };
  return transitionAppointmentAttemptWithCapturedPackageRejectionStrict({ attemptKind: captured.attempt.attemptKind, expectedAttempt: captured.attempt.attempt, nextAttempt: next,
    originRequirement: "legacy-non-gateway", allowGatewayOriginAbsence: true, isCurrentOwner }, undefined, evidence);
}

/** Captures historical no-booking evidence while exact canonical ownership remains current. */
function terminalEnvelopeHasOnlyMembershipReleaseCommitTransition(input: {
  readonly initialRawValue: string;
  readonly currentRawValue: string;
  readonly expectedAggregate: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1;
}) {
  try {
    if (input.expectedAggregate.membershipSessionReservationReleaseCommit) return false;
    const initial = JSON.parse(input.initialRawValue) as unknown;
    const current = JSON.parse(input.currentRawValue) as unknown;
    if (
      !isRecord(initial) ||
      !isRecord(current) ||
      !hasExactKeys(initial, ["storeVersion", "storeKind", "entries"]) ||
      !hasExactKeys(current, ["storeVersion", "storeKind", "entries"]) ||
      initial.storeVersion !== 1 ||
      current.storeVersion !== 1 ||
      initial.storeKind !== "specialist-location-gateway-appointment-terminal-attempt-aggregates" ||
      current.storeKind !== initial.storeKind ||
      !Array.isArray(initial.entries) ||
      !Array.isArray(current.entries) ||
      initial.entries.length !== current.entries.length
    ) return false;
    let transitioned = false;
    for (let index = 0; index < initial.entries.length; index += 1) {
      const beforeRow = initial.entries[index];
      const afterRow = current.entries[index];
      if (!isRecord(beforeRow) || !isRecord(afterRow) ||
        !hasExactKeys(beforeRow, ["draftId", "savedAtEpochMs", "draft"]) ||
        !hasExactKeys(afterRow, ["draftId", "savedAtEpochMs", "draft"]) ||
        beforeRow.draftId !== afterRow.draftId) return false;
      if (beforeRow.draftId !== input.expectedAggregate.draftId) {
        if (!sameSnapshot(beforeRow, afterRow)) return false;
        continue;
      }
      if (transitioned || !sameSnapshot(beforeRow.draft, input.expectedAggregate) ||
        !isEpoch(beforeRow.savedAtEpochMs) || !isEpoch(afterRow.savedAtEpochMs) ||
        afterRow.savedAtEpochMs < beforeRow.savedAtEpochMs) return false;
      const validation = validateTerminalAttemptAggregate(afterRow.draft);
      if (!validation.valid) return false;
      const afterAggregate = validation.draft;
      const afterReleaseCommit =
        afterAggregate.membershipSessionReservationReleaseCommit;
      const expectedAfter = afterReleaseCommit
        ? validateTerminalAttemptAggregate({
            ...input.expectedAggregate,
            revision: input.expectedAggregate.revision + 1,
            membershipSessionReservationReleaseCommit: afterReleaseCommit,
          })
        : null;
      if (
        !afterReleaseCommit ||
        !expectedAfter?.valid ||
        !sameSnapshot(afterAggregate, expectedAfter.draft)
      ) return false;
      transitioned = true;
    }
    return transitioned;
  } catch {
    return false;
  }
}

export function captureMembershipSessionDefinitiveSubmissionFailureCloseAuthority(input: {
  expectedAttempt: AppointmentPaymentAttemptResumeDraftV1 | AppointmentSubmissionAttemptDraftV1;
  isCurrentOwner: () => boolean;
}) {
  try {
    const expected = validateSupportedAttempt("payment", input.expectedAttempt) ?? validateSupportedAttempt("payment-free-submission", input.expectedAttempt);
    if (!expected || !isMembershipSessionAppointmentDraftId(expected.attempt.draftId)) return null;
    const authenticated = capturePackageSessionAuthenticatedOwnerGuard(expected.attempt.customerAccountId);
    if (!authenticated || !input.isCurrentOwner()) return null;
    const storage = window.sessionStorage;
    const storageKey = SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY;
    const initialRaw = readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey });
    if (initialRaw.status !== "ready" || initialRaw.rawValue === null) return null;
    const snapshot = inspectStableTerminalAttemptAggregate(expected.attempt.draftId);
    if (snapshot.status !== "stable" || snapshot.value.status !== "present") return null;
    const aggregate = snapshot.value.draft;
    if (!aggregate.membershipSessionProof || !aggregate.membershipSessionDefinitiveRejection ||
      aggregate.receiptCommit !== null || !sameSnapshot(aggregate.attempt, expected.attempt)) return null;
    const afterValidation = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
    if (afterValidation.status !== "ready" || afterValidation.rawValue !== initialRaw.rawValue) return null;
    const rawMatchesCapturedLineage = (rawValue: string | null) => rawValue === initialRaw.rawValue ||
      (rawValue !== null && initialRaw.rawValue !== null &&
        terminalEnvelopeHasOnlyMembershipReleaseCommitTransition({
          initialRawValue: initialRaw.rawValue,
          currentRawValue: rawValue,
          expectedAggregate: aggregate,
        }));
    const isCurrent = () => {
      try {
        if (!input.isCurrentOwner() || !authenticated() || isCustomerOwnedSessionStorageQuarantined() || window.sessionStorage !== storage) return false;
        const current = readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey });
        if (current.status !== "ready" || !rawMatchesCapturedLineage(current.rawValue) || !authenticated()) return false;
        const final = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
        return final.status === "ready" && final.rawValue === current.rawValue &&
          rawMatchesCapturedLineage(final.rawValue) && !isCustomerOwnedSessionStorageQuarantined();
      } catch { return false; }
    };
    const currentAggregate = () => {
      if (!isCurrent()) return null;
      const current = inspectStableTerminalAttemptAggregate(expected.attempt.draftId);
      if (current.status !== "stable" || current.value.status !== "present" ||
        !sameSnapshot(current.value.draft.attempt, expected.attempt) ||
        !sameSnapshot(current.value.draft.membershipSessionProof, aggregate.membershipSessionProof) ||
        !sameSnapshot(current.value.draft.membershipSessionDefinitiveRejection, aggregate.membershipSessionDefinitiveRejection) ||
        !isCurrent()) return null;
      return current.value.draft;
    };
    const reservationReleaseIsCommitted = () =>
      currentAggregate()?.membershipSessionReservationReleaseCommit !== undefined;
    const commitReservationRelease = (
      candidate: MembershipSessionBookingReservationOperationRecordV1 | MembershipSessionBookingReservationOperationRecordV2,
    ) => {
      const record = candidate?.schemaVersion === 1
        ? validateMembershipSessionBookingReservationOperationRecord(candidate)
        : candidate?.schemaVersion === 2
          ? validateMembershipSessionBookingReservationOperationRecordV2(candidate)
          : null;
      if (!record || record.status !== "released") return false;
      const ownsReleaseCommit = () => {
        try { return input.isCurrentOwner() === true && authenticated() === true; }
        catch { return false; }
      };
      const lease = beginSpecialistLocationGatewayTerminalWrite(
        JSON.stringify(["membership-session-reservation-release-commit", expected.attempt.draftId]),
        ownsReleaseCommit,
      );
      if (!lease) return false;
      try {
        const before = inspectTerminalAttemptAggregate(expected.attempt.draftId);
        if (before.status !== "present" ||
          !sameSnapshot(before.draft.attempt, expected.attempt) ||
          !sameSnapshot(before.draft.membershipSessionProof, aggregate.membershipSessionProof) ||
          !sameSnapshot(before.draft.membershipSessionDefinitiveRejection, aggregate.membershipSessionDefinitiveRejection) ||
          before.draft.receiptCommit !== null) return false;
        const releaseCommit = createMembershipSessionReservationReleaseCommit(record, before.draft);
        if (!releaseCommit) return false;
        if (before.draft.membershipSessionReservationReleaseCommit) {
          return sameSnapshot(before.draft.membershipSessionReservationReleaseCommit, releaseCommit) &&
            isCurrent();
        }
        const recordIsCurrent = record.schemaVersion === 1
          ? captureMembershipSessionBookingReservationRecordCurrentGuard(record, record.updatedAtEpochMs)
          : captureMembershipSessionBookingReservationRecordCurrentGuardV2(record, record.updatedAtEpochMs);
        const mutationOwnerIsCurrent = () =>
          specialistLocationGatewayTerminalWriteIsCurrent(lease, ownsReleaseCommit) &&
          recordIsCurrent?.() === true;
        if (!mutationOwnerIsCurrent()) return false;
        const nextValidation = validateTerminalAttemptAggregate({
          ...before.draft,
          revision: before.draft.revision + 1,
          membershipSessionReservationReleaseCommit: releaseCommit,
        });
        if (!nextValidation.valid) return false;
        const next = nextValidation.draft;
        const baseAuthority = captureTerminalAttemptAggregateStrictMutationAuthority(lease);
        const committed = replaceTerminalAttemptAggregateStrict(before.draft, next, {
          ...baseAuthority,
          mutationIsCurrent: () => baseAuthority.mutationIsCurrent() && mutationOwnerIsCurrent(),
        });
        if (committed.status !== "replaced" || !sameSnapshot(committed.draft, next) ||
          recordIsCurrent?.() !== true || !isCurrent()) return false;
        const final = inspectStableTerminalAttemptAggregate(expected.attempt.draftId);
        return final.status === "stable" && final.value.status === "present" &&
          sameSnapshot(final.value.draft, next) && recordIsCurrent?.() === true && isCurrent();
      } finally {
        completeSpecialistLocationGatewayTerminalWrite(lease);
      }
    };
    return isCurrent() ? {
      proof: aggregate.membershipSessionProof,
      rejection: aggregate.membershipSessionDefinitiveRejection,
      isCurrent,
      reservationMatches: (record: MembershipSessionBookingReservationOperationRecordV1 | MembershipSessionBookingReservationOperationRecordV2) =>
        membershipReservationMatchesTerminalAggregate(record, aggregate),
      reservationReleaseIsCommitted,
      commitReservationRelease,
    } : null;
  } catch { return null; }
}

/** Only the fulfilled response of this exact canonical invocation can add release authority. */
export async function commitPackageSessionDefinitiveSubmissionRejectionFromInvocationStrict(input: {
  invocation: object;
  expectedAttempt: AppointmentPaymentAttemptResumeDraftV1;
  isCurrentOwner: () => boolean;
}): Promise<TransitionSpecialistLocationGatewayAppointmentAttemptResult> {
  const captured = packageAuthorizedSubmissionInvocations.get(input.invocation);
  if (!captured || !sameSnapshot(captured.attempt, input.expectedAttempt)) return { status: "refused" };
  packageAuthorizedSubmissionInvocations.delete(input.invocation);
  const response = await captured.response;
  if (!isRecord(response) || response.status !== "error" || response.retryable !== false || typeof response.reason !== "string") {
    return { status: "refused" };
  }
  const evidence = validatePackageSessionSubmissionDefinitiveRejection({
    value: response.packageSessionDefinitiveRejection, attempt: captured.attempt,
    proof: captured.proof.proof, expectedReason: response.reason,
  });
  if (!evidence || evidence.rejectedAtEpochMs > Date.now()) return { status: "refused" };
  const authenticated = capturePackageSessionAuthenticatedOwnerGuard(captured.attempt.customerAccountId);
  const isCurrentOwner = () => {
    try { return input.isCurrentOwner() === true && authenticated?.() === true; } catch { return false; }
  };
  if (!isCurrentOwner()) return { status: "refused" };
  const next = markAppointmentPaymentAttemptTerminal({
    currentDraft: captured.attempt, failure: { source: "submission", reason: evidence.reason },
  });
  if (!next) return { status: "refused" };
  return transitionAppointmentAttemptWithCapturedPackageRejectionStrict({
    attemptKind: "payment", expectedAttempt: captured.attempt, nextAttempt: next,
    originRequirement: "legacy-non-gateway", allowGatewayOriginAbsence: true, isCurrentOwner,
  }, evidence);
}

/** Historical, exact terminal rejection is the sole exception to canonical hold ownership. */
export function capturePackageSessionDefinitiveSubmissionFailureCloseAuthority(input: {
  expectedAttempt: AppointmentPaymentAttemptResumeDraftV1;
  isCurrentOwner: () => boolean;
}) {
  try {
    const expected = validateAppointmentPaymentAttemptResumeDraft(input.expectedAttempt);
    if (!expected.valid || !isPackageSessionAppointmentDraftId(expected.draft.draftId)) return null;
    const authenticated = capturePackageSessionAuthenticatedOwnerGuard(expected.draft.customerAccountId);
    if (!authenticated || !input.isCurrentOwner()) return null;
    const storage = window.sessionStorage;
    const storageKey = SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY;
    const initialRaw = readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey });
    if (initialRaw.status !== "ready" || initialRaw.rawValue === null) return null;
    const snapshot = inspectStableTerminalAttemptAggregate(expected.draft.draftId);
    if (snapshot.status !== "stable" || snapshot.value.status !== "present") return null;
    const aggregate = snapshot.value.draft;
    if (!aggregate.packageSessionProof || aggregate.packageSessionProof.proof.schemaVersion !== 1 || !aggregate.packageSessionDefinitiveRejection ||
      aggregate.receiptCommit !== null || !sameSnapshot(aggregate.attempt, expected.draft)) return null;
    const afterValidation = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
    if (afterValidation.status !== "ready" || afterValidation.rawValue !== initialRaw.rawValue) return null;
    const isCurrent = () => {
      try {
        if (!input.isCurrentOwner() || !authenticated() || isCustomerOwnedSessionStorageQuarantined() || window.sessionStorage !== storage) return false;
        const current = readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey });
        if (current.status !== "ready" || current.rawValue !== initialRaw.rawValue || !authenticated()) return false;
        const final = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
        return final.status === "ready" && final.rawValue === initialRaw.rawValue && !isCustomerOwnedSessionStorageQuarantined();
      } catch { return false; }
    };
    return isCurrent() ? {
      proof: { ...aggregate.packageSessionProof, proof: aggregate.packageSessionProof.proof },
      isCurrent,
      reservationMatches: (record: PackageSessionBookingReservationOperationRecordV1) =>
        packageReservationMatchesTerminalAggregate(record, aggregate),
    } : null;
  } catch { return null; }
}

function invokeAttemptProvider<Result>(input: {
  attempt: SupportedAttempt;
  originRequirement: OriginRequirement;
  allowOriginCreation: boolean;
  sourceAppointmentDraft: AppointmentBookingDraftV1 | null;
  freshLineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
  requireFreshOrigin: boolean;
  isCurrentOwner: () => boolean;
  invokeProvider: (packageSessionProof?: AppointmentPackageSessionTerminalProofV1,
    membershipSessionProof?: AppointmentMembershipSessionTerminalProofV1) => Result;
}): InvokeSpecialistLocationGatewayAppointmentAttemptResult<Result> {
  const {
    attempt,
    originRequirement,
    allowOriginCreation,
    sourceAppointmentDraft,
    freshLineage,
    requireFreshOrigin,
    isCurrentOwner,
    invokeProvider,
  } = input;
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify([
      "appointment-attempt-origin",
      attempt.attemptKind,
      attempt.attempt.draftId,
    ]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    const entrySnapshot = inspectTerminalAttemptAggregate(
      attempt.attempt.draftId,
    );
    if (
      entrySnapshot.status !== "present" ||
      !terminalAttemptAggregateMatchesAttempt(
        entrySnapshot.draft,
        attempt,
        originRequirement,
      ) ||
      entrySnapshot.draft.receiptCommit !== null ||
      attemptStateIsTerminal(attempt)
    ) {
      return { status: "refused" };
    }

    if (originRequirement === "legacy-non-gateway") {
      if (
        entrySnapshot.draft.origin !== null ||
        !specialistLocationGatewayTerminalWriteIsCurrent(lease, isCurrentOwner)
      ) {
        return { status: "refused" };
      }
      const packageOwnerIsAuthenticated = entrySnapshot.draft.packageSessionProof || entrySnapshot.draft.membershipSessionProof
        ? capturePackageSessionAuthenticatedOwnerGuard(attempt.attempt.customerAccountId)
        : () => true;
      if (!packageOwnerIsAuthenticated) return { status: "refused" };
      const atomicReservationIsAbsent = entrySnapshot.draft.packageSessionProof
        ? entrySnapshot.draft.packageSessionProof.proof.schemaVersion === 3
          ? capturePackageAtomicTerminalReservationGuard(entrySnapshot.draft)
          : capturePackageSessionAtomicReservationAbsenceGuardV2(attempt.attempt.draftId, Date.now())
        : () => true;
      if (!atomicReservationIsAbsent || !packageOwnerIsAuthenticated() || !atomicReservationIsAbsent()) return { status: "refused" };
      const postOwnerSnapshot = inspectStableTerminalAttemptAggregate(
        attempt.attempt.draftId,
      );
      if (
        postOwnerSnapshot.status !== "stable" ||
        postOwnerSnapshot.value.status !== "present" ||
        !sameSnapshot(postOwnerSnapshot.value.draft, entrySnapshot.draft) ||
        !packageOwnerIsAuthenticated() ||
        !atomicReservationIsAbsent() ||
        !specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
      ) {
        return { status: "refused" };
      }
      try {
        const invocation = {
          status: "invoked" as const,
          authority: "legacy-non-gateway" as const,
          lineage: null,
          providerInvocation: {
            status: "returned" as const,
            value: entrySnapshot.draft.membershipSessionProof
              ? invokeProvider(undefined, entrySnapshot.draft.membershipSessionProof)
              : entrySnapshot.draft.packageSessionProof
              ? invokeProvider(entrySnapshot.draft.packageSessionProof)
              : invokeProvider(),
          },
        };
        if (entrySnapshot.draft.packageSessionProof?.proof.schemaVersion === 1 && attempt.attemptKind === "payment" &&
          attempt.attempt.state.kind === "authorized-pending-booking") {
          packageAuthorizedSubmissionInvocations.set(invocation, {
            attempt: attempt.attempt, proof: { ...entrySnapshot.draft.packageSessionProof, proof: entrySnapshot.draft.packageSessionProof.proof },
            response: Promise.resolve(invocation.providerInvocation.value).then(
              capturePackageSessionBookingProviderPlainData,
              () => null,
            ),
          });
        }
        if (entrySnapshot.draft.packageSessionProof?.proof.schemaVersion === 3 && (attempt.attemptKind === "payment-free-submission" ||
          attempt.attempt.state.kind === "authorized-pending-booking")) {
          packageAtomicSubmissionInvocations.set(invocation, { attempt, proof: entrySnapshot.draft.packageSessionProof,
            response: Promise.resolve(invocation.providerInvocation.value).then(capturePackageSessionBookingProviderPlainData, () => null) });
        }
        if (entrySnapshot.draft.membershipSessionProof && (attempt.attemptKind === "payment-free-submission" ||
          attempt.attempt.state.kind === "authorized-pending-booking")) {
          membershipSubmissionInvocations.set(invocation, {
            attempt, proof: entrySnapshot.draft.membershipSessionProof,
            response: Promise.resolve(invocation.providerInvocation.value).then(captureMembershipSessionPlainData, () => null),
          });
        }
        return invocation;
      } catch {
        return {
          status: "invoked",
          authority: "legacy-non-gateway",
          lineage: null,
          providerInvocation: { status: "threw" },
        };
      }
    }

    const freshOrigin =
      freshLineage && sourceAppointmentDraft
        ? createOrigin({
            attempt,
            lineage: freshLineage,
            sourceAppointmentDraft,
          })
        : null;
    let authoritativeAggregate = entrySnapshot.draft;
    let origin = authoritativeAggregate.origin;
    let createdCanonicalOrigin = false;

    if (origin) {
      const exact = exactOriginForAttempt(attempt, origin);
      if (!exact || !sameSnapshot(exact, origin)) {
        quarantineCustomerOwnedSessionStorage();
        return { status: "refused" };
      }
      origin = exact;
    } else {
      if (!allowOriginCreation || !freshOrigin) {
        return { status: "refused" };
      }
      const nextAggregate: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1 =
        {
          ...authoritativeAggregate,
          revision: authoritativeAggregate.revision + 1,
          origin: freshOrigin,
        };
      const originCommit = replaceTerminalAttemptAggregateStrict(
        authoritativeAggregate,
        nextAggregate,
        captureTerminalAttemptAggregateStrictMutationAuthority(lease),
      );
      if (originCommit.status !== "replaced") {
        if (ambiguousStatus(originCommit.status)) {
          quarantineCustomerOwnedSessionStorage();
        }
        return { status: "refused" };
      }
      authoritativeAggregate = originCommit.draft;
      origin = freshOrigin;
      createdCanonicalOrigin = true;

      // Compatibility marker only. It can aid old readers but never grants
      // provider authority and a failed projection never rolls back the
      // canonical single-key lineage.
      try {
        const projected = inspectOrigin(attempt.attempt.draftId);
        if (projected.status === "absent") {
          createOriginStrictPersisted(freshOrigin);
        }
      } catch {
        // Canonical lineage remains recoverable.
      }
    }

    const compensateFreshCanonicalAttempt = () => {
      if (
        !createdCanonicalOrigin ||
        !allowOriginCreation ||
        !specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
      ) {
        return;
      }
      const current = inspectTerminalAttemptAggregate(attempt.attempt.draftId);
      if (
        current.status !== "present" ||
        !sameSnapshot(current.draft, authoritativeAggregate)
      ) {
        return;
      }
      const removed = removeTerminalAttemptAggregateStrict(
        current.draft,
        captureTerminalAttemptAggregateStrictMutationAuthority(lease),
      );
      if (
        removed.status !== "removed" &&
        removed.status !== "absent" &&
        removed.status !== "superseded"
      ) {
        quarantineCustomerOwnedSessionStorage();
        return;
      }
      try {
        const projectedOrigin = inspectOrigin(attempt.attempt.draftId);
        if (
          projectedOrigin.status === "present" &&
          sameSnapshot(projectedOrigin.draft, origin)
        ) {
          removeOriginStrictPersisted(projectedOrigin.draft);
        }
        const projectedAttempt = inspectPersistedAttempt(attempt);
        if (projectedAttempt.status === "present" && projectedAttempt.exact) {
          removePersistedAttemptStrict(projectedAttempt.attempt);
        }
      } catch {
        // Projection cleanup cannot resurrect canonical authority.
      }
    };

    if (
      !origin ||
      (requireFreshOrigin &&
        (!freshOrigin || !sameSnapshot(freshOrigin, origin))) ||
      (allowOriginCreation &&
        (!freshOrigin || !sameSnapshot(freshOrigin, origin)))
    ) {
      compensateFreshCanonicalAttempt();
      return { status: "refused" };
    }

    if (
      !specialistLocationGatewayTerminalWriteIsCurrent(lease, isCurrentOwner)
    ) {
      compensateFreshCanonicalAttempt();
      return { status: "refused" };
    }
    // This is the only persisted proof after the last external owner
    // callback. From here to the captured provider closure there are no
    // property getters, storage callbacks, or caller callbacks.
    const postOwnerSnapshot = inspectStableTerminalAttemptAggregate(
      attempt.attempt.draftId,
    );
    const postOwnerSnapshotsAreExact =
      postOwnerSnapshot.status === "stable" &&
      postOwnerSnapshot.value.status === "present" &&
      sameSnapshot(postOwnerSnapshot.value.draft, authoritativeAggregate) &&
      postOwnerSnapshot.value.draft.receiptCommit === null &&
      postOwnerSnapshot.value.draft.origin !== null &&
      sameSnapshot(postOwnerSnapshot.value.draft.origin, origin);
    const postOwnerLeaseIsCurrent =
      specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease);
    if (!postOwnerSnapshotsAreExact || !postOwnerLeaseIsCurrent) {
      compensateFreshCanonicalAttempt();
      return { status: "refused" };
    }
    try {
      return {
        status: "invoked",
        authority: "gateway",
        lineage: origin.lineage,
        providerInvocation: {
          status: "returned",
          value: invokeProvider(),
        },
      };
    } catch {
      return {
        status: "invoked",
        authority: "gateway",
        lineage: origin.lineage,
        providerInvocation: { status: "threw" },
      };
    }
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export function invokeSpecialistLocationGatewayAppointmentPaymentAttemptProvider<
  Result,
>(
  input: SharedInvocationInput & {
    attempt: unknown;
    operation: "start" | "resume" | "cancel" | "authorized-submit";
    invokeProvider: (packageSessionProof?: AppointmentPackageSessionTerminalProofV1,
      membershipSessionProof?: AppointmentMembershipSessionTerminalProofV1) => Result;
  },
): InvokeSpecialistLocationGatewayAppointmentAttemptResult<Result> {
  let attemptValue: unknown;
  let operation: "start" | "resume" | "cancel" | "authorized-submit";
  let originRequirement: OriginRequirement;
  let allowOriginCreation: boolean;
  let sourceAppointmentDraft: AppointmentBookingDraftV1 | null;
  let freshLineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
  let isCurrentOwner: () => boolean;
  let invokeProvider: (packageSessionProof?: AppointmentPackageSessionTerminalProofV1,
    membershipSessionProof?: AppointmentMembershipSessionTerminalProofV1) => Result;
  try {
    attemptValue = input.attempt;
    operation = input.operation;
    originRequirement = input.originRequirement;
    allowOriginCreation = input.allowOriginCreation;
    sourceAppointmentDraft = input.sourceAppointmentDraft;
    freshLineage = input.freshLineage;
    isCurrentOwner = input.isCurrentOwner;
    invokeProvider = input.invokeProvider;
  } catch {
    return { status: "refused" };
  }
  if (
    (operation !== "start" &&
      operation !== "resume" &&
      operation !== "cancel" &&
      operation !== "authorized-submit") ||
    (originRequirement !== "gateway" &&
      originRequirement !== "legacy-non-gateway") ||
    typeof allowOriginCreation !== "boolean" ||
    typeof isCurrentOwner !== "function" ||
    typeof invokeProvider !== "function"
  ) {
    return { status: "refused" };
  }
  const attempt = validateSupportedAttempt("payment", attemptValue);
  if (!attempt || attempt.attemptKind !== "payment") {
    return { status: "refused" };
  }
  const state =
    attempt.attempt.state.kind === "terminal"
      ? attempt.attempt.state.priorState
      : attempt.attempt.state;
  const operationMatches =
    operation === "start"
      ? attempt.attempt.state.kind === "starting"
      : operation === "resume"
        ? state.kind === "external-authorization" ||
          state.kind === "processing" ||
          state.kind === "unknown-outcome"
        : operation === "cancel"
          ? state.kind === "external-authorization" ||
            state.kind === "processing" ||
            state.kind === "unknown-outcome"
          : state.kind === "authorized-pending-booking";
  if (!operationMatches || attempt.attempt.state.kind === "terminal") {
    return { status: "refused" };
  }
  if (allowOriginCreation && operation !== "start") {
    return { status: "refused" };
  }
  return invokeAttemptProvider({
    attempt,
    originRequirement,
    allowOriginCreation,
    sourceAppointmentDraft,
    freshLineage,
    requireFreshOrigin: allowOriginCreation || operation === "cancel",
    isCurrentOwner,
    invokeProvider,
  });
}

export function invokeSpecialistLocationGatewayAppointmentSubmissionAttemptProvider<
  Result,
>(
  input: SharedInvocationInput & {
    attempt: unknown;
    /** The caller captured a retry/mutable transition before normalizing it. */
    requireFreshOrigin?: boolean;
    invokeProvider: (packageSessionProof?: AppointmentPackageSessionTerminalProofV1,
      membershipSessionProof?: AppointmentMembershipSessionTerminalProofV1) => Result;
  },
): InvokeSpecialistLocationGatewayAppointmentAttemptResult<Result> {
  let attemptValue: unknown;
  let originRequirement: OriginRequirement;
  let allowOriginCreation: boolean;
  let sourceAppointmentDraft: AppointmentBookingDraftV1 | null;
  let freshLineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
  let isCurrentOwner: () => boolean;
  let invokeProvider: (packageSessionProof?: AppointmentPackageSessionTerminalProofV1,
    membershipSessionProof?: AppointmentMembershipSessionTerminalProofV1) => Result;
  let callerRequiresFreshOrigin: boolean;
  try {
    attemptValue = input.attempt;
    originRequirement = input.originRequirement;
    allowOriginCreation = input.allowOriginCreation;
    sourceAppointmentDraft = input.sourceAppointmentDraft;
    freshLineage = input.freshLineage;
    isCurrentOwner = input.isCurrentOwner;
    invokeProvider = input.invokeProvider;
    callerRequiresFreshOrigin = input.requireFreshOrigin === true;
  } catch {
    return { status: "refused" };
  }
  if (
    (originRequirement !== "gateway" &&
      originRequirement !== "legacy-non-gateway") ||
    typeof allowOriginCreation !== "boolean" ||
    typeof isCurrentOwner !== "function" ||
    typeof invokeProvider !== "function"
  ) {
    return { status: "refused" };
  }
  const attempt = validateSupportedAttempt(
    "payment-free-submission",
    attemptValue,
  );
  if (
    !attempt ||
    attempt.attemptKind !== "payment-free-submission" ||
    attempt.attempt.state.kind === "terminal"
  ) {
    return { status: "refused" };
  }
  return invokeAttemptProvider({
    attempt,
    originRequirement,
    allowOriginCreation,
    sourceAppointmentDraft,
    freshLineage,
    requireFreshOrigin:
      allowOriginCreation ||
      callerRequiresFreshOrigin ||
      attempt.attempt.state.kind === "retryable",
    isCurrentOwner,
    invokeProvider,
  });
}

function cloneAppointmentBookingReceipt(value: unknown) {
  try {
    const validation = validateAppointmentBookingReceipt(value);
    return validation.valid ? validation.receipt : null;
  } catch {
    return null;
  }
}

function canonicalReceiptMatchesAttempt(input: {
  attempt: SupportedAttempt;
  origin: SpecialistLocationGatewayAppointmentAttemptOriginV1 | null;
  originRequirement: OriginRequirement;
  receipt: AppointmentBookingReceiptV2;
}) {
  const { attempt, origin, originRequirement, receipt } = input;
  const sourceDraft = getWorkingAppointmentDraft(attempt);
  const sourceFingerprint =
    originRequirement === "gateway"
      ? (origin?.sourceAppointmentDraftFingerprint ?? null)
      : attempt.attemptKind === "payment-free-submission"
        ? createAppointmentStepOneDraftFingerprint(
            attempt.attempt.sourceAppointmentDraft,
          )
        : createAppointmentStepOneDraftFingerprint(
            getPaymentAuthorizedSourceDraft(attempt.attempt) ?? sourceDraft,
          );
  if (
    receipt.draftId !== attempt.attempt.draftId ||
    receipt.sourceAppointmentDraftId !== attempt.attempt.draftId ||
    receipt.customerAccountId !== attempt.attempt.customerAccountId ||
    receipt.sourceAppointmentDraftFingerprint !== sourceFingerprint ||
    !sameSnapshot(receipt.reviewDraft, attempt.attempt.reviewDraft) ||
    !sameSnapshot(
      receipt.presentationSnapshot,
      attempt.attempt.presentationSnapshot,
    ) ||
    !sameSnapshot(
      receipt.staffChoiceEvidence,
      attempt.attempt.staffChoiceEvidence ?? null,
    ) ||
    (originRequirement === "gateway" &&
      (!origin ||
        receipt.sourceAppointmentDraftFingerprint !==
          origin.sourceAppointmentDraftFingerprint)) ||
    (originRequirement === "legacy-non-gateway" && origin !== null)
  ) {
    return false;
  }
  if (attempt.attemptKind === "payment-free-submission") {
    return (
      receipt.idempotencyKey === attempt.attempt.idempotencyKey &&
      receipt.payment === null
    );
  }
  const state =
    attempt.attempt.state.kind === "terminal"
      ? attempt.attempt.state.priorState
      : attempt.attempt.state;
  return (
    state.kind === "authorized-pending-booking" &&
    receipt.idempotencyKey === state.submissionIdempotencyKey &&
    sameSnapshot(receipt.payment, state.authorization)
  );
}

export type ReadSpecialistLocationGatewayAppointmentTerminalAttemptResult =
  | {
      status: "ready";
      attemptKind: "payment";
      attempt: AppointmentPaymentAttemptResumeDraftV1;
      originRequirement: OriginRequirement;
      origin: SpecialistLocationGatewayAppointmentAttemptOriginV1 | null;
      resultsEntryProof: SpecialistLocationGatewayAppointmentResultsEntryProofV1 | null;
      packageSessionProof: AppointmentPackageSessionTerminalProofV1 | null;
      membershipSessionProof: AppointmentMembershipSessionTerminalProofV1 | null;
      receipt: AppointmentBookingReceiptV2 | null;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
    }
  | {
      status: "ready";
      attemptKind: "payment-free-submission";
      attempt: AppointmentSubmissionAttemptDraftV1;
      originRequirement: OriginRequirement;
      origin: SpecialistLocationGatewayAppointmentAttemptOriginV1 | null;
      resultsEntryProof: SpecialistLocationGatewayAppointmentResultsEntryProofV1 | null;
      packageSessionProof: AppointmentPackageSessionTerminalProofV1 | null;
      membershipSessionProof: AppointmentMembershipSessionTerminalProofV1 | null;
      receipt: AppointmentBookingReceiptV2 | null;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
    }
  | { status: "absent" | "owner-mismatch" | "unreadable" };

export type InspectSpecialistLocationGatewayAppointmentTerminalAttemptPresenceResult =
  { status: "absent" | "present" } | { status: "unreadable" };

/**
 * Owner-agnostic presence proof for lifecycle cleanup gates. It exposes no
 * aggregate bytes and therefore cannot authorize provider, transition, or
 * Receipt work; callers use it only to refuse cleanup while any canonical
 * Appointment terminal-attempt aggregate owns the destination.
 */
export function inspectSpecialistLocationGatewayAppointmentTerminalAttemptPresenceStrict(
  draftIdInput: string,
): InspectSpecialistLocationGatewayAppointmentTerminalAttemptPresenceResult {
  let draftId: string;
  try {
    draftId = draftIdInput;
  } catch {
    return { status: "unreadable" };
  }
  if (!isIdentifier(draftId)) return { status: "unreadable" };
  const stableInspected = inspectStableTerminalAttemptAggregate(draftId);
  if (stableInspected.status !== "stable") {
    return { status: "unreadable" };
  }
  return stableInspected.value.status === "present"
    ? { status: "present" }
    : { status: stableInspected.value.status };
}

/**
 * Sole durable authority for an Appointment provider attempt and its accepted
 * Receipt. The older attempt/origin/Receipt stores are compatibility
 * projections and are deliberately not consulted by this reader.
 */
export function readSpecialistLocationGatewayAppointmentTerminalAttemptStrict(input: {
  draftId: string;
  expectedCustomerAccountId: string;
}): ReadSpecialistLocationGatewayAppointmentTerminalAttemptResult {
  let draftId: string;
  let expectedCustomerAccountId: string;
  try {
    draftId = input.draftId;
    expectedCustomerAccountId = input.expectedCustomerAccountId;
  } catch {
    return { status: "unreadable" };
  }
  if (!isIdentifier(draftId) || !isIdentifier(expectedCustomerAccountId)) {
    return { status: "owner-mismatch" };
  }
  const stableInspected = inspectStableTerminalAttemptAggregate(draftId);
  if (stableInspected.status !== "stable") {
    return { status: "unreadable" };
  }
  const inspected = stableInspected.value;
  if (inspected.status !== "present") return { status: inspected.status };
  if (inspected.draft.customerAccountId !== expectedCustomerAccountId) {
    return { status: "owner-mismatch" };
  }
  const attempt = validateSupportedAttempt(
    inspected.draft.attemptKind,
    inspected.draft.attempt,
  );
  if (!attempt) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" };
  }
  const common = {
    status: "ready" as const,
    originRequirement: inspected.draft.originRequirement,
    origin: inspected.draft.origin,
    resultsEntryProof: inspected.draft.resultsEntryProof ?? null,
    packageSessionProof: inspected.draft.packageSessionProof ?? null,
    membershipSessionProof: inspected.draft.membershipSessionProof ?? null,
    receipt: inspected.draft.receiptCommit?.receipt ?? null,
    lineage:
      inspected.draft.receiptCommit?.terminalLineage ??
      inspected.draft.origin?.lineage ??
      null,
  };
  return attempt.attemptKind === "payment"
    ? {
        ...common,
        attemptKind: "payment",
        attempt: attempt.attempt,
      }
    : {
        ...common,
        attemptKind: "payment-free-submission",
        attempt: attempt.attempt,
      };
}

export function readSpecialistLocationGatewayAppointmentTerminalReceiptStrict(input: {
  draftId: string;
  expectedCustomerAccountId: string;
}):
  | {
      status: "ready";
      receipt: AppointmentBookingReceiptV2;
      attemptKind: SpecialistLocationGatewayAppointmentAttemptKind;
      attempt:
        | AppointmentPaymentAttemptResumeDraftV1
        | AppointmentSubmissionAttemptDraftV1;
      originRequirement: OriginRequirement;
      resultsEntryProof: SpecialistLocationGatewayAppointmentResultsEntryProofV1 | null;
      packageSessionProof: AppointmentPackageSessionTerminalProofV1 | null;
      membershipSessionProof: AppointmentMembershipSessionTerminalProofV1 | null;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
    }
  | { status: "absent" | "owner-mismatch" | "unreadable" } {
  const aggregate =
    readSpecialistLocationGatewayAppointmentTerminalAttemptStrict(input);
  if (aggregate.status !== "ready") return aggregate;
  if (!aggregate.receipt) return { status: "absent" };
  return {
    status: "ready",
    receipt: aggregate.receipt,
    attemptKind: aggregate.attemptKind,
    attempt: aggregate.attempt,
    originRequirement: aggregate.originRequirement,
    resultsEntryProof: aggregate.resultsEntryProof,
    packageSessionProof: aggregate.packageSessionProof,
    membershipSessionProof: aggregate.membershipSessionProof,
    lineage: aggregate.lineage,
  };
}

/**
 * Marks an exact canonical Receipt as observed by the authenticated
 * Confirmation destination. The Receipt stays embedded in the aggregate for
 * reload recovery; acknowledgement only makes the oldest resolved aggregate
 * eligible for deterministic capacity reclamation by a later fresh attempt.
 */
export function acknowledgeSpecialistLocationGatewayAppointmentConfirmationStrict(input: {
  receipt: unknown;
  acknowledgedAtEpochMs: number;
  isCurrentOwner: () => boolean;
}):
  { status: "acknowledged" | "already-acknowledged" } | { status: "refused" } {
  let receipt: AppointmentBookingReceiptV2 | null;
  let acknowledgedAtEpochMs: number;
  let isCurrentOwner: () => boolean;
  try {
    receipt = cloneAppointmentBookingReceipt(input.receipt);
    acknowledgedAtEpochMs = input.acknowledgedAtEpochMs;
    isCurrentOwner = input.isCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (
    !receipt ||
    !isEpoch(acknowledgedAtEpochMs) ||
    typeof isCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify([
      "appointment-terminal-attempt-confirmation-ack",
      receipt.draftId,
      receipt.customerAccountId,
    ]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    const initial = inspectTerminalAttemptAggregate(receipt.draftId);
    if (
      initial.status !== "present" ||
      initial.draft.customerAccountId !== receipt.customerAccountId ||
      !initial.draft.receiptCommit ||
      !sameSnapshot(initial.draft.receiptCommit.receipt, receipt) ||
      !specialistLocationGatewayTerminalWriteIsCurrent(lease, isCurrentOwner)
    ) {
      return { status: "refused" };
    }
    const postOwner = inspectStableTerminalAttemptAggregate(receipt.draftId);
    if (
      postOwner.status !== "stable" ||
      postOwner.value.status !== "present" ||
      !sameSnapshot(postOwner.value.draft, initial.draft) ||
      !specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
    ) {
      return { status: "refused" };
    }
    if (postOwner.value.draft.confirmationAcknowledgedAtEpochMs !== null) {
      return { status: "already-acknowledged" };
    }
    const next: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1 =
      {
        ...postOwner.value.draft,
        revision: postOwner.value.draft.revision + 1,
        confirmationAcknowledgedAtEpochMs: acknowledgedAtEpochMs,
      };
    const replaced = replaceTerminalAttemptAggregateStrict(
      postOwner.value.draft,
      next,
      captureTerminalAttemptAggregateStrictMutationAuthority(lease),
    );
    if (replaced.status !== "replaced") {
      const concurrent = inspectTerminalAttemptAggregate(receipt.draftId);
      if (
        concurrent.status === "present" &&
        concurrent.draft.customerAccountId === receipt.customerAccountId &&
        concurrent.draft.receiptCommit &&
        sameSnapshot(concurrent.draft.receiptCommit.receipt, receipt) &&
        concurrent.draft.confirmationAcknowledgedAtEpochMs !== null
      ) {
        return { status: "already-acknowledged" };
      }
      if (ambiguousStatus(replaced.status)) {
        quarantineCustomerOwnedSessionStorage();
      }
      return { status: "refused" };
    }
    const finalOwnerIsCurrent = specialistLocationGatewayTerminalWriteIsCurrent(
      lease,
      isCurrentOwner,
    );
    const final = finalOwnerIsCurrent
      ? inspectStableTerminalAttemptAggregate(receipt.draftId)
      : ({ status: "changed" } as const);
    if (
      finalOwnerIsCurrent &&
      final.status === "stable" &&
      final.value.status === "present" &&
      sameSnapshot(final.value.draft, replaced.draft) &&
      specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
    ) {
      return { status: "acknowledged" };
    }

    // ACK is not provider evidence. If its final owner proof fails, restore
    // the Receipt to an unacknowledged (therefore non-evictable) revision.
    if (specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)) {
      const current = inspectTerminalAttemptAggregate(receipt.draftId);
      if (
        current.status === "present" &&
        sameSnapshot(current.draft, replaced.draft)
      ) {
        const rollback: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1 =
          {
            ...current.draft,
            revision: current.draft.revision + 1,
            confirmationAcknowledgedAtEpochMs: null,
          };
        const restored = replaceTerminalAttemptAggregateStrict(
          current.draft,
          rollback,
          captureTerminalAttemptAggregateStrictMutationAuthority(lease),
        );
        if (restored.status !== "replaced") {
          const remaining = inspectTerminalAttemptAggregate(receipt.draftId);
          if (
            remaining.status === "present" &&
            sameSnapshot(remaining.draft, current.draft)
          ) {
            quarantineCustomerOwnedSessionStorage();
          }
        }
      }
    }
    return { status: "refused" };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

type CommitAppointmentTerminalReceiptResult<Attempt> =
  | {
      status: "committed" | "already-committed";
      receipt: AppointmentBookingReceiptV2;
      attempt: Attempt;
      ownerCurrent: boolean;
    }
  | { status: "absorbed-terminal"; attempt: Attempt }
  | { status: "refused" };

function commitAppointmentTerminalReceiptStrict(input: {
  attempt: SupportedAttempt;
  receipt: AppointmentBookingReceiptV2;
  originRequirement: OriginRequirement;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
  atomicTerminalCommit?: PackageSessionAtomicTerminalCommitV3;
}): CommitAppointmentTerminalReceiptResult<
  AppointmentPaymentAttemptResumeDraftV1 | AppointmentSubmissionAttemptDraftV1
> {
  const {
    attempt,
    receipt,
    originRequirement,
    isCurrentOwner,
    isPureCurrentOwner,
  } = input;
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify([
      "appointment-terminal-attempt-receipt-commit",
      attempt.attemptKind,
      attempt.attempt.draftId,
    ]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    const initial = inspectTerminalAttemptAggregate(attempt.attempt.draftId);
    if (initial.status !== "present") return { status: "refused" };
    if (
      !terminalAttemptAggregateMatchesAttempt(
        initial.draft,
        attempt,
        originRequirement,
      )
    ) {
      const concurrent = validateSupportedAttempt(
        initial.draft.attemptKind,
        initial.draft.attempt,
      );
      return concurrent &&
        createAttemptFingerprint(concurrent) ===
          createAttemptFingerprint(attempt) &&
        attemptStateIsTerminal(concurrent)
        ? { status: "absorbed-terminal", attempt: concurrent.attempt }
        : { status: "refused" };
    }
    if (attemptStateIsTerminal(attempt)) {
      return { status: "absorbed-terminal", attempt: attempt.attempt };
    }
    if (
      !canonicalReceiptMatchesAttempt({
        attempt,
        origin: initial.draft.origin,
        originRequirement,
        receipt,
      })
    ) {
      return { status: "refused" };
    }
    if (initial.draft.receiptCommit) {
      return sameSnapshot(initial.draft.receiptCommit.receipt, receipt)
        ? {
            status: "already-committed",
            receipt: initial.draft.receiptCommit.receipt,
            attempt: initial.draft.attempt,
            ownerCurrent: false,
          }
        : { status: "refused" };
    }
    if (initial.draft.packageSessionProof?.proof.schemaVersion === 3 && !validatePackageSessionAtomicTerminalCommitV3({
      value: input.atomicTerminalCommit, attempt: attempt.attempt, proof: initial.draft.packageSessionProof.proof, receipt, rejectionReason: null })) return { status: "refused" };

    let pureOwnerWasCurrent = false;
    try {
      pureOwnerWasCurrent = isPureCurrentOwner() === true;
    } catch {
      pureOwnerWasCurrent = false;
    }
    const ownerWasCurrent =
      pureOwnerWasCurrent &&
      specialistLocationGatewayTerminalWriteIsCurrent(lease, isCurrentOwner);
    const postOwner = ownerWasCurrent
      ? inspectStableTerminalAttemptAggregate(attempt.attempt.draftId)
      : ({ status: "changed" } as const);
    if (
      !ownerWasCurrent ||
      postOwner.status !== "stable" ||
      postOwner.value.status !== "present" ||
      !sameSnapshot(postOwner.value.draft, initial.draft) ||
      !specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
    ) {
      return { status: "refused" };
    }

    const committedAggregate: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1 =
      {
        ...initial.draft,
        revision: initial.draft.revision + 1,
        ...(input.atomicTerminalCommit ? { packageSessionAtomicTerminalCommit: input.atomicTerminalCommit } : {}),
        receiptCommit: {
          receipt,
          terminalLineage: initial.draft.origin?.lineage ?? null,
        },
      };
    const committed = replaceTerminalAttemptAggregateStrict(
      initial.draft,
      committedAggregate,
      captureTerminalAttemptAggregateStrictMutationAuthority(lease),
    );
    if (committed.status !== "replaced") {
      const concurrent = inspectTerminalAttemptAggregate(
        attempt.attempt.draftId,
      );
      if (
        concurrent.status === "present" &&
        concurrent.draft.receiptCommit &&
        terminalAttemptAggregateMatchesAttempt(
          concurrent.draft,
          attempt,
          originRequirement,
        ) &&
        sameSnapshot(concurrent.draft.receiptCommit.receipt, receipt)
      ) {
        return {
          status: "already-committed",
          receipt: concurrent.draft.receiptCommit.receipt,
          attempt: concurrent.draft.attempt,
          ownerCurrent: false,
        };
      }
      if (concurrent.status === "present") {
        const concurrentAttempt = validateSupportedAttempt(
          concurrent.draft.attemptKind,
          concurrent.draft.attempt,
        );
        if (
          concurrentAttempt &&
          createAttemptFingerprint(concurrentAttempt) ===
            createAttemptFingerprint(attempt) &&
          attemptStateIsTerminal(concurrentAttempt)
        ) {
          return {
            status: "absorbed-terminal",
            attempt: concurrentAttempt.attempt,
          };
        }
      }
      if (ambiguousStatus(committed.status)) {
        quarantineCustomerOwnedSessionStorage();
      }
      return { status: "refused" };
    }

    let finalPureOwnerIsCurrent = false;
    try {
      finalPureOwnerIsCurrent = isPureCurrentOwner() === true;
    } catch {
      finalPureOwnerIsCurrent = false;
    }
    const finalOwnerIsCurrent =
      finalPureOwnerIsCurrent &&
      specialistLocationGatewayTerminalWriteIsCurrent(lease, isCurrentOwner);
    // Accepted provider evidence is never rolled back after this canonical
    // CAS. Owner drift only withdraws same-mount publication; recovery reads
    // the immutable Receipt from the aggregate under the new owner.
    const final = finalOwnerIsCurrent
      ? inspectStableTerminalAttemptAggregate(attempt.attempt.draftId)
      : ({ status: "changed" } as const);
    const exactFinal =
      final.status === "stable" &&
      final.value.status === "present" &&
      sameSnapshot(final.value.draft, committed.draft) &&
      specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease);
    return {
      status: "committed",
      receipt: committed.draft.receiptCommit!.receipt,
      attempt: committed.draft.attempt,
      ownerCurrent: exactFinal,
    };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export function commitSpecialistLocationGatewayAppointmentPaymentReceiptStrict(input: {
  attempt: unknown;
  receipt: unknown;
  originRequirement: OriginRequirement;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}): CommitAppointmentTerminalReceiptResult<AppointmentPaymentAttemptResumeDraftV1> {
  let attempt: SupportedAttempt | null;
  let receipt: AppointmentBookingReceiptV2 | null;
  try {
    attempt = validateSupportedAttempt("payment", input.attempt);
    receipt = cloneAppointmentBookingReceipt(input.receipt);
  } catch {
    return { status: "refused" };
  }
  if (
    !attempt ||
    attempt.attemptKind !== "payment" ||
    !receipt ||
    (input.originRequirement !== "gateway" &&
      input.originRequirement !== "legacy-non-gateway") ||
    typeof input.isCurrentOwner !== "function" ||
    typeof input.isPureCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const result = commitAppointmentTerminalReceiptStrict({
    attempt,
    receipt,
    originRequirement: input.originRequirement,
    isCurrentOwner: input.isCurrentOwner,
    isPureCurrentOwner: input.isPureCurrentOwner,
  });
  if (result.status === "refused") return result;
  const validated = validateAppointmentPaymentAttemptResumeDraft(
    result.attempt,
  );
  if (!validated.valid) return { status: "refused" };
  return result.status === "absorbed-terminal"
    ? { status: result.status, attempt: validated.draft }
    : {
        ...result,
        attempt: validated.draft,
      };
}

export function commitSpecialistLocationGatewayAppointmentSubmissionReceiptStrict(input: {
  attempt: unknown;
  receipt: unknown;
  originRequirement: OriginRequirement;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}): CommitAppointmentTerminalReceiptResult<AppointmentSubmissionAttemptDraftV1> {
  let attempt: SupportedAttempt | null;
  let receipt: AppointmentBookingReceiptV2 | null;
  try {
    attempt = validateSupportedAttempt(
      "payment-free-submission",
      input.attempt,
    );
    receipt = cloneAppointmentBookingReceipt(input.receipt);
  } catch {
    return { status: "refused" };
  }
  if (
    !attempt ||
    attempt.attemptKind !== "payment-free-submission" ||
    !receipt ||
    (input.originRequirement !== "gateway" &&
      input.originRequirement !== "legacy-non-gateway") ||
    typeof input.isCurrentOwner !== "function" ||
    typeof input.isPureCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const result = commitAppointmentTerminalReceiptStrict({
    attempt,
    receipt,
    originRequirement: input.originRequirement,
    isCurrentOwner: input.isCurrentOwner,
    isPureCurrentOwner: input.isPureCurrentOwner,
  });
  if (result.status === "refused") return result;
  const validated = validateAppointmentSubmissionAttemptDraft(result.attempt);
  if (!validated.valid) return { status: "refused" };
  return result.status === "absorbed-terminal"
    ? { status: result.status, attempt: validated.draft }
    : {
        ...result,
        attempt: validated.draft,
      };
}

function consumeAttemptOriginAfterTerminalReceipt(input: {
  attempt: SupportedAttempt;
  receipt: AppointmentBookingReceiptV2;
  isCurrentOwner: () => boolean;
}): { status: "consumed" } | { status: "refused" } {
  if (attemptStateIsTerminal(input.attempt)) {
    return { status: "refused" };
  }
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify([
      "appointment-attempt-origin-terminal-consumption",
      input.attempt.attemptKind,
      input.attempt.attempt.draftId,
    ]),
    input.isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    const entrySnapshot = inspectTerminalAttemptAggregate(
      input.attempt.attempt.draftId,
    );
    if (
      entrySnapshot.status !== "present" ||
      !terminalAttemptAggregateMatchesAttempt(
        entrySnapshot.draft,
        input.attempt,
        "gateway",
      ) ||
      !entrySnapshot.draft.origin ||
      !entrySnapshot.draft.receiptCommit ||
      !sameSnapshot(entrySnapshot.draft.receiptCommit.receipt, input.receipt)
    ) {
      return { status: "refused" };
    }
    const origin = entrySnapshot.draft.origin;
    const initialOwnerIsCurrent =
      specialistLocationGatewayTerminalWriteIsCurrent(
        lease,
        input.isCurrentOwner,
      );
    const postOwnerAggregate = initialOwnerIsCurrent
      ? inspectStableTerminalAttemptAggregate(input.attempt.attempt.draftId)
      : ({ status: "changed" } as const);
    if (
      !initialOwnerIsCurrent ||
      postOwnerAggregate.status !== "stable" ||
      postOwnerAggregate.value.status !== "present" ||
      !sameSnapshot(postOwnerAggregate.value.draft, entrySnapshot.draft) ||
      !specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
    ) {
      return { status: "refused" };
    }

    // Drain only the old marker projection. Absence, refusal, or a concurrent
    // projection writer cannot revoke the canonical accepted Receipt and does
    // not authorize cleanup by itself.
    try {
      const projection = inspectOrigin(input.attempt.attempt.draftId);
      if (
        projection.status === "present" &&
        sameSnapshot(projection.draft, origin)
      ) {
        removeOriginStrictPersisted(projection.draft);
      }
    } catch {
      // Canonical accepted state remains authoritative and retryable.
    }

    const finalAggregate = inspectStableTerminalAttemptAggregate(
      input.attempt.attempt.draftId,
    );
    return finalAggregate.status === "stable" &&
      finalAggregate.value.status === "present" &&
      sameSnapshot(finalAggregate.value.draft, entrySnapshot.draft) &&
      specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
      ? { status: "consumed" }
      : { status: "refused" };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export function consumeSpecialistLocationGatewayAppointmentPaymentAttemptOriginAfterTerminalReceiptStrict(input: {
  attempt: unknown;
  receipt: unknown;
  isCurrentOwner: () => boolean;
}): { status: "consumed" } | { status: "refused" } {
  let attemptValue: unknown;
  let receiptValue: unknown;
  let isCurrentOwner: () => boolean;
  try {
    attemptValue = input.attempt;
    receiptValue = input.receipt;
    isCurrentOwner = input.isCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (typeof isCurrentOwner !== "function") return { status: "refused" };
  const attempt = validateSupportedAttempt("payment", attemptValue);
  const receipt = cloneAppointmentBookingReceipt(receiptValue);
  return attempt && receipt
    ? consumeAttemptOriginAfterTerminalReceipt({
        attempt,
        receipt,
        isCurrentOwner,
      })
    : { status: "refused" };
}

export function consumeSpecialistLocationGatewayAppointmentSubmissionAttemptOriginAfterTerminalReceiptStrict(input: {
  attempt: unknown;
  receipt: unknown;
  isCurrentOwner: () => boolean;
}): { status: "consumed" } | { status: "refused" } {
  let attemptValue: unknown;
  let receiptValue: unknown;
  let isCurrentOwner: () => boolean;
  try {
    attemptValue = input.attempt;
    receiptValue = input.receipt;
    isCurrentOwner = input.isCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (typeof isCurrentOwner !== "function") return { status: "refused" };
  const attempt = validateSupportedAttempt(
    "payment-free-submission",
    attemptValue,
  );
  const receipt = cloneAppointmentBookingReceipt(receiptValue);
  return attempt && receipt
    ? consumeAttemptOriginAfterTerminalReceipt({
        attempt,
        receipt,
        isCurrentOwner,
      })
    : { status: "refused" };
}

function retireAppointmentTerminalAttemptStrict(input: {
  attempt: SupportedAttempt;
  isCurrentOwner: () => boolean;
}): { status: "retired" } | { status: "refused" } {
  const { attempt, isCurrentOwner } = input;
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify([
      "appointment-terminal-attempt-retirement",
      attempt.attemptKind,
      attempt.attempt.draftId,
      attempt.attempt.idempotencyKey,
    ]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    const initial = inspectTerminalAttemptAggregate(attempt.attempt.draftId);
    if (
      initial.status !== "present" ||
      !terminalAttemptAggregateMatchesAttempt(initial.draft, attempt) ||
      initial.draft.receiptCommit !== null ||
      !specialistLocationGatewayTerminalWriteIsCurrent(lease, isCurrentOwner)
    ) {
      return { status: "refused" };
    }
    const postOwner = inspectStableTerminalAttemptAggregate(
      attempt.attempt.draftId,
    );
    if (
      postOwner.status !== "stable" ||
      postOwner.value.status !== "present" ||
      !sameSnapshot(postOwner.value.draft, initial.draft) ||
      !specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
    ) {
      return { status: "refused" };
    }
    const removed = removeTerminalAttemptAggregateStrict(
      initial.draft,
      captureTerminalAttemptAggregateStrictMutationAuthority(lease),
    );
    if (removed.status !== "removed") {
      if (ambiguousStatus(removed.status)) {
        quarantineCustomerOwnedSessionStorage();
      }
      return { status: "refused" };
    }

    // Old stores are drained only after canonical authority is absent. Their
    // result cannot resurrect or veto the canonical retirement.
    try {
      const projectedOrigin = inspectOrigin(attempt.attempt.draftId);
      if (
        projectedOrigin.status === "present" &&
        (!initial.draft.origin ||
          sameSnapshot(projectedOrigin.draft, initial.draft.origin))
      ) {
        removeOriginStrictPersisted(projectedOrigin.draft);
      }
      const projectedAttempt = inspectPersistedAttempt(attempt);
      if (projectedAttempt.status === "present" && projectedAttempt.exact) {
        removePersistedAttemptStrict(projectedAttempt.attempt);
      }
    } catch {
      // Canonical absence remains the only authority.
    }

    const finalOwnerIsCurrent = specialistLocationGatewayTerminalWriteIsCurrent(
      lease,
      isCurrentOwner,
    );
    const final = finalOwnerIsCurrent
      ? inspectStableTerminalAttemptAggregate(attempt.attempt.draftId)
      : ({ status: "changed" } as const);
    return finalOwnerIsCurrent &&
      final.status === "stable" &&
      final.value.status === "absent" &&
      specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
      ? { status: "retired" }
      : { status: "refused" };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export function retireSpecialistLocationGatewayAppointmentPaymentAttemptOriginStrict(input: {
  attempt: unknown;
  isCurrentOwner: () => boolean;
}): { status: "retired" } | { status: "refused" } {
  let attempt: SupportedAttempt | null;
  try {
    attempt = validateSupportedAttempt("payment", input.attempt);
  } catch {
    return { status: "refused" };
  }
  return attempt &&
    attempt.attemptKind === "payment" &&
    typeof input.isCurrentOwner === "function"
    ? retireAppointmentTerminalAttemptStrict({
        attempt,
        isCurrentOwner: input.isCurrentOwner,
      })
    : { status: "refused" };
}

export function retireSpecialistLocationGatewayAppointmentSubmissionAttemptOriginStrict(input: {
  attempt: unknown;
  isCurrentOwner: () => boolean;
}): { status: "retired" } | { status: "refused" } {
  let attempt: SupportedAttempt | null;
  try {
    attempt = validateSupportedAttempt(
      "payment-free-submission",
      input.attempt,
    );
  } catch {
    return { status: "refused" };
  }
  return attempt &&
    attempt.attemptKind === "payment-free-submission" &&
    typeof input.isCurrentOwner === "function"
    ? retireAppointmentTerminalAttemptStrict({
        attempt,
        isCurrentOwner: input.isCurrentOwner,
      })
    : { status: "refused" };
}

export function clearSpecialistLocationGatewayAppointmentAttemptOrigins() {
  try {
    return appointmentAttemptOriginStore.clearDrafts();
  } catch {
    return false;
  }
}

/** Auth needs complete immutable owner presence even while ordinary readers
 * are quarantined. This inventory grants no booking, release or receipt right. */
export function captureSpecialistLocationGatewayAppointmentTerminalPresenceForTeardown(evaluatedAtEpochMs: number) {
  const refused = () => { quarantineCustomerOwnedSessionStorage(); return null; };
  try {
    if (!isEpoch(evaluatedAtEpochMs)) return refused();
    const storage = window.sessionStorage;
    const storageKey = SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY;
    const initial = readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey });
    if (initial.status !== "ready") return refused();
    const owners: { draftId: string; customerAccountId: string }[] = [];
    if (initial.rawValue !== null) {
      if (initial.rawValue.length > 3_500_000) return refused();
      const envelope: unknown = JSON.parse(initial.rawValue);
      if (!isRecord(envelope) || !hasExactKeys(envelope, ["storeVersion", "storeKind", "entries"]) || envelope.storeVersion !== 1 ||
        envelope.storeKind !== "specialist-location-gateway-appointment-terminal-attempt-aggregates" || !Array.isArray(envelope.entries) || envelope.entries.length > 8) return refused();
      for (const row of envelope.entries) {
        if (!isRecord(row) || !hasExactKeys(row, ["draftId", "savedAtEpochMs", "draft"]) ||
          !isEpoch(row.savedAtEpochMs) || row.savedAtEpochMs > evaluatedAtEpochMs + 300_000) return refused();
        const valid = validateTerminalAttemptAggregate(row.draft);
        if (!valid.valid || valid.draft.draftId !== row.draftId || !sameSnapshot(valid.draft, row.draft) ||
          owners.some(owner => owner.draftId === row.draftId)) return refused();
        owners.push({ draftId: valid.draft.draftId, customerAccountId: valid.draft.customerAccountId });
      }
    }
    const isCurrent = () => {
      try {
        const current = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
        return window.sessionStorage === storage && current.status === "ready" && current.rawValue === initial.rawValue;
      } catch { return false; }
    };
    return isCurrent() ? { owners: Object.freeze(owners.map(owner => Object.freeze(owner))), isCurrent } : refused();
  } catch { return refused(); }
}

export function clearSpecialistLocationGatewayAppointmentTerminalAttempts() {
  const mutationProof = captureCustomerOwnedSessionStorageMutationProof();
  const refused = () => {
    quarantineCustomerOwnedSessionStorage();
    return false;
  };
  try {
    const storage = window.sessionStorage;
    const storageKey =
      SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY;
    const initial = readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey });
    if (initial.status !== "ready" ||
      !customerOwnedSessionStorageMutationProofIsCurrent(mutationProof)) return refused();
    if (initial.rawValue === null) return true;
    const now = Date.now();
    if (!isEpoch(now)) return refused();

    // Auth teardown runs under the shared quarantine, so normal store reads
    // are intentionally unavailable. Decode only canonical dense envelopes;
    // never normalize protected rows away or treat malformed bytes as empty.
    const parseRows = <T extends { draftId: string }>(
      raw: string | null,
      kind: string,
      maxCharacters: number,
      validate: (value: unknown) => T | null,
    ) => {
      if (raw === null) return [] as { row: Record<string, unknown>; draft: T }[];
      if (raw.length > maxCharacters) throw new Error("Oversized terminal envelope.");
      const envelope: unknown = JSON.parse(raw);
      if (!isRecord(envelope) || !hasExactKeys(envelope, ["storeVersion", "storeKind", "entries"]) ||
        envelope.storeVersion !== 1 || envelope.storeKind !== kind ||
        !Array.isArray(envelope.entries) || envelope.entries.length > 8) {
        throw new Error("Invalid terminal envelope.");
      }
      const seen = new Set<string>();
      return envelope.entries.map((row: unknown) => {
        if (!isRecord(row) || !hasExactKeys(row, ["draftId", "savedAtEpochMs", "draft"]) ||
          !isEpoch(row.savedAtEpochMs) || row.savedAtEpochMs > now + 300_000) {
          throw new Error("Invalid terminal row.");
        }
        const draft = validate(row.draft);
        if (!draft || draft.draftId !== row.draftId || seen.has(draft.draftId) ||
          !sameSnapshot(draft, row.draft)) throw new Error("Invalid terminal owner.");
        seen.add(draft.draftId);
        return { row, draft };
      });
    };
    const rows = parseRows(
      initial.rawValue,
      "specialist-location-gateway-appointment-terminal-attempt-aggregates",
      3_500_000,
      (value) => {
        const validation = validateTerminalAttemptAggregate(value);
        return validation.valid ? validation.draft : null;
      },
    );
    const hasPackageRows = rows.some(({ draft }) => draft.packageSessionProof != null);
    const atomicKey = PACKAGE_SESSION_ATOMIC_RESERVATION_STORAGE_KEY_V2;
    const atomicRaw = hasPackageRows
      ? readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey: atomicKey })
      : { status: "ready" as const, rawValue: null };
    const atomicInventory = hasPackageRows ? inspectPackageSessionAtomicReservationsForTeardownV2(now) : null;
    if (atomicRaw.status !== "ready" || (hasPackageRows && atomicInventory?.status !== "ready")) return refused();
    if (atomicInventory?.status === "ready" && rows.some(({ draft }) => {
      const proof = draft.packageSessionProof?.proof; if (!proof) return false;
      const record = atomicInventory.records.find(row => row.draftId === draft.draftId); if (!record) return false;
      return proof.schemaVersion !== 3 || record.customerAccountId !== draft.customerAccountId || !sameSnapshot(record.heldSnapshot, proof.reservationHold) ||
        !sameSnapshot(record.operation.selection, proof.selection) || !sameSnapshot(record.operation.reviewDraft, proof.reviewDraft);
    })) return refused();
    const reservationKey = PACKAGE_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY;
    const reservationRaw = hasPackageRows
      ? readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey: reservationKey })
      : { status: "ready" as const, rawValue: null };
    if (reservationRaw.status !== "ready") return refused();
    const reservations = parseRows<PackageSessionBookingReservationOperationRecordV1>(
      reservationRaw.rawValue,
      "package-session-booking-reservation-operations",
      8_000_000,
      validatePackageSessionBookingReservationOperationRecord,
    );
    const hasMembershipRows = rows.some(({ draft }) => draft.membershipSessionProof != null);
    const membershipReservationKey = MEMBERSHIP_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY;
    const membershipReservationRaw = hasMembershipRows
      ? readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey: membershipReservationKey })
      : { status: "ready" as const, rawValue: null };
    if (membershipReservationRaw.status !== "ready") return refused();
    const membershipReservations = parseRows<MembershipSessionBookingReservationOperationRecordV1>(
      membershipReservationRaw.rawValue,
      "membership-session-booking-reservation-operations",
      8_000_000,
      validateMembershipSessionBookingReservationOperationRecord,
    );
    const membershipReservationKeyV2 = MEMBERSHIP_SESSION_BOOKING_RESERVATION_OPERATION_STORAGE_KEY_V2;
    const membershipReservationRawV2 = hasMembershipRows
      ? readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey: membershipReservationKeyV2 })
      : { status: "ready" as const, rawValue: null };
    if (membershipReservationRawV2.status !== "ready") return refused();
    const membershipReservationsV2 = parseRows<MembershipSessionBookingReservationOperationRecordV2>(
      membershipReservationRawV2.rawValue, "membership-session-booking-reservation-operations-v2", 8_000_000,
      validateMembershipSessionBookingReservationOperationRecordV2,
    );

    const giftSettlementFrontiers: Array<() => boolean> = [];
    const hasExactSettledGiftPayment = (draft: SpecialistLocationGatewayAppointmentTerminalAttemptAggregateV1) => {
      if (draft.attemptKind !== "payment" || draft.receiptCommit !== null) return false;
      const payment = draft.attempt as AppointmentPaymentAttemptResumeDraftV1;
      // Releasing the Gift hold does not prove a second tender's refund/void.
      // Only the exact single-Gift plan can retire from this provider proof.
      if (payment.plan.allocations.length !== 1) return false;
      const reviewFingerprint = frontendGiftCardAppointmentSnapshotFingerprintV1(payment.reviewDraft);
      const checkoutFingerprint = frontendGiftCardAppointmentSnapshotFingerprintV1(payment.checkoutContext);
      const planFingerprint = frontendGiftCardAppointmentSnapshotFingerprintV1(payment.plan);
      if (!reviewFingerprint || !checkoutFingerprint || !planFingerprint) return false;
      const historical = readFrontendGiftCardAppointmentHoldForTeardownV1({ customerAccountId: draft.customerAccountId,
        draftId: draft.draftId, attemptId: payment.attemptId, idempotencyKey: payment.idempotencyKey,
        reviewFingerprint, checkoutFingerprint, planFingerprint });
      if (historical.status !== "ready") return false;
      const hold = historical.row; const terminal = hold.terminal; const authorization = hold.authorization;
      const state = payment.state.kind === "terminal" ? payment.state.priorState : payment.state;
      if (!terminal || terminal.kind === "accepted" || terminal.resolvedAtEpochMs > now ||
        !frontendGiftCardAppointmentHoldMatchesPaymentV1(hold, payment) || hold.paymentAttemptId !== payment.paymentAttemptId ||
        hold.packageReservationId !== (draft.packageSessionProof?.proof.schemaVersion === 3 ? draft.packageSessionProof.proof.reservationHold.reservationId : null)) return false;
      if (terminal.kind === "cancelled") {
        if (state.kind === "authorized-pending-booking" || payment.resumeToken !== `gc-resume:${payment.attemptId}` ||
          payment.state.kind === "terminal" && (payment.state.failure.source !== "payment" || payment.state.failure.reason !== "request-aborted")) return false;
      } else {
        // Canonical validation independently rederives the source-bound
        // submission key. Do not reconstruct the caller's optional request
        // properties or treat a generic local terminal flag as provider proof.
        if (state.kind !== "authorized-pending-booking" || !sameSnapshot(state.authorization, authorization) ||
          state.submissionIdempotencyKey !== terminal.submissionIdempotencyKey ||
          payment.state.kind === "terminal" && (payment.state.failure.source !== "submission" || payment.state.failure.reason !== terminal.reason)) return false;
      }
      if (!historical.isCurrent()) return false;
      giftSettlementFrontiers.push(historical.isCurrent);
      return true;
    };
    const retained = rows.filter(({ row, draft }) => {
      const packageProof = draft.packageSessionProof?.proof;
      const membershipProof = draft.membershipSessionProof?.proof;
      // The canonical selected Gift tender can already own a financial hold
      // while its authorization/booking response is still missing locally.
      // Normal holder readers are unavailable under auth quarantine; absence
      // or a local terminal state is not provider settlement. Preserve the
      // exact attempt until receipt acknowledgement or an independently
      // matched historical pure-Gift cancellation/rejection proves settlement.
      // A merely displayed/unallocated Gift Card does not protect ordinary
      // payment rows, and retained data grants no replacement-owner authority.
      const usesGiftPayment = draft.attemptKind === "payment" &&
        appointmentPaymentUsesOwnedGiftCardTenderV1(draft.attempt as AppointmentPaymentAttemptResumeDraftV1);
      const giftPaymentSettled = usesGiftPayment && hasExactSettledGiftPayment(draft);
      const giftCardNeedsRecovery = usesGiftPayment &&
        (draft.receiptCommit === null || draft.confirmationAcknowledgedAtEpochMs === null) && !giftPaymentSettled;
      // Rewards has its own retained provider journal, including explicit
      // None. Auth teardown must not replace an uncertain canonical attempt.
      const rewardNeedsRecovery = appointmentReviewRequiresRewardCompanionV1(draft.attempt.reviewDraft) &&
        (draft.receiptCommit === null || draft.confirmationAcknowledgedAtEpochMs === null);
      if (!packageProof && !membershipProof) return giftCardNeedsRecovery || rewardNeedsRecovery;
      if ((row.savedAtEpochMs as number) < (packageProof ?? membershipProof)!.capturedAtEpochMs) {
        throw new Error("Entitlement terminal capture is newer than its row.");
      }
      const reservation = packageProof ? reservations.find(({ draft: candidate }) =>
        candidate.draftId === draft.draftId)?.draft
        : undefined;
      const memberRecords = membershipProof ? [...membershipReservations, ...membershipReservationsV2].filter(({ draft: candidate }) => candidate.draftId === draft.draftId) : [];
      if (memberRecords.length > 1) throw new Error("Conflicting Membership reservation versions.");
      const membershipReservation = memberRecords[0]?.draft;
      if (reservation && !packageReservationMatchesTerminalAggregate(reservation, draft)) {
        throw new Error("Package reservation no longer matches its terminal lineage.");
      }
      if (membershipReservation && !membershipReservationMatchesTerminalAggregate(membershipReservation, draft)) {
        throw new Error("Membership reservation no longer matches its terminal lineage.");
      }
      const needsTerminalRecovery = draft.receiptCommit !== null
        ? draft.confirmationAcknowledgedAtEpochMs === null
        : draft.attempt.state.kind !== "terminal" || (membershipProof !== undefined && !draft.membershipSessionDefinitiveRejection) ||
          // None has no reservation ledger to protect an uncertain booking.
          // Only the independently settled pure-Gift no-booking proof above
          // can retire a None rejection; local flags and split tenders cannot.
          (packageProof?.schemaVersion === 2 && !giftPaymentSettled) || (packageProof?.schemaVersion === 3 && !draft.packageSessionAtomicReleaseCommit);
      // A definitive no-booking result authorizes releasing a reserved hold,
      // but a missing mutable ledger does not prove that release happened.
      // Keep the immutable terminal lineage until its exact provider-released
      // row is consumed. Explicit V2 None is the only no-ledger terminal case.
      const missingMembershipReleaseProof = membershipProof !== undefined &&
        draft.membershipSessionDefinitiveRejection !== undefined &&
        (membershipProof.schemaVersion === 1 || membershipProof.redemption === "reserved") &&
        draft.membershipSessionReservationReleaseCommit === undefined &&
        membershipReservation === undefined;
      // Releasing a package entitlement is not a payment void/refund. Preserve
      // the original financial authorization and submission idempotency forever
      // until a separately-authorized financial settlement contract resolves it.
      const hasUnsettledAuthorization = draft.attemptKind === "payment" &&
        draft.attempt.state.kind === "terminal" &&
        "priorState" in draft.attempt.state &&
        draft.attempt.state.priorState.kind === "authorized-pending-booking" && !giftPaymentSettled;
      const atomicReservation = atomicInventory?.status === "ready" ? atomicInventory.records.find(record => record.draftId === draft.draftId) : null;
      const atomicProtected = atomicReservation && !["released", "not-applied"].includes(atomicReservation.status);
      return giftCardNeedsRecovery || needsTerminalRecovery || atomicProtected || missingMembershipReleaseProof || hasUnsettledAuthorization || (reservation !== undefined &&
        packageSessionBookingReservationRecordRequiresProtection(reservation)) || (membershipReservation !== undefined &&
        (membershipReservation.schemaVersion === 2 ? membershipSessionBookingReservationRecordRequiresProtectionV2(membershipReservation)
          : membershipSessionBookingReservationRecordRequiresProtection(membershipReservation)));
    });

    const sourcesRemainExact = () => {
      const currentAtomic = hasPackageRows
        ? readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey: atomicKey })
        : { status: "ready" as const, rawValue: null };
      const current = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey });
      const currentReservation = hasPackageRows
        ? readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey: reservationKey })
        : { status: "ready" as const, rawValue: null };
      const currentMembershipReservation = hasMembershipRows
        ? readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey: membershipReservationKey })
        : { status: "ready" as const, rawValue: null };
      const currentMembershipReservationV2 = hasMembershipRows
        ? readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey: membershipReservationKeyV2 })
        : { status: "ready" as const, rawValue: null };
      return current.status === "ready" && current.rawValue === initial.rawValue &&
        currentAtomic.status === "ready" && currentAtomic.rawValue === atomicRaw.rawValue &&
        currentReservation.status === "ready" && currentReservation.rawValue === reservationRaw.rawValue &&
        currentMembershipReservation.status === "ready" && currentMembershipReservation.rawValue === membershipReservationRaw.rawValue &&
        currentMembershipReservationV2.status === "ready" && currentMembershipReservationV2.rawValue === membershipReservationRawV2.rawValue &&
        giftSettlementFrontiers.every(isCurrent => isCurrent()) &&
        customerOwnedSessionStorageMutationProofIsCurrent(mutationProof);
    };
    if (!sourcesRemainExact()) return refused();
    if (retained.length > 0 && retained.length === rows.length) return true;
    const nextRawValue = retained.length === 0 ? null : JSON.stringify({
      storeVersion: 1,
      storeKind: "specialist-location-gateway-appointment-terminal-attempt-aggregates",
      entries: retained.map(({ row }) => row),
    });
    const result = discardCustomerOwnedSessionStorageMutationExactStrict({
      storage, storageKey, expectedRawValue: initial.rawValue, nextRawValue,
      mutationProof,
      mutationIsCurrent: () => customerOwnedSessionStorageMutationProofIsCurrent(mutationProof) && giftSettlementFrontiers.every(isCurrent => isCurrent()),
    });
    const postReservation = hasPackageRows
      ? readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey: reservationKey })
      : { status: "ready" as const, rawValue: null };
    const postAtomic = hasPackageRows
      ? readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey: atomicKey })
      : { status: "ready" as const, rawValue: null };
    const postMembershipReservation = hasMembershipRows
      ? readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey: membershipReservationKey })
      : { status: "ready" as const, rawValue: null };
    const postMembershipReservationV2 = hasMembershipRows
      ? readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey: membershipReservationKeyV2 })
      : { status: "ready" as const, rawValue: null };
    return result.status === "committed" && postReservation.status === "ready" &&
      postAtomic.status === "ready" && postAtomic.rawValue === atomicRaw.rawValue &&
      postReservation.rawValue === reservationRaw.rawValue &&
      postMembershipReservation.status === "ready" && postMembershipReservation.rawValue === membershipReservationRaw.rawValue &&
      postMembershipReservationV2.status === "ready" && postMembershipReservationV2.rawValue === membershipReservationRawV2.rawValue &&
      giftSettlementFrontiers.every(isCurrent => isCurrent()) &&
      customerOwnedSessionStorageMutationProofIsCurrent(mutationProof)
      ? true
      : refused();
  } catch {
    return refused();
  }
}
